import Phaser from "phaser";
import { GameConfig } from "@/config/GameConfig";
import { LevelGame, type LevelGameSnapshot, type LevelTickResult } from "@/core/LevelGame";
import { clampActiveDeltaMs, runMovementCatchUp } from "@/core/MovementAccumulator";
import {
  appendRoutePoint,
  buildInitialRoute,
  computeBodyEdgeQuad,
  computeSnakeRenderProgress,
  computeSnakeTransitionDurationMs,
  computeSnakeVisualRuns,
  computeTailTaperScales,
  ROUTE_HISTORY_MARGIN,
  sampleSnakeRoute,
  softenRunCorners,
  type RoutePoint,
} from "@/rendering/snakeRenderInterpolation";
import { InputQueue, isOppositeDirection } from "@/core/InputQueue";
import type { Bite, BiteKind, Direction, Point } from "@/core/types";
import type { LevelDefinition } from "@/levels/types";
import { portalDefinitionEndpoints } from "@/levels/geometry";
import { LevelRegistry } from "@/levels/LevelRegistry";
import { WorldRegistry } from "@/levels/WorldRegistry";
import { obstacleStyleForWorld } from "@/levels/worldObstacleStyle";
import { ProgressStorage, type LevelStats } from "@/progress/ProgressStorage";
import { TutorialStorage, TUTORIAL_VERSION } from "@/progress/TutorialStorage";
import { qaOverrideBiteTutorialSeen, resolveQaInputModeOverride, resolveQaEndlessGoalHintOverride } from "@/qa/onboardingQa";
import { OnboardingStorage } from "@/progress/OnboardingStorage";
import { getStarThresholds } from "@/progress/stars";
import { computeCampaignCompletionResult } from "@/progress/campaignCompletion";
import { KeyboardInput } from "@/input/KeyboardInput";
import { TouchInput } from "@/input/TouchInput";
import type { DirectionHandlerResult } from "@/input/InputSource";
import { computeInsetBoardLayout, type BoardLayout } from "@/utils/BoardLayout";
import { readSafeAreaInsets } from "@/utils/readSafeAreaInsets";
import { createButton } from "@/ui/Button";
import { contentFlowStartY, layoutButtonRows } from "@/ui/buttonLayout";
import { drawStar } from "@/ui/Star";
import { createHowToPlayOverlay, type HowToPlayOverlay } from "@/ui/HowToPlay";
import { createNameEntryModal, type NameEntryModalHandle } from "@/ui/NameEntryModal";
import { hexToCss, mixColors } from "@/ui/colors";
import { drawWorldIcon, drawWorldBackdrop, worldSeed } from "@/ui/worldGraphics";
import { RunTracker } from "@/online/RunTracker";
import { detectPlatform } from "@/platform/detectPlatform";
import type { Platform } from "@/platform/Platform";
import { getEffectiveTargetLength } from "@/levels/effectiveTarget";
import { endlessLeaderboardService } from "@/endless/EndlessLeaderboardService";
import { getLeaderboardService } from "@/online/LeaderboardService";
import { decideNamePrompt } from "@/online/completionCoordinator";
import type { RunSource } from "@/online/types";
import {
  resolveQaMode,
  qaModeLabel,
  forceStarfruitEligibleRandom,
  forceNextChaosWormholeRandom,
  shouldPersistRun,
  computeQaOnlyOverlayOutcome,
  type QaModeKind,
} from "@/qa/QaMode";
import type { ManeuverType } from "@/core/scoring/types";
import { computePaceBonus, paceConfigForLevel } from "@/core/scoring/paceBonus";
import { ScoreV2Storage } from "@/progress/ScoreV2Storage";
import {
  trackTutorialCompleted,
  trackRunStarted,
  trackGateOpened,
  trackRunFinished,
  trackFirstInputReceived,
  trackPlayAgainClicked,
  RunFinishedGuard,
} from "@/analytics/gameAnalytics";
import { CURRENT_GAME_MODE, type GameMode, type InputMode, type AnalyticsRunSource } from "@/analytics/events";
import type { DailyRunAnalyticsContext } from "@/analytics/gameAnalytics";
import { dailyChallengeToLevelDefinition, dailyLevelGameOptions } from "@/daily/convert";
import { DailyChallengeClient } from "@/daily/DailyChallengeClient";
import { DAILY_SCORING_RULES_VERSION } from "@/daily/types";
import { shouldSubmitDailyResult } from "@/daily/qaPreview";
import { computeDailyPoints } from "@/daily/dailyPoints";
import type { DailyGameSceneContext, DailyAttemptOutcome, DailyResultSceneData } from "@/daily/DailyRunContext";
import { buildEndlessLevelDefinition, endlessLevelGameOptions } from "@/levels/endlessLevel";
import { EndlessStorage } from "@/endless/EndlessStorage";
import type { EndlessRunContext, EndlessEndReason, EndlessRunOutcome, EndlessResultSceneData, EndlessBestRecord } from "@/endless/types";
import { formatSurvivalTime } from "@/endless/formatSurvivalTime";
import { computeDisplayedBestLength } from "@/endless/hud";
import { buildEndlessSubmissionMetrics } from "@/endless/submissionMetrics";
import { VisualFeedbackController, type BiteImpactContext } from "@/scenes/feedback/VisualFeedbackController";
import { QA_GALLERY_TRIGGERS } from "@/scenes/feedback/qaGallery";
import { attachOrientationGate, type OrientationGateHandle } from "@/scenes/OrientationGate";
import { MANEUVER_LABEL, eventIntensityTier } from "@/scenes/feedback/mapEvents";
import { getAudioManager, type AudioManager } from "@/audio/AudioManager";

interface GameSceneData {
  levelId?: string;
  runSource?: RunSource;
  /** Present only for a Daily Challenge attempt - see `DailyChallengeScene`. The attempt is already reserved/consumed server-side by the time this scene starts; this scene's job is only to play it out and hand the outcome to `DailyResultScene`. */
  daily?: DailyGameSceneContext;
  /** Present only for an Endless Growth V1 run - see `MenuScene`'s `onEndless()`/`EndlessResultScene`'s PLAY AGAIN. Mutually exclusive with `daily`; never set for a Campaign run. */
  endless?: EndlessRunContext;
  /**
   * Instant Play & Onboarding V1: set only by `EntryScene`'s PLAY NOW
   * handler, never by `MenuScene`'s ENDLESS button or `EndlessResultScene`'s
   * PLAY AGAIN - purely an analytics-only distinction (see
   * `AnalyticsRunSource`'s own doc). Never forwarded to `RunSubmission`/
   * any backend call - `RunTracker`'s `runSource` field stays exactly
   * `data.runSource` ("play"), unaffected by this field.
   */
  entrySource?: "instant_play";
}

/** Scoring V2's level-complete breakdown - everything `populateLevelCompleteOverlay()` needs to render `BITE SCORE / SKILL SCORE / PACE BONUS / FINAL SCORE / TIME / MOVES` plus per-maneuver counts, computed once in `handleLevelComplete()`. */
interface LevelCompleteOverlayResult {
  biteScore: number;
  skillScore: number;
  paceBonus: number;
  finalScore: number;
  activeDurationMs: number;
  moveCount: number;
  maneuverCounts: Record<ManeuverType, number>;
  doubleDangerCount: number;
  bestFlowStreak: number;
  trickBiteCount: number;
  clutchBiteCount: number;
  stars: 1 | 2 | 3;
  /** Compares Final Score V2 against `ScoreV2Storage`'s own record only - never against a legacy V1 score. */
  isNewHighScore: boolean;
  /** This run raised the level's stored star record (Score & Stars V2, from completed Final Score) - independent of `isNewHighScore`, since either can improve without the other. */
  isNewStarRecord: boolean;
  /** `ScoreV2Storage`'s best Final Score for this level, already including this run if it just set a new one - `null` when no run (this one included, in QA mode) has ever recorded one. */
  bestFinalScore: number | null;
  isStarfruitGate: boolean;
  newlyUnlockedWorldName?: string;
  /**
   * Campaign Complete Option 2: true only on the run that completes the
   * very last level of the very last existing world for the FIRST time
   * ever (derived from `ProgressStorage.recordLevelResult()`'s existing
   * `newlyCompletedWorld` signal - the same one every prior world already
   * uses to show its own "<world> UNLOCKED" banner - so this needs no new
   * persisted field). A later replay of that same final level has
   * `newlyCompletedWorld` undefined (the world was already completed
   * before this run), so this is false - the permanent CAMPAIGN COMPLETE
   * title/subtitle still show (see `isCampaignComplete` below, unchanged),
   * just without the one-time celebratory emphasis this flag gates.
   */
  isFirstEverCampaignCompletion: boolean;
}

const TOP_RESERVED = 84;
/**
 * New player goal clarity: Endless-only extra top margin, reserved ABOVE
 * the board (never inside it) purely to make room for the persistent
 * "GROW AS LONG AS YOU CAN" objective label - see `endlessObjectiveText`.
 * Added only to the value passed into `computeBoardLayout()`/
 * `feedback.setLayout()` in `recomputeLayout()`, never to the bare
 * `TOP_RESERVED` constant itself - every other HUD element (score line,
 * pause/help buttons, qaModeText, trickBiteWindowText, QA overlays) keeps
 * positioning off the unchanged base value, so Campaign/Daily (and every
 * one of those existing Endless positions) are pixel-for-pixel unaffected.
 */
const ENDLESS_OBJECTIVE_TOP_RESERVED_EXTRA = 26;
const BOTTOM_RESERVED = 16;
const SIDE_PADDING = 16;
const PAUSE_BUTTON_SIZE = 44;
const HELP_BUTTON_SIZE = 44;
const HELP_BUTTON_GAP = 10;

const GAME_OVER_REASON_TEXT: Record<"wall" | "obstacle" | "self" | "black_hole", string> = {
  wall: "You hit the wall.",
  obstacle: "You hit an obstacle.",
  self: "You ran into yourself.",
  black_hole: "The black hole swallowed your snake.",
};

const BITE_LABEL: Record<BiteKind, string> = { safe: "+1", rush: "+2", chaos: "?", starfruit: "★" };
const BITE_COLOR: Record<BiteKind, number> = {
  safe: GameConfig.colors.biteSafe,
  rush: GameConfig.colors.biteRush,
  chaos: GameConfig.colors.biteChaos,
  starfruit: GameConfig.colors.starfruitBody,
};

/** In-run HUD label for a Daily Challenge's `biteMode` - `"standard"` is never shown (it's the normal CHOOSE YOUR BITE experience, nothing to call out). */
const DAILY_BITE_MODE_HUD_LABEL: Record<"safe_only" | "rush_only" | "chaos_only", string> = {
  safe_only: "SAFE ONLY",
  rush_only: "RUSH ONLY",
  chaos_only: "CHAOS ONLY",
};

/** How many of the most-recently-executed directions the QA scoring overlay shows. */
const RECENT_DIRECTIONS_MAX = 5;

/**
 * PR31 follow-up fix (POLISH 1): how far each snake segment's own fill
 * color blends toward `GameConfig.colors.biteRush` gold while the RUSH
 * scoring combo is live - see `drawSnakeSegments()`'s own doc for the
 * full "why a static blend, not a pulse" reasoning. Deliberately modest
 * (well under half) so the snake's own green identity/silhouette is
 * still unmistakably a snake, not a solid gold shape - the gold OUTLINE
 * `drawSnakeSegments()` also draws at full `GameConfig.colors.biteRush`
 * strength is what carries most of "immediately visible" on its own.
 */
const RUSH_FILL_MIX = 0.32;

/**
 * PR31 follow-up fix (real-device feedback: "RUSH yellow now reads
 * better but lingers too long"). How many GRID TICKS (not wall-clock
 * time - see `rushGlowTicksRemaining`'s own doc) the gold identity stays
 * visible after the most recently eaten RUSH bite, before fading out -
 * independent of `rushStack`, which the engine itself only resets on a
 * SAFE/CHAOS/STARFRUIT bite and could otherwise stay elevated (keeping
 * the snake gold) for the rest of a run if the player simply stops
 * eating anything else. At this codebase's typical ~150-170ms base step,
 * ~8 ticks is a little over a second - long enough to read clearly as
 * "you just ate RUSH", short enough to have visibly resolved well before
 * it could plausibly be called lingering. Eating another RUSH bite before
 * this elapses refreshes it back to the full value (see `tick()`), so a
 * real back-to-back RUSH combo still reads as continuously gold.
 */
const RUSH_GLOW_TICKS = 8;

/**
 * The last this-many ticks of `RUSH_GLOW_TICKS` fade the gold identity
 * out smoothly (see `rushGlowIntensity()`) instead of cutting it off
 * abruptly - a graceful resolve, not a pop.
 */
const RUSH_GLOW_FADE_TICKS = 3;

/**
 * PR32 follow-up (continuous-body art pass). The outline/shadow pass's
 * tone at RUSH intensity 0 - a darkened shade of the body's/head's own
 * fill (not a new, unrelated hue), so it reads as restrained depth
 * rather than a competing color identity. Replaces the old
 * per-segment-only `BODY_CONTOUR_COLOR` (PR31): the new outline is ONE
 * continuous pass over the whole body, and - unlike the old fixed
 * contour - blends toward `GameConfig.colors.biteRush` as RUSH intensity
 * rises (see `drawSnakeSegments()`'s own doc), so it also carries RUSH's
 * gold identity, which the old plain contour color never did. Kept at
 * module scope (computed once, not per-frame) since `drawSnakeSegments()`
 * runs on every render frame.
 */
const BODY_SHADOW_COLOR = mixColors(GameConfig.colors.snakeBody, 0x000000, 0.32);
/** Same role as `BODY_SHADOW_COLOR`, for the head's own outline pass. */
const HEAD_SHADOW_COLOR = mixColors(GameConfig.colors.snakeHead, 0x000000, 0.3);

/** How much larger than the body's own outer radius the head's outer radius is - "slightly larger... but never implies collisions outside its real grid footprint" (see `drawSnakeSegments()`'s own clamp against `cell * 0.48` that enforces the footprint half). */
const HEAD_RADIUS_SCALE = 1.15;
/** The highlight pass's radius, as a fraction of the layer it's highlighting (body or head) - small and restrained, a sheen, never a second competing shape. */
const HIGHLIGHT_RADIUS_SCALE = 0.5;
/** How far (as a fraction of cell size) the highlight pass is offset toward the upper-left of each circle/edge, suggesting one consistent, subtle light source. */
const HIGHLIGHT_OFFSET_SCALE = 0.1;
/** The highlight pass's alpha - restrained on purpose, so it reads as a soft sheen layered on the main fill, never a hard shape of its own. */
const HIGHLIGHT_ALPHA = 0.38;
/** How far the highlight pass's color blends toward white, off the LIVE (already RUSH-aware) body/head fill - so the highlight automatically warms up with RUSH too, with no separate branch. */
const HIGHLIGHT_MIX = 0.4;
/** The width of the visible outline rim (outline pass radius minus main body/head radius), as a fraction of cell size. */
const OUTLINE_WIDTH_SCALE = 0.05;

/** PR32 follow-up (continuous-body art pass, eyes): a light "sclera" ring behind each dark pupil, so the eyes stay readable against the new head fill/highlight instead of being two flat dots. Plain white - simplest, cleanest choice for an arcade look, no new GameConfig entry needed for a single rendering-only accent. */
const EYE_SCLERA_COLOR = 0xffffff;

export class GameScene extends Phaser.Scene {
  private level!: LevelDefinition;
  // Note: deliberately NOT named `game` - Phaser.Scene already exposes
  // `this.game` (the Phaser.Game instance), and shadowing it would be
  // confusing and fragile.
  private levelGame!: LevelGame;
  private runTracker!: RunTracker;
  /**
   * PR34: this run's Mobile/Desktop category, decided ONCE in `create()`
   * (`detectPlatform()` - the existing phone-like viewport rule) and frozen
   * for the whole run: never re-derived from which input was used, never
   * switched mid-run. Drives the leaderboard category every submission of
   * this run carries, the Mobile Mars/Saturn/Sun gate target, and the
   * `platform` analytics property.
   */
  private platform: Platform = "desktop";
  /** PR34: the gate target THIS run must reach - `getEffectiveTargetLength(level, platform)` for Campaign, the level's own raw `targetLength` for Daily/Endless. The single value every target-describing UI/submission path below uses; `this.level` is never mutated. */
  private effectiveTargetLength = 0;
  private nameModal!: NameEntryModalHandle;
  private inputQueue!: InputQueue;
  private keyboardInput?: KeyboardInput;
  private touchInput?: TouchInput;

  private layout!: BoardLayout;
  private boardGraphics!: Phaser.GameObjects.Graphics;
  private obstacleGraphics!: Phaser.GameObjects.Graphics;
  /** Portals V1 (Saturn World) - persistent pair markers. Empty/no-op draw for every level without `portals`. */
  private portalGraphics!: Phaser.GameObjects.Graphics;
  /** BLACK HOLE V1 (Saturn World) - persistent hazard markers. Empty/no-op draw for every level without `blackHoles`. */
  private blackHoleGraphics!: Phaser.GameObjects.Graphics;
  /** PR27: BLACK HOLE's full-cell hazard frame - kept on its own layer (separate from the always-opaque core/ring in `blackHoleGraphics`) purely so its alpha can pulse without the core/ring ever dimming. See `renderBlackHoles()`. */
  private blackHolePulseGraphics!: Phaser.GameObjects.Graphics;
  /** PR27: one small rotating Container per BLACK HOLE (unlike the shared layers above, a swirl needs its own rotation pivot per hole) - destroyed and rebuilt from scratch on every `renderBlackHoles()` call, so a level/resize can never leave a stray swirl behind. See `renderBlackHoles()`. */
  private blackHoleSwirls: Phaser.GameObjects.Container[] = [];
  private gateGraphics!: Phaser.GameObjects.Graphics;
  /**
   * PR31 follow-up fix (round 2: "still looks like a green tile with a
   * play icon" - full portal redesign). The open gate's own small rotating
   * swirl motif (`createGatePortalSwirl()`) - EXACT same lifecycle as
   * `blackHoleSwirls` above: destroyed and rebuilt from scratch on every
   * `renderGate()` call (locked<->open<->starfruit transitions, level
   * load, resize), so a state change can never leave a stray swirl/tween
   * behind. `null` whenever the gate isn't currently open, or this level
   * has no gate at all (Daily survival). A single Container (not an
   * array) - unlike black holes, a level has at most one gate.
   */
  private gatePortalSwirl: Phaser.GameObjects.Container | null = null;
  private snakeGraphics!: Phaser.GameObjects.Graphics;
  private biteGraphics!: Phaser.GameObjects.Graphics;
  private biteLabels: Phaser.GameObjects.Text[] = [];

  private scoreText!: Phaser.GameObjects.Text;
  private subLineText!: Phaser.GameObjects.Text;
  private worldIconGraphics!: Phaser.GameObjects.Graphics;
  /** The reward line under an active SPEED TRIAL - never changes mid-level, so it's the only piece of the old SPEED TRIAL HUD kept here directly; the live countdown/activation/warning/exit treatment is `feedback`'s status lane. */
  private speedTrialRewardText!: Phaser.GameObjects.Text;
  private qaModeText!: Phaser.GameObjects.Text;
  /** TRICK BITE / CLUTCH BITE: the persistent "TRICK BITE READY: N" indicator, visible only while a window is active - not a fading toast, since it must track the live countdown. Deliberately kept as its own always-on indicator (not routed through `feedback`'s Skill lane) so it can never be pre-empted by an unrelated toast while a window is genuinely still open. */
  private trickBiteWindowText!: Phaser.GameObjects.Text;
  /** PR13-C (Visual Feedback V2): the single typed controller for every gameplay visual-feedback element (headlines, near-snake bites, status badges, particles/tweens, priority/queueing, pause/cleanup) - see that class's own doc. */
  private feedback!: VisualFeedbackController;
  /** PR13-D production integration: the module-level `AudioManager` singleton (never recreated per Scene) - called from the exact same authoritative transition points as `feedback` above, so visual and audio consume the same signal without one depending on the other. */
  private readonly audio: AudioManager = getAudioManager();
  /** `?qa=feedback` only - the trigger buttons for the visual-feedback QA gallery. Destroyed and rebuilt only once, in `create()`; never touches gameplay state. */
  private qaGalleryButtons: Phaser.GameObjects.Container[] = [];
  /** `?qa=chaos` only (deterministic gameplay QA controls, PR13-D audio follow-up section D) - FORCE NEXT CHAOS/FORCE NEXT SPAWN buttons that call straight into `LevelGame`'s one-shot QA overrides, so the very next real CHAOS bite/fruit group actually resolves as requested through the normal gameplay/audio path. Unreachable on the real production hostname - see `buildQaLevelGameOptions()`. */
  private qaChaosButtons: Phaser.GameObjects.Container[] = [];
  private qaChaosStatusText?: Phaser.GameObjects.Text;
  /** `?qa=scoring` only - the live scoring debug overlay described in the PR/spec. `null` whenever `qaMode !== "scoring"`. */
  private qaScoringOverlayText?: Phaser.GameObjects.Text;
  /** `?qa=speed` only (PR11 - Speed Engine V2) - the live movement-speed debug overlay. `null` whenever `qaMode !== "speed"`. */
  private qaSpeedOverlayText?: Phaser.GameObjects.Text;
  /** `?qa=endless` only - minimal live debug counters (bites/phase/pending respawn), for lifecycle testing. `undefined` whenever `qaMode !== "endless"`. */
  private qaEndlessDebugText?: Phaser.GameObjects.Text;
  private pauseButton!: Phaser.GameObjects.Container;
  private helpButton!: Phaser.GameObjects.Container;

  private pauseOverlay!: Phaser.GameObjects.Container;
  private gameOverOverlay!: Phaser.GameObjects.Container;
  private levelCompleteOverlay!: Phaser.GameObjects.Container;
  private howToPlayOverlay!: HowToPlayOverlay;
  private introOverlay!: Phaser.GameObjects.Container;
  /** Instant Play & Onboarding V1: the lightweight (non-full-screen) "waiting for your first move" ready-prompt text - see `createReadyIndicator()`. Sits over the board without a dark scrim, so it never hides the HUD/board while gameplay is frozen. */
  private readyPromptText!: Phaser.GameObjects.Text;
  /** PR31 (portrait-only mobile gameplay), follow-up fix: the ROTATE YOUR DEVICE / TAP TO CONTINUE gate, now the shared `OrientationGate.ts` module (every other Scene attaches the same gate) instead of a GameScene-only implementation. `requiresExplicitResume: () => true` unconditionally, exactly reproducing this Scene's original behavior - see that option's own doc. */
  private orientationGate!: OrientationGateHandle;
  /**
   * New player goal clarity: the small, persistent "GROW AS LONG AS YOU
   * CAN" objective label - Endless only, positioned in its own reserved
   * strip above the board (see `ENDLESS_OBJECTIVE_TOP_RESERVED_EXTRA`),
   * never over the arena or any other HUD element. Its initial visibility
   * (set once at creation) is `false` for Campaign/Daily (never shown) and
   * for a first-ever Endless run (where the one-time emphasized hint plays
   * first - see `completeReadyGate()`'s callback to `showEndlessGoalHint()`,
   * which reveals this in its place once that emphasis finishes, so the
   * two are never visible at the same time); `true` immediately for every
   * other Endless run.
   */
  private endlessObjectiveText!: Phaser.GameObjects.Text;

  private isPaused = false;
  private isHelpOpen = false;
  private wasPausedBeforeHelp = false;
  private isIntroActive = false;
  /**
   * Instant Play & Onboarding V1: true while the snake is frozen waiting
   * for the player's first legal directional input this run - gated
   * identically to `isIntroActive` in every guard below (movement, Pause,
   * Help, active-time tracking), but with its own dismiss condition (a
   * legal direction, not a button click) and its own compact "PRESS A
   * DIRECTION TO START"/"SWIPE TO START" overlay - never a tutorial, never
   * persisted, never a separate analytics-completion event. Set only once
   * `isIntroActive` has already cleared (see `beginReadyGate()`), so the
   * two never overlap. Active for every Endless run (Instant Play, Main
   * Menu, PLAY AGAIN, a returning player's run) and, for Campaign/Daily/
   * Beat My Score, only on a brand-new player's very first run (right
   * after CHOOSE YOUR BITE) - a returning player in those modes ticks
   * immediately, exactly as before this feature.
   */
  private isReadyGateActive = false;
  /**
   * New player goal clarity: decided ONCE in `create()` (never re-derived
   * later) - true only for Endless, and only when `OnboardingStorage`
   * (or a QA override) says the "GROW AS LONG AS YOU CAN" goal hint has
   * never been shown before. Deliberately NOT marked in `OnboardingStorage`
   * at decision time (unlike `EndlessResultScene`'s worlds-discovery flag) -
   * a player who never actually reaches `completeReadyGate()` (closes the
   * tab during the ready gate, say) must still see the hint on their next
   * run, so the flag is only written at the moment the hint actually
   * displays - see `completeReadyGate()`.
   */
  private shouldShowEndlessGoalHint = false;
  private isExiting = false;
  private isGameOver = false;
  private isLevelComplete = false;
  private accumulatorMs = 0;
  /**
   * PR32 (Snake Movement Smoothing) - see `src/rendering/snakeRenderInterpolation.ts`.
   * A bounded, oldest-first history of the snake HEAD's own committed
   * positions (one `RoutePoint` per ordinary-movement tick), used to
   * render-only sample a continuous body centerline behind the head -
   * see `advanceSnakeRoute()` and `sampleSnakeRoute()`'s own docs. Never
   * a live reference into `LevelGame`'s own internal `snakeBody` array;
   * each appended point is a fresh `{x, y}` read off `LevelGameSnapshot.snake[0]`.
   */
  private snakeRoute: readonly RoutePoint[] = [];
  /** True from the moment the most recently committed tick was anything other than ordinary movement ("moved"/"ate"/"gate-opened") - the exit walk's own steps, level-complete, game-over, arena-cleared. While true, rendering bypasses `snakeRoute` sampling entirely and draws the exact current logical `snapshot.snake` (see `computeCurrentRenderSnake()`) - the same "snap to the committed state, no interpolation" behavior this feature has always had for non-ordinary movement, just expressed as a flag instead of a collapsed previous/current pair. */
  private snakeRenderSnapped = true;
  /** Render-only ACTIVE time elapsed since the current route head edge (or snap) was set (`advanceSnakeRoute()`) - advanced only from `update()`'s own already-active-time `delta`, so it freezes automatically the instant the caller stops advancing it (Pause/game-over/etc.), exactly like every other active-time value in this codebase (`SpeedEngine`, WORMHOLE/SPEED TRIAL timers, ...). Never a wall-clock read of its own. */
  private snakeRenderElapsedMs = 0;
  /** The transition duration captured at the exact moment the current route head edge was last set - FIXED for the lifetime of that one transition, never recomputed against a live `stepMs`. This is what guarantees render progress can only ever move forward: a Speed Engine retarget mid-transition can only change the NEXT transition's duration, never this one's. */
  private snakeRenderTransitionMs = 0;
  /** PR32: true from the instant `startBlackHoleConsumption()` begins its own 450ms consumption animation - normal movement-position rendering (`renderSnakePosition()`) must never touch `snakeGraphics` again once that animation owns it exclusively (see that method's own doc). Reset only by a fresh `create()`. */
  private blackHoleConsuming = false;
  /** True once STARFRUIT has ever been observed among the live bites this run - drives the one-time `runTracker.recordStarfruitSpawned()` call, since spawning (unlike every other tracked event) isn't itself an eaten-bite event. */
  private starfruitSpawnTracked = false;
  /** The last `RECENT_DIRECTIONS_MAX` executed directions, oldest first - display-only (the QA scoring overlay), never read by any gameplay/scoring logic (that's `LevelGame`'s own internal direction history). */
  private recentDirections: Direction[] = [];
  /**
   * PR31 follow-up fix: ticks left before the snake's gold RUSH identity
   * (`drawSnakeSegments()`) fully fades out - counted in grid TICKS, not
   * wall-clock time, so it only ever advances alongside real movement
   * (frozen exactly like everything else while paused/orientation-blocked/
   * between ticks), never via `update()`'s own per-frame `delta`. Reset to
   * `RUSH_GLOW_TICKS` by `tick()` on every RUSH bite eaten (extending a
   * real back-to-back streak), decremented by one on every OTHER tick, and
   * forced to `0` immediately once `rushStack` itself reads `0` (the combo
   * actually ended) - see `tick()`'s own update site for the exact order.
   */
  private rushGlowTicksRemaining = 0;
  /**
   * `null` on every real production run and on an ordinary Deploy Preview
   * visit with no `?qa=` param - only ever non-null when `resolveQaMode()`
   * has already confirmed both the hostname allowlist AND a recognized
   * `?qa=` value. While set: the run's RNG is skewed to force the named
   * mechanic (see `create()`), a small on-screen indicator is shown, and
   * the run is never submitted to the leaderboard/queue and never writes
   * to `ProgressStorage` - see `handleGameOver()`/`handleLevelComplete()`.
   */
  private qaMode: QaModeKind | null = null;

  /** Non-null only for a Daily Challenge attempt (see `GameSceneData.daily`) - drives every campaign-vs-Daily branch below (persistence, overlays, which mode's analytics context is attached). `null` for every campaign run, always. */
  private dailyContext: DailyGameSceneContext | null = null;
  /** Non-null only for an Endless Growth V1 run (see `GameSceneData.endless`) - its own explicit top-level branch, never falling through to the Campaign `else` path. Mutually exclusive with `dailyContext`. */
  private endlessContext: EndlessRunContext | null = null;
  /** `"instant_play"` only when `EntryScene`'s PLAY NOW launched this exact run (see `GameSceneData.entrySource`) - analytics-only, never affects gameplay/persistence/submission. */
  private entrySource: "instant_play" | null = null;
  /** This run's backend-shared `RunSource` (`data.runSource ?? "play"`), captured once in `create()` so every later `trackRunFinished()` call site can derive the same `analyticsRunSource()` value `run_started` already used - never re-read from `data` after `create()` returns. */
  private runSourceValue: RunSource = "play";
  /** `"daily"`/`"endless"` for those modes, `CURRENT_GAME_MODE` ("campaign") otherwise - what every analytics event's `game_mode` reports. */
  private gameMode: GameMode = CURRENT_GAME_MODE;
  /** Guards `finishDailyAttempt()`/the teardown DNF fallback against ever running twice for the same attempt - the attempt itself only ever ends once (death, completion, or an abandoned teardown), and only one of those paths should ever report it. */
  private dailyAttemptFinished = false;
  /**
   * PR28: Phaser's own `shutdown`/`destroy` events (wired in `create()`)
   * only ever fire for an IN-APP scene transition - a real browser tab
   * close/refresh/navigation-away never reaches them, so a Daily attempt
   * abandoned that way previously reported nothing at all. `pagehide`
   * fires reliably for exactly that case (unlike `visibilitychange`,
   * which also fires on a merely-backgrounded tab the player intends to
   * resume, and must never be treated as abandonment). A stable bound
   * field (not an inline closure) so `addEventListener`/`removeEventListener`
   * in `create()`/`teardown()` reference the identical function - the same
   * anti-leak pattern `TouchInput`/`KeyboardInput` already use.
   */
  private readonly handlePageHide = (): void => {
    this.submitDailyDnfOnTeardownIfNeeded();
  };
  /** Guards `finishEndlessRun()` against ever running twice for the same run - mirrors `dailyAttemptFinished`. */
  private endlessRunFinished = false;
  /**
   * True the instant an Endless run reaches a "quit" (END RUN) or
   * "arena_cleared" ending - a real death still uses the shared
   * `isGameOver` flag (Endless death goes through the same
   * `handleGameOver()` every other mode does). Gates the update loop/
   * input/pause exactly like `isGameOver`/`isLevelComplete` do for their
   * own endings - see every guard that checks it alongside those two.
   */
  private isEndlessRunOver = false;
  /** Endless HUD/result only - the fastest (smallest) INTERPOLATED movement interval actually observed this run, updated every active frame. `null` until the first frame runs. Never read by any gameplay/scoring logic. */
  private endlessFastestStepMs: number | null = null;
  /** Endless HUD only - the stored best Length at run start, read ONCE via `EndlessStorage.getBest()` in `create()` - never re-read mid-run, so an in-run improvement is never "confirmed" against storage before the run actually reaches a terminal result. `null` when no record exists yet. */
  private endlessStoredBestLength: number | null = null;

  /** This level's best score/stars *before* this run - captured once in `create()`, read by `run_started` and, at the end of the run, to compute `run_finished`'s `new_best_score`/`new_best_stars`/`best_*_improved`. See `src/analytics`. */
  private previousLevelStats: LevelStats = { highScore: 0, stars: 0 };
  /** Guarantees this run emits at most one `run_finished`, whichever path (death, completion, or the scene tearing down mid-run) gets there first. See `RunFinishedGuard`. */
  private runFinishedGuard = new RunFinishedGuard();

  constructor() {
    super("GameScene");
  }

  create(data: GameSceneData): void {
    // Every fresh run (a first "play"/Level Select entry, Campaign RETRY/
    // REPLAY/NEXT LEVEL, Daily REPLAY, or Endless PLAY AGAIN - `create()`
    // runs identically for all of them) must never inherit stale one-shot/
    // dedupe/warning-tier audio state from whatever the PREVIOUS scene
    // left behind: `AudioManager` is a persistent singleton, never
    // recreated per Scene (see `getAudioManager()`), so nothing else would
    // ever reset it between two GameScene lifetimes. `onSceneShutdown()`
    // (called from `teardown()` below) is the complementary half - it
    // stops whatever THIS scene was doing the instant it goes away,
    // whichever scene comes next; this is the "a NEW run is starting"
    // half, always called here regardless of what comes next.
    this.audio.onRestart();

    this.dailyContext = data.daily ?? null;
    this.endlessContext = data.endless ?? null;
    this.entrySource = data.entrySource ?? null;
    this.runSourceValue = data.runSource ?? "play";
    this.gameMode = this.dailyContext ? "daily" : this.endlessContext ? "endless" : CURRENT_GAME_MODE;
    this.dailyAttemptFinished = false;
    this.endlessRunFinished = false;
    this.isEndlessRunOver = false;
    this.endlessFastestStepMs = null;
    this.endlessStoredBestLength = this.endlessContext ? (EndlessStorage.getBest()?.length ?? null) : null;
    this.platform = detectPlatform();

    if (this.dailyContext) {
      this.level = dailyChallengeToLevelDefinition(this.dailyContext.spec);
      this.qaMode = null; // Daily never uses the campaign's ?qa= RNG-forcing modes.
      this.levelGame = new LevelGame(this.level, dailyLevelGameOptions(this.dailyContext.spec));
    } else if (this.endlessContext) {
      this.level = buildEndlessLevelDefinition();
      // Endless has exactly one QA mode of its own ("endless"), gated by
      // the same host-allowlisted resolveQaMode() every other mode uses -
      // never any of Campaign's six (starfruit/wormhole/scoring/speed/
      // feedback/chaos), which Endless simply never resolves to.
      const resolved = resolveQaMode(window.location.hostname, window.location.search);
      this.qaMode = resolved === "endless" ? "endless" : null;
      this.levelGame = new LevelGame(this.level, { ...endlessLevelGameOptions(), qaOverridesEnabled: this.qaMode === "endless" });
    } else {
      this.level = LevelRegistry.byId(data.levelId ?? "") ?? LevelRegistry.firstLevel();
      this.qaMode = resolveQaMode(window.location.hostname, window.location.search);
      // PR34: Mobile Mars/Saturn/Sun only - the ONE gameplay value the
      // platform may change is the snake length the normal length-based gate
      // requires. Passed as an engine option; the shared LevelDefinition keeps
      // its raw (Desktop) target.
      const effectiveTarget = getEffectiveTargetLength(this.level, this.platform);
      this.levelGame = new LevelGame(this.level, {
        ...this.buildQaLevelGameOptions(),
        ...(effectiveTarget !== this.level.targetLength ? { targetLengthOverride: effectiveTarget } : {}),
      });
    }
    this.effectiveTargetLength = this.levelGame.getSnapshot().targetLength;
    this.runTracker = new RunTracker({ levelId: this.level.id, worldId: this.level.worldId, runSource: data.runSource ?? "play", platform: this.platform });
    this.runFinishedGuard = new RunFinishedGuard();
    // Daily and Endless runs never read/write campaign progress - see the
    // PR description's "Campaign and Daily/Endless state must remain
    // completely separate" rule. Neither field is shown anywhere in
    // Daily/Endless UI.
    this.previousLevelStats = this.dailyContext || this.endlessContext ? { highScore: 0, stars: 0 } : ProgressStorage.getLevelStats(this.level);
    if (shouldPersistRun(this.qaMode)) {
      const isReplay = (data.runSource ?? "play") === "replay";
      const runSource = this.analyticsRunSource(data.runSource ?? "play");
      trackRunStarted({
        runId: this.runTracker.runId,
        worldId: this.level.worldId,
        levelId: this.level.id,
        gameMode: this.gameMode,
        platform: this.platform,
        daily: this.dailyAnalyticsContext(),
        startingLength: this.level.initialLength,
        targetLength: this.level.objective === "survival" ? undefined : this.effectiveTargetLength,
        isReplay,
        previousBestScore: this.previousLevelStats.highScore,
        previousBestStars: this.previousLevelStats.stars,
        runSource,
      });
    }
    this.inputQueue = new InputQueue(GameConfig.input.queueMax);
    this.isPaused = false;
    this.isHelpOpen = false;
    this.wasPausedBeforeHelp = false;
    this.isExiting = false;
    this.isGameOver = false;
    this.isLevelComplete = false;
    this.accumulatorMs = 0;
    // PR32: a brand-new run/restart must never inherit render-interpolation
    // state from whatever the PREVIOUS GameScene instance was doing - the
    // route is rebuilt fresh from the starting snake and snapped (so
    // `renderSnakePosition()` below renders it exactly at rest, no
    // phantom transition) and `blackHoleConsuming` always starts clear.
    this.snakeRoute = buildInitialRoute(this.levelGame.getSnapshot().snake);
    this.snakeRenderSnapped = true;
    this.snakeRenderElapsedMs = 0;
    this.snakeRenderTransitionMs = 0;
    this.blackHoleConsuming = false;
    this.starfruitSpawnTracked = false;
    this.recentDirections = [];
    this.rushGlowTicksRemaining = 0;
    this.isReadyGateActive = false;

    // Instant Play & Onboarding V1: the CHOOSE YOUR BITE intro is
    // QA-overridable via `?qaOnboarding=new|returning` (see
    // `onboardingQa.ts`). `null` on every real production host and on an
    // ordinary Deploy Preview visit with no `?qaOnboarding=` param - real
    // storage decides, exactly as before this feature.
    const hostname = window.location.hostname;
    const search = window.location.search;
    const biteTutorialSeen = qaOverrideBiteTutorialSeen(hostname, search) ?? TutorialStorage.hasSeenIntro();
    // Shared, global gate - a Daily attempt shows the same one-time
    // "CHOOSE YOUR BITE" intro a first campaign run would, per the PR
    // description's "show it before the first daily attempt" rule.
    this.isIntroActive = !biteTutorialSeen;

    // New player goal clarity: Endless only, never Campaign/Daily - see
    // `shouldShowEndlessGoalHint`'s own doc for why this is a pure read
    // here (the storage write happens later, only once the hint actually
    // displays).
    const goalHintOverride = resolveQaEndlessGoalHintOverride(hostname, search);
    this.shouldShowEndlessGoalHint = this.endlessContext !== null && (goalHintOverride ? goalHintOverride === "pending" : !OnboardingStorage.hasShownEndlessGoalHint());

    this.cameras.main.setBackgroundColor(GameConfig.colors.background);

    this.boardGraphics = this.add.graphics();
    this.obstacleGraphics = this.add.graphics();
    this.portalGraphics = this.add.graphics();
    this.blackHoleGraphics = this.add.graphics();
    this.blackHolePulseGraphics = this.add.graphics();
    this.gateGraphics = this.add.graphics();
    this.snakeGraphics = this.add.graphics();
    this.biteGraphics = this.add.graphics();
    this.biteLabels = [];
    // +1: the regular trio (SAFE/RUSH/CHAOS), plus one extra slot for the
    // legendary STARFRUIT reveal, which can appear alongside them.
    for (let i = 0; i < GameConfig.bites.spawn.count + 1; i++) {
      const label = this.add
        .text(0, 0, "", {
          fontFamily: "Arial, sans-serif",
          fontSize: "16px",
          fontStyle: "bold",
          color: "#0f1117",
        })
        .setOrigin(0.5)
        .setVisible(false);
      this.biteLabels.push(label);
    }

    this.scoreText = this.add
      .text(0, 0, "", {
        fontFamily: "Arial, sans-serif",
        fontSize: "20px",
        fontStyle: "bold",
        color: "#f5f7fa",
      })
      .setOrigin(0, 0.5);

    this.subLineText = this.add
      .text(0, 0, "", {
        fontFamily: "Arial, sans-serif",
        fontSize: "13px",
        color: "#8b93a7",
      })
      .setOrigin(0, 0.5);

    // New player goal clarity: Endless only. Visible immediately for a
    // returning player; stays hidden for a first-ever run until the
    // one-time emphasized hint settles into it (completeReadyGate()'s
    // callback to feedback.showEndlessGoalHint()) - never both visible at
    // once. Always `false` for Campaign/Daily (endlessContext is null).
    this.endlessObjectiveText = this.add
      .text(0, 0, "GROW AS LONG AS YOU CAN", {
        fontFamily: "Arial, sans-serif",
        fontSize: "12px",
        fontStyle: "bold",
        color: "#8b93a7",
        align: "center",
      })
      .setOrigin(0.5, 0)
      .setDepth(41)
      .setVisible(this.endlessContext !== null && !this.shouldShowEndlessGoalHint);

    // Small world icon next to the level name - drawn once (it never
    // changes mid-level) and simply repositioned on resize. Endless has no
    // Campaign world of its own (never registered in LevelRegistry/
    // WorldRegistry - see buildEndlessLevelDefinition()'s doc), so
    // this.world() is never called for it; the graphics object stays
    // empty/invisible instead.
    this.worldIconGraphics = this.add.graphics();
    if (!this.endlessContext) {
      drawWorldIcon(this.worldIconGraphics, this.world().theme, 0, 0, 7);
    }

    // The reward line never changes mid-level - computed once from
    // GameConfig, not re-typed as a literal, so the on-screen number can
    // never drift from what the engine actually pays out.
    const { rewardGrowth, rewardScore } = GameConfig.bites.chaos.speedTrial;
    this.speedTrialRewardText = this.add
      .text(0, 0, `SURVIVE TO WIN +${rewardGrowth} LENGTH • +${rewardScore} SCORE`, {
        fontFamily: "Arial, sans-serif",
        fontSize: "14px",
        fontStyle: "bold",
        color: "#f5f7fa",
      })
      .setOrigin(0.5)
      .setDepth(42)
      .setVisible(false);

    // Small but clearly visible - a QA-only run must never be mistaken
    // for a real one, on-screen for its entire duration.
    this.qaModeText = this.add
      .text(0, 0, this.qaMode ? qaModeLabel(this.qaMode) : "", {
        fontFamily: "Arial, sans-serif",
        fontSize: "12px",
        fontStyle: "bold",
        color: "#0f1117",
        backgroundColor: "#ffd166",
        padding: { x: 6, y: 3 },
      })
      .setOrigin(0.5, 0)
      .setDepth(50)
      .setVisible(this.qaMode !== null);

    // PR13-C (Visual Feedback V2): the single typed controller for every
    // headline/skill/near-snake/status feedback element this PR adds or
    // restyles - constructed once here, after `gateGraphics` exists (it
    // owns the gate-glow tween lifecycle fix), reused for this scene's
    // entire lifetime (`resetForNewRun()` on Restart, `destroy()` on
    // teardown - see `teardown()`).
    this.feedback = new VisualFeedbackController(this, this.gateGraphics);
    this.feedback.setGatePosition(this.levelGame.exitPosition);

    // The compact, persistent "TRICK BITE READY: N" indicator - mobile-
    // safe (small, top-of-board, never over the touch controls), visible
    // only while a window is actually active.
    this.trickBiteWindowText = this.add
      .text(0, 0, "", {
        fontFamily: "Arial, sans-serif",
        fontSize: "12px",
        fontStyle: "bold",
        color: hexToCss(GameConfig.colors.star),
        backgroundColor: "#0f1117cc",
        padding: { x: 6, y: 3 },
      })
      .setOrigin(0.5, 0)
      .setDepth(41)
      .setVisible(false);

    if (this.qaMode === "scoring") {
      // Anchored to the top-left, well clear of the mobile controls at the
      // bottom of the screen - positioned in layoutHud().
      this.qaScoringOverlayText = this.add
        .text(0, 0, "", {
          fontFamily: "monospace",
          fontSize: "11px",
          color: "#f5f7fa",
          backgroundColor: "#0f1117cc",
          padding: { x: 6, y: 4 },
          lineSpacing: 3,
        })
        .setOrigin(0, 0)
        .setDepth(51);
    }

    // `?qa=speed` (PR11 - Speed Engine V2): the movement-speed diagnostics
    // overlay - never shown in normal gameplay, same strict host-allowlist
    // gate as every other QA mode (see `resolveQaMode()`).
    if (this.qaMode === "speed") {
      this.qaSpeedOverlayText = this.add
        .text(0, 0, "", {
          fontFamily: "monospace",
          fontSize: "11px",
          color: "#f5f7fa",
          backgroundColor: "#0f1117cc",
          padding: { x: 6, y: 4 },
          lineSpacing: 3,
        })
        .setOrigin(0, 0)
        .setDepth(51);
    }

    // `?qa=endless` - minimal debug counters for lifecycle testing (bites
    // on board, engine phase, whether a respawn is pending) - all already
    // exposed on `getSnapshot()`, no new engine API needed.
    if (this.qaMode === "endless") {
      this.qaEndlessDebugText = this.add
        .text(0, 0, "", {
          fontFamily: "monospace",
          fontSize: "11px",
          color: "#f5f7fa",
          backgroundColor: "#0f1117cc",
          padding: { x: 6, y: 4 },
          lineSpacing: 3,
        })
        .setOrigin(0, 0)
        .setDepth(51);
    }

    this.createPauseButton();
    this.createHelpButton();
    this.howToPlayOverlay = createHowToPlayOverlay(this, 95, () => this.closeHelp());
    this.nameModal = createNameEntryModal(this, 97, {
      onSave: (name) => this.handleSaveName(name),
      onNotNow: () => {},
    });
    this.createPauseOverlay();
    this.createGameOverOverlay();
    this.createLevelCompleteOverlay();
    this.createIntroOverlay();
    this.createReadyIndicator();
    this.orientationGate = attachOrientationGate(this, {
      requiresExplicitResume: () => true,
      onBlockStart: () => this.onOrientationBlockStart(),
      onBlockEnd: () => this.onOrientationBlockEnd(),
    });

    this.recomputeLayout();
    this.renderBoard();
    this.renderObstacles();
    this.renderPortals();
    this.renderBlackHoles();
    this.renderGate();
    this.renderSnakeBites();
    this.renderSnakePosition();
    this.updateHud();

    this.setupInput();

    if (this.qaMode === "feedback") this.buildFeedbackQaGallery();
    if (this.qaMode === "chaos" || this.qaMode === "endless") this.buildChaosQaControls();

    this.scale.on("resize", this.onResize, this);
    window.addEventListener("pagehide", this.handlePageHide);
    this.events.once("shutdown", () => this.teardown());
    this.events.once("destroy", () => this.teardown());

    if (this.isIntroActive) {
      this.showIntroOverlay();
    } else if (this.endlessContext) {
      // A returning player skips CHOOSE YOUR BITE entirely. For every
      // other mode that means ticking begins immediately, unchanged from
      // before this feature - but Endless always shows its own short
      // ready prompt first, on every run, so it must be evaluated here
      // too, not only from `dismissIntro()`.
      this.beginReadyGate();
    }

    // PR31 (portrait-only mobile gameplay): must run last, after every
    // other pre-gameplay overlay/state above is already set up - if the
    // device is a phone in landscape, this immediately covers whatever was
    // just shown with the ROTATE YOUR DEVICE gate instead, before the
    // player ever sees a frame of the wrong orientation. See
    // `OrientationGate.ts`'s own doc.
    this.orientationGate.check();
  }

  /**
   * The only place `this.qaMode` is allowed to influence the engine's
   * RNG - everything else about QA mode (the indicator, skipping
   * submission/persistence) is decided elsewhere from the same field.
   * Production runs (`qaMode === null`) get `{}`, i.e. every source
   * defaults exactly as it always has (`Math.random`).
   */
  private buildQaLevelGameOptions(): {
    chaosRandom?: ReturnType<typeof forceNextChaosWormholeRandom>;
    starfruitRandom?: ReturnType<typeof forceStarfruitEligibleRandom>;
    qaOverridesEnabled?: boolean;
  } {
    if (this.qaMode === "starfruit") return { starfruitRandom: forceStarfruitEligibleRandom() };
    if (this.qaMode === "wormhole") return { chaosRandom: forceNextChaosWormholeRandom() };
    // QA follow-up (deterministic gameplay controls, section D): only ever
    // reached via `?qa=chaos`, which - like every other `qaMode` - is
    // already gated by `resolveQaMode()`'s `isQaModeAllowedHost` check
    // before this method is ever called, so `qaOverridesEnabled` can never
    // be true on the real production hostname.
    if (this.qaMode === "chaos") return { qaOverridesEnabled: true };
    return {};
  }

  /** `undefined` for a campaign run; the Daily analytics context (challenge id/generator version/attempt number/attempts remaining) for a Daily one - spread into every gameplay event's `RunContext.daily` field. Never includes the participant id, a display name, or anything else PII - see `DailyAnalyticsProps`'s doc. */
  /**
   * Instant Play & Onboarding V1: maps this run's backend-shared
   * `RunSource` (`data.runSource`, `"play"` by default) plus
   * `this.entrySource` to the analytics-only `AnalyticsRunSource` -
   * `"play"` splits into `"instant_play"` (EntryScene's PLAY NOW) vs
   * `"menu"` (every other "play" launch: Main Menu ENDLESS/CONTINUE,
   * Endless PLAY AGAIN, a fresh Level Select pick), every other value
   * passes through unchanged. See `AnalyticsRunSource`'s own doc for why
   * this is never the backend `RunSource` type itself.
   */
  private analyticsRunSource(runSource: RunSource): AnalyticsRunSource {
    if (this.entrySource === "instant_play") return "instant_play";
    return runSource === "play" ? "menu" : runSource;
  }

  private dailyAnalyticsContext(): DailyRunAnalyticsContext | undefined {
    if (!this.dailyContext) return undefined;
    const spec = this.dailyContext.spec;
    return {
      challengeId: spec.challengeId,
      generatorVersion: spec.generatorVersion,
      attemptNumber: this.dailyContext.attemptNumber,
      attemptsRemaining: this.dailyContext.attemptsRemaining,
      layoutId: spec.formatVersion === 1 ? spec.mapFamily : spec.formatVersion === 2 ? spec.layoutArchetype : spec.archetype,
      scoringProfileId: spec.formatVersion === 1 ? undefined : spec.scoringProfile.id,
    };
  }

  update(_time: number, rawDelta: number): void {
    // PR11 (Speed Engine V2) frame-delta/catch-up guard: a backgrounded or
    // throttled tab can hand back one enormous `delta` on resume - clamp
    // it BEFORE it ever reaches active-time tracking, SPEED TRIAL/Daily
    // pressure timers, or the movement accumulator, so background time is
    // discarded rather than silently counted as real active/trial/pressure
    // time. Ordinary gameplay (a real frame is a few ms to a few tens of
    // ms) is completely unaffected - this only ever engages for a delta
    // far outside normal frame timing. See `src/core/MovementAccumulator.ts`
    // for the full reasoning and the directly-testable pure logic (this
    // Scene has no unit test harness of its own).
    const delta = clampActiveDeltaMs(rawDelta);
    this.trackActiveTime(delta);
    this.updateQaScoringOverlay();
    this.updateQaSpeedOverlay();
    this.updateEndlessQaDebugCounters();

    if (this.isIntroActive || this.isReadyGateActive || this.isPaused || this.isGameOver || this.isLevelComplete || this.isEndlessRunOver || this.orientationGate.blocked) {
      return;
    }

    this.processRealTime(delta);
    this.updateSpeedTrialCountdown();
    const liveSnapshot = this.levelGame.getSnapshot();
    // PR32: the WORMHOLE snake overlay needs the same render-only
    // interpolated positions the main snake is drawn at - computed here,
    // BEFORE this frame's own ticks (`snakeRenderElapsedMs` is advanced
    // just below), so `feedback.update()` keeps its existing "reflects
    // state as of the start of this frame, one frame behind this frame's
    // own ticks" timing (identical to how `liveSnapshot` itself already
    // lags this frame's ticks here) - only WHICH coordinates it uses
    // (visual vs logical) changes, not when it runs.
    const feedbackRenderSnake = this.computeCurrentRenderSnake(liveSnapshot.snake);
    this.feedback.update(delta, liveSnapshot, feedbackRenderSnake);
    this.audio.update(delta, liveSnapshot);
    this.trackEndlessFastestStep(liveSnapshot.stepMs);

    this.snakeRenderElapsedMs += delta;
    this.accumulatorMs = runMovementCatchUp({
      accumulatorMs: this.accumulatorMs,
      deltaMs: delta,
      getStepMs: () => this.levelGame.getSnapshot().stepMs,
      tick: () => this.tick(),
      hasEnded: () => this.isGameOver || this.isLevelComplete || this.isEndlessRunOver,
    });

    // PR32: the snake's own POSITION render is a per-FRAME concern, not a
    // per-TICK one - called exactly once here regardless of how many
    // ticks `runMovementCatchUp()` just ran (0 to `MAX_MOVEMENT_ITERATIONS_PER_FRAME`),
    // so a multi-tick catch-up burst still only redraws `snakeGraphics`
    // once for this frame, using whatever `snakeRoute` state the burst's
    // LAST tick left behind (see `advanceSnakeRoute()`).
    this.renderSnakePosition();
  }

  /** Endless only - the fastest (smallest) INTERPOLATED step interval observed this run, for the `run_finished` `fastest_step_ms` field. A no-op for every other mode. */
  private trackEndlessFastestStep(stepMs: number): void {
    if (!this.endlessContext) return;
    this.endlessFastestStepMs = this.endlessFastestStepMs === null ? stepMs : Math.min(this.endlessFastestStepMs, stepMs);
  }

  /**
   * `RunTracker.activeDurationMs` counts only time the player had actual
   * control - excludes the first-run tutorial, Pause, HOW TO PLAY, the
   * Game Over/Level Complete overlays, and the automatic gate-exit walk.
   * Checked every frame rather than at each state-change call site, so it
   * can never drift out of sync with a flag this scene already tracks.
   */
  private trackActiveTime(delta: number): void {
    const isActive =
      !this.isIntroActive &&
      !this.isReadyGateActive &&
      !this.isPaused &&
      !this.isGameOver &&
      !this.isLevelComplete &&
      !this.isExiting &&
      !this.isEndlessRunOver &&
      !this.orientationGate.blocked;
    if (isActive) this.runTracker.addActiveMs(delta);
    // Paused time (Pause itself or the HOW TO PLAY overlay, which pauses
    // the same way) is tracked separately, purely for telemetry - it never
    // contributes to activeDurationMs/Pace Bonus either way.
    if (this.isPaused && !this.isGameOver && !this.isLevelComplete && !this.isEndlessRunOver) this.runTracker.addPausedMs(delta);
  }

  /**
   * Advances the engine's real-time-only state (SPEED TRIAL countdown,
   * the ~250ms delayed bite respawn) and resyncs the board/HUD/gate with
   * whatever actually changed - never deferring that resync to the next
   * input or grid step. These transitions happen outside the tick loop,
   * so `tick()`'s own rendering can't be relied on to catch them: a
   * SPEED TRIAL success can open the gate or spawn a fresh triplet, and
   * either must be visible immediately, this frame.
   */
  private processRealTime(delta: number): void {
    const before = this.levelGame.getSnapshot();
    const timerEvent = this.levelGame.advanceRealTime(delta);
    const after = this.levelGame.getSnapshot();

    const gateJustOpened = after.isGateOpen && !before.isGateOpen;
    const bitesChanged = after.bites !== before.bites;
    if (gateJustOpened || bitesChanged) {
      // PR32: only bites actually change here (a real-time event, never a
      // grid tick) - the snake's own logical position is untouched, so
      // there's nothing for `renderSnakePosition()` to redraw.
      this.renderSnakeBites();
      this.updateHud();
      this.trackStarfruitSpawn();
    }
    if (gateJustOpened) {
      // A completed SPEED TRIAL can open the gate without ever going
      // through tick() - the READY indicator (and the bite glow, via the
      // now-empty bites re-render above) must clear immediately too.
      this.updateTrickBiteWindowIndicator();
      this.renderGate();
      this.feedback.onGateOpened();
      this.audio.onGateOpened(after.starfruitGateRemainingMs !== null);
      // The only `gateJustOpened` path that doesn't already go through
      // handleTickResult()'s "gate-opened" case (which tracks its own
      // gate_opened) - a completed SPEED TRIAL, driven by advanceRealTime()
      // rather than tick().
      this.trackGateOpenedAnalytics();
    }

    if (timerEvent && timerEvent.kind === "starfruit-gate-expired") {
      this.renderGate();
      this.feedback.onStarfruitGateExpired();
      this.audio.onStarfruitGateExpired();
      return;
    }

    if (timerEvent && timerEvent.kind === "arena-cleared") {
      // Endless Growth V1 only (`level.completeWhenNoBitesCanSpawn`) - no
      // Campaign/Daily level ever sets that policy, so this branch is
      // structurally unreachable for either of those modes.
      this.handleArenaCleared();
      return;
    }

    if (timerEvent) {
      this.runTracker.recordBiteEvent(timerEvent);
      // A completed SPEED TRIAL is the only `LevelRealTimeEvent` besides
      // `starfruit-gate-expired` (handled above) - never a cell to anchor
      // a near-snake/graphics accent to, so `atPoint: null`.
      const ctx = this.impactContext(before, after, null);
      this.feedback.onBiteEvent(timerEvent, ctx);
      this.audio.onBiteEvent(timerEvent, { wormholeAlreadyActiveBefore: ctx.wormholeAlreadyActiveBefore });
    }
  }

  /** `BiteImpactContext` builder - every field is read straight off the same before/after snapshots the caller already has, never reconstructed. Shared by every `feedback.onBiteEvent()` call site. */
  private impactContext(before: LevelGameSnapshot, after: LevelGameSnapshot, atPoint: Point | null): BiteImpactContext {
    return {
      atPoint,
      wormholeAlreadyActiveBefore: before.wormholeRemainingMs !== null,
      snakeAfter: after.snake,
      snakeBefore: before.snake,
      direction: after.direction,
    };
  }

  // ---------------------------------------------------------------------
  // Input wiring
  // ---------------------------------------------------------------------

  private setupInput(): void {
    this.keyboardInput = new KeyboardInput(
      (direction) => this.handleDirectionInput(direction, "keyboard"),
      () => this.togglePause(),
    );
    this.keyboardInput.start();

    const canvas = this.sys.game.canvas;
    this.touchInput = new TouchInput(
      canvas,
      (direction) => this.handleDirectionInput(direction, "touch"),
      () => this.levelGame.getSnapshot().direction,
    );
    this.touchInput.start();
  }

  private handleDirectionInput(direction: Direction, inputMode: InputMode): DirectionHandlerResult {
    // PR31: checked FIRST, ahead of the ready-gate branch too - a swipe
    // must never dismiss the ready-gate (or do anything else) while the
    // ROTATE YOUR DEVICE gate is covering the screen.
    if (this.orientationGate.blocked) return "ignored";
    if (this.isReadyGateActive) {
      return this.handleReadyGateInput(direction, inputMode);
    }
    if (this.isGameOver || this.isPaused || this.isLevelComplete || this.isIntroActive || this.isExiting || this.isEndlessRunOver) return "ignored";
    return this.inputQueue.push(direction, this.levelGame.getSnapshot().direction);
  }

  private teardown(): void {
    this.trackAbandonedRunIfNeeded();
    this.keyboardInput?.stop();
    this.touchInput?.stop();
    this.scale.off("resize", this.onResize, this);
    window.removeEventListener("pagehide", this.handlePageHide);
    this.nameModal.destroy();
    this.feedback.destroy();
    this.audio.onSceneShutdown();
    // BLACK HOLE's frame-pulse and per-hole swirl tweens/objects (see
    // `renderBlackHoles()`) - Phaser's own scene shutdown already tears
    // down every tween/GameObject for this scene, but this is explicit so
    // a restart or level transition never carries a stray pulse/swirl
    // over into the next scene instance.
    this.tweens.killTweensOf(this.blackHolePulseGraphics);
    this.tweens.killTweensOf(this.blackHoleSwirls);
    this.blackHoleSwirls.forEach((swirl) => swirl.destroy());
    this.blackHoleSwirls = [];
    // The open gate's own portal swirl (see `renderGate()`) - same
    // explicit-teardown reasoning as the black hole swirls above.
    if (this.gatePortalSwirl) this.tweens.killTweensOf(this.gatePortalSwirl);
    this.gatePortalSwirl?.destroy();
    this.gatePortalSwirl = null;
  }

  /**
   * Best-effort `run_finished: "menu_exit"` for a run still in progress
   * (never reached death or completion) when this scene tears down - a
   * safety net, not currently a reachable UI path: every exit control
   * (RETRY/REPLAY/LEVELS/WORLDS/NEXT LEVEL/LEADERBOARD) only
   * ever appears on the Game Over or Level Complete overlay, both of
   * which already emit `run_finished` (via `runFinishedGuard`, which this
   * also goes through) before any of those buttons can be pressed. Kept
   * so a future in-run exit/restart control - or an unexpected scene
   * teardown - can never silently produce zero `run_finished` events for
   * an otherwise-tracked run. Never fires before at least one executed
   * movement step (`moveCount > 0`) - tearing down during the intro, or
   * before the very first tick, isn't a "run" to report.
   */
  private trackAbandonedRunIfNeeded(): void {
    // Daily Challenge's own equivalent - a DNF result submission for an
    // attempt that never reached death/completion (e.g. the scene tore
    // down mid-run) - runs independently of the analytics guard below,
    // since it reports to the Daily backend, not PostHog.
    this.submitDailyDnfOnTeardownIfNeeded();

    if (this.runFinishedGuard.hasEmitted) return;
    if (!shouldPersistRun(this.qaMode)) return;
    const snapshot = this.levelGame.getSnapshot();
    if (snapshot.moveCount === 0) return;

    const bites = this.runTracker.getBiteCounts();
    this.runFinishedGuard.emitOnce(() =>
      trackRunFinished({
        runId: this.runTracker.runId,
        worldId: this.level.worldId,
        levelId: this.level.id,
        gameMode: this.gameMode,
        platform: this.platform,
        daily: this.dailyAnalyticsContext(),
        runSource: this.analyticsRunSource(this.runSourceValue),
        outcome: "menu_exit",
        score: snapshot.score + snapshot.skillScore,
        finalLength: snapshot.length,
        elapsedMs: this.runTracker.getElapsedMs(),
        activePlayMs: this.runTracker.getActiveDurationMs(),
        steps: snapshot.moveCount,
        newBestScore: this.previousLevelStats.highScore,
        newBestStars: this.previousLevelStats.stars,
        bestScoreImproved: false,
        bestStarsImproved: false,
        safeBites: bites.safe,
        rushBites: bites.rush,
        chaosBites: bites.chaos,
        maxRushMultiplier: bites.maxRushStreak,
      }),
    );
  }

  /**
   * Best-effort: if this scene tears down mid-Daily-attempt (never
   * reached `handleGameOver()`/`handleLevelComplete()` - a restart, menu
   * exit, closing the game, or a stray unmount), the attempt was already
   * consumed at reservation time regardless, but the backend should still
   * learn about it - as `"incomplete"`, never a ranked result and never a
   * personal best (see `DailyAttemptOutcome.status`'s doc and
   * `record_daily_result()` in `005_daily_survival.sql`). Fire-and-forget:
   * never blocks scene teardown, and a failed submission here is simply
   * lost telemetry, not a data-loss bug (the attempt count itself is
   * already correct server-side either way).
   */
  private submitDailyDnfOnTeardownIfNeeded(): void {
    if (!this.dailyContext || this.dailyAttemptFinished) return;
    // A QA preview never reserved a real attempt in the first place (see
    // `DailyChallengeScene.startQaPreview()` - it never calls
    // `daily-attempt`), so there is nothing for `daily-result` to
    // legitimately attach this DNF to, and it must never be called for
    // one regardless: no real attempt/participation row exists to update.
    if (!shouldSubmitDailyResult(this.dailyContext.isQaPreview)) return;
    const snapshot = this.levelGame.getSnapshot();
    if (snapshot.moveCount === 0) return;
    this.dailyAttemptFinished = true;

    // `keepalive: true` (PR28) - this call is reached both by an in-app
    // scene teardown and by `handlePageHide()` (a real tab close/
    // navigation-away), so the request must be able to outlive the page
    // that issued it rather than being silently aborted mid-flight.
    void DailyChallengeClient.submitResult(
      {
        challengeId: this.dailyContext.spec.challengeId,
        attemptNumber: this.dailyContext.attemptNumber,
        status: "incomplete",
        score: snapshot.score + snapshot.skillScore,
        finalLength: snapshot.length,
        activeDurationMs: this.runTracker.getActiveDurationMs(),
        steps: snapshot.moveCount,
        scoringRulesVersion: DAILY_SCORING_RULES_VERSION,
        platform: this.platform,
      },
      { keepalive: true },
    );
  }

  // ---------------------------------------------------------------------
  // Game loop
  // ---------------------------------------------------------------------

  private tick(): void {
    const before = this.levelGame.getSnapshot();
    const direction = this.inputQueue.consume(before.direction);
    const result = this.levelGame.tick(direction);

    this.recentDirections.push(direction);
    if (this.recentDirections.length > RECENT_DIRECTIONS_MAX) this.recentDirections.shift();

    this.updateRushGlow(result);
    this.advanceSnakeRoute(result);
    this.renderSnakeBites();
    this.detectWormholeWrap(before, result);
    this.detectPortalTraversal(result);
    this.trackStarfruitSpawn();
    this.showSkillFeedback();
    this.showBiteBonusFeedback();
    this.updateTrickBiteWindowIndicator();
    this.handleTickResult(result, before);
  }

  /**
   * PR32 (Snake Movement Smoothing) route/trail replacement: appends this
   * tick's newly committed head position to `snakeRoute` so
   * `sampleSnakeRoute()` can walk a continuous centerline behind it. Only
   * `"moved"`/`"ate"`/`"gate-opened"` - the same three result types
   * `detectWormholeWrap()`/`detectPortalTraversal()` already treat as "a
   * real, continued movement happened" (see those methods' own doc) - get
   * appended to the route at all; every other result (the gate-entry/exit-walk's
   * own `"exiting"` steps, `"level-complete"`, `"game-over"`,
   * `"arena-cleared"`) instead sets `snakeRenderSnapped`, which makes
   * `computeCurrentRenderSnake()` draw the exact current logical snake
   * with no route sampling at all - the same "snap to the committed
   * state, no interpolation" behavior this feature has always had for
   * non-ordinary movement. This is also what keeps the automatic
   * gate-exit walk's own existing discrete (non-smoothed) rendering
   * completely unchanged with no dedicated `isExiting`/phase check here -
   * every exit-walk tick (including the very first, gate-entry step)
   * already returns `"exiting"`/`"level-complete"`, never one of the
   * three route-appending types - and why an ordinary death can never
   * render one visual step behind its true final position: `"game-over"`
   * snaps immediately to the exact cell `LevelGame` actually stopped at
   * (wall/obstacle/self death never even commits the head onto the fatal
   * cell in the first place - see `LevelGame.tick()`'s own doc).
   */
  private advanceSnakeRoute(result: LevelTickResult): void {
    const isOrdinaryMovement = result.type === "moved" || result.type === "ate" || result.type === "gate-opened";
    this.snakeRenderSnapped = !isOrdinaryMovement;
    if (isOrdinaryMovement) {
      const snapshot = this.levelGame.getSnapshot();
      const newHead = snapshot.snake[0];
      if (newHead) {
        const maxLength = snapshot.length + ROUTE_HISTORY_MARGIN;
        this.snakeRoute = appendRoutePoint(this.snakeRoute, { x: newHead.x, y: newHead.y }, maxLength);
      }
    }
    this.snakeRenderElapsedMs = 0;
    this.snakeRenderTransitionMs = computeSnakeTransitionDurationMs(this.levelGame.getSnapshot().stepMs);
  }

  /**
   * PR32 route/trail replacement: the single render-only entry point both
   * `renderSnakePosition()` and `update()`'s WORMHOLE-overlay feed use -
   * see `snakeRenderSnapped`'s own doc for why non-ordinary movement
   * bypasses `snakeRoute` sampling entirely.
   */
  private computeCurrentRenderSnake(fallbackSnake: readonly Point[]): readonly Point[] {
    if (this.snakeRenderSnapped) return fallbackSnake;
    const progress = computeSnakeRenderProgress(this.snakeRenderElapsedMs, this.snakeRenderTransitionMs);
    return sampleSnakeRoute(this.snakeRoute, progress, fallbackSnake, fallbackSnake.length);
  }

  /**
   * PR31 follow-up fix: advances `rushGlowTicksRemaining` for this tick -
   * see that field's own doc for the full "why ticks, not wall-clock time"
   * reasoning. Order matters: a fresh RUSH bite refreshes to the full
   * window (a real back-to-back streak keeps reading as continuously
   * gold) even on the exact tick the combo also happens to end up at
   * `rushStack === 0` is structurally impossible here (eating RUSH always
   * increments it) - but an explicit, unconditional "combo is actually
   * over" check still runs last regardless, so this can never drift out
   * of sync with the engine's own authoritative `rushStack` for any
   * reason this method doesn't already anticipate.
   */
  private updateRushGlow(result: LevelTickResult): void {
    if (this.rushGlowTicksRemaining > 0) this.rushGlowTicksRemaining--;
    if ((result.type === "ate" || result.type === "gate-opened") && result.biteKind === "rush") {
      this.rushGlowTicksRemaining = RUSH_GLOW_TICKS;
    }
    if (this.levelGame.getSnapshot().rushStack === 0) this.rushGlowTicksRemaining = 0;
  }

  /** `0` (no RUSH identity) to `1` (full strength), smoothly fading over `RUSH_GLOW_FADE_TICKS` - see `rushGlowTicksRemaining`'s own doc. */
  private rushGlowIntensity(): number {
    if (this.rushGlowTicksRemaining <= 0) return 0;
    return Math.min(1, this.rushGlowTicksRemaining / RUSH_GLOW_FADE_TICKS);
  }

  /**
   * Scoring V2: short floating Skill-lane feedback for whatever
   * `getLastSkillMoment()` resolved on the tick that just happened (`null`
   * on almost every tick - most executed moves credit nothing). Reads it
   * once, immediately after `tick()` returns, the same pattern
   * `detectWormholeWrap()` already uses for the engine's own before/after
   * snapshots. PR13-C: routed through `feedback`'s Skill-lane priority
   * queue instead of its own one-off toast, so it can never overlap
   * TRICK/CLUTCH BITE.
   */
  private showSkillFeedback(): void {
    const moment = this.levelGame.getLastSkillMoment();
    if (moment) {
      this.feedback.onSkillMoment(moment);
      this.audio.onSkillMoment(moment);
    }
  }

  /** TRICK BITE / CLUTCH BITE: Skill-lane feedback for whatever `getLastBiteBonus()` resolved on the tick that just happened - `null` on almost every tick. PR13-B's WORMHOLE-sourced copy (`WORMHOLE CLUTCH!`/`WORMHOLE BITE!`) is preserved by `mapBiteBonusToSkillFeedback()`. */
  private showBiteBonusFeedback(): void {
    const bonus = this.levelGame.getLastBiteBonus();
    if (bonus) {
      this.feedback.onBiteBonus(bonus);
      this.audio.onBiteBonus(bonus);
    }
  }

  /** TRICK BITE: the persistent "TRICK BITE READY: N" indicator - visible only while a window is active, and cleared immediately whenever it isn't (expired, consumed, the gate is open, or the run ended - `hideRealTimeEffectVisuals()` covers the last one). PR13-B: shows "WORMHOLE BITE READY: N" instead whenever WORMHOLE is the currently-authoritative source for the active window. */
  private updateTrickBiteWindowIndicator(): void {
    const snap = this.levelGame.getSnapshot();
    if (snap.trickBiteWindowRemainingMoves > 0 && snap.phase === "playing" && !snap.isGateOpen) {
      const label = snap.trickBiteWindowSource === "wormhole" ? "WORMHOLE BITE READY" : "TRICK BITE READY";
      this.trickBiteWindowText.setText(`${label}: ${snap.trickBiteWindowRemainingMoves}`).setVisible(true);
    } else {
      this.trickBiteWindowText.setVisible(false);
    }
  }

  /** PR13-C: the wrap flash now fires from `LevelGame.getLastWormholeWrapSuccess()` - the engine's own authoritative signal (see that method's doc) - rather than inferring a wrap from a >1-cell jump between two snapshots. A collision/death attempt can never reach this (it returns "game-over", excluded below, before the engine would ever set the flag), so it can never produce a false wrap celebration. */
  private detectWormholeWrap(before: LevelGameSnapshot, result: LevelTickResult): void {
    if (result.type !== "moved" && result.type !== "ate" && result.type !== "gate-opened") return;
    if (!this.levelGame.getLastWormholeWrapSuccess()) return;
    const prevHead = before.snake[0];
    const newHead = this.levelGame.getSnapshot().snake[0];
    if (!prevHead || !newHead) return;
    this.feedback.onWormholeWrap(prevHead, newHead);
    this.audio.onWormholeWrap();
  }

  /**
   * Portals V1 (Saturn World): fires the entry/exit flash for whatever
   * `getLastPortalTraversal()` resolved on the tick that just happened -
   * same read-immediately-after-`tick()` pattern as `detectWormholeWrap()`
   * above. A collision/death attempt can never reach this (the engine
   * only ever sets the traversal once the resolved head has survived
   * every lethal check), so it can never produce a false traversal
   * celebration. Visual-only for V1 - see `portals.audio` doc note; no
   * dedicated audio cue is wired here.
   */
  private detectPortalTraversal(result: LevelTickResult): void {
    if (result.type !== "moved" && result.type !== "ate" && result.type !== "gate-opened") return;
    const traversal = this.levelGame.getLastPortalTraversal();
    if (!traversal) return;
    this.feedback.onPortalTraversal(traversal.entry, traversal.exit);
  }

  /** One-time-per-run analytics hook: the moment STARFRUIT is first seen among the live bites (whether or not it's ever eaten), tell RunTracker - spawning isn't itself an eaten-bite event, so it can't be picked up via `recordBiteEvent`. Also tells `audio` whenever STARFRUIT's live presence toggles (absent<->present) - unlike the analytics flag above, this is never one-time-per-run, since STARFRUIT's appearance sound must play again on a later fresh spawn in the same run. */
  private trackStarfruitSpawn(): void {
    const present = this.levelGame.getSnapshot().bites.some((bite) => bite.kind === "starfruit");
    this.audio.onStarfruitPresenceChanged(present);
    if (this.starfruitSpawnTracked || !present) return;
    this.runTracker.recordStarfruitSpawned();
    this.starfruitSpawnTracked = true;
  }

  /**
   * `gate_opened` - called from both places the gate can actually open:
   * `handleTickResult()`'s "gate-opened" case (a SAFE/RUSH/CHAOS bite
   * reaching the real target length, or STARFRUIT's temporary gate) and
   * `processRealTime()`'s own `gateJustOpened` check (a completed SPEED
   * TRIAL, which never goes through `tick()`) - never both for the same
   * gate opening, since those two paths are mutually exclusive. Never
   * fires for a QA-mode run.
   */
  private trackGateOpenedAnalytics(): void {
    if (!shouldPersistRun(this.qaMode)) return;
    const snapshot = this.levelGame.getSnapshot();
    trackGateOpened({
      runId: this.runTracker.runId,
      worldId: this.level.worldId,
      levelId: this.level.id,
      gameMode: this.gameMode,
      daily: this.dailyAnalyticsContext(),
      score: snapshot.score,
      length: snapshot.length,
      elapsedMs: this.runTracker.getElapsedMs(),
      steps: snapshot.moveCount,
    });
  }

  private handleTickResult(result: LevelTickResult, before: LevelGameSnapshot): void {
    const after = this.levelGame.getSnapshot();
    switch (result.type) {
      case "ate": {
        this.runTracker.recordBiteEvent(result.event);
        this.updateHud();
        const ateCtx = this.impactContext(before, after, this.eatenBitePoint(result.biteKind, before.bites));
        this.feedback.onBiteEvent(result.event, ateCtx);
        this.feedback.pulseScoreHud(this.scoreText, eventIntensityTier(result.event), result.event);
        this.audio.onBiteEvent(result.event, { wormholeAlreadyActiveBefore: ateCtx.wormholeAlreadyActiveBefore });
        break;
      }
      case "gate-opened": {
        this.runTracker.recordBiteEvent(result.event);
        this.trackGateOpenedAnalytics();
        this.updateHud();
        this.renderGate();
        const gateOpenedCtx = this.impactContext(before, after, this.eatenBitePoint(result.biteKind, before.bites));
        this.feedback.onBiteEvent(result.event, gateOpenedCtx);
        this.feedback.pulseScoreHud(this.scoreText, eventIntensityTier(result.event), result.event);
        this.feedback.onGateOpened();
        this.audio.onBiteEvent(result.event, { wormholeAlreadyActiveBefore: gateOpenedCtx.wormholeAlreadyActiveBefore });
        this.audio.onGateOpened(after.starfruitGateRemainingMs !== null);
        break;
      }
      case "exiting":
        this.onExitingStep();
        break;
      case "moved":
        break;
      case "game-over":
        // BLACK HOLE V1 (Saturn World): a short deterministic consumption
        // animation plays first - `handleGameOver()` itself (the overlay/
        // submission flow) only runs once it completes. Every other
        // reason goes straight to the normal instant presentation, byte-
        // for-byte unchanged.
        if (result.reason === "black_hole") {
          this.startBlackHoleConsumption();
        } else {
          this.handleGameOver(result.reason);
        }
        break;
      case "level-complete":
        this.handleLevelComplete();
        break;
    }
  }

  private eatenBitePoint(kind: BiteKind, previousBites: readonly Bite[]): Point | null {
    return previousBites.find((b) => b.kind === kind)?.point ?? null;
  }

  private togglePause(): void {
    if (this.isGameOver || this.isLevelComplete || this.isIntroActive || this.isReadyGateActive || this.isExiting || this.isHelpOpen || this.isEndlessRunOver || this.orientationGate.blocked) return;
    this.isPaused = !this.isPaused;
    this.feedback.onPauseChange(this.isPaused);
    this.audio.onPauseChange(this.isPaused);
    this.pauseOverlay.setVisible(this.isPaused);
    if (this.isPaused) {
      this.runTracker.recordPauseStart();
      this.pauseOverlay.setAlpha(0);
      this.tweens.add({ targets: this.pauseOverlay, alpha: 1, duration: 120 });
    }
  }

  private openHelp(): void {
    if (this.isGameOver || this.isLevelComplete || this.isIntroActive || this.isReadyGateActive || this.isExiting || this.isHelpOpen || this.isEndlessRunOver || this.orientationGate.blocked) return;
    this.wasPausedBeforeHelp = this.isPaused;
    // A false -> true transition here (opening HOW TO PLAY mid-play, not
    // via the Pause menu) is itself a pause start - HOW TO PLAY pauses the
    // same way Pause does (see trackActiveTime()), so it must count the
    // same way too. Opening it from the already-Paused menu is not a new
    // pause start - already counted.
    if (!this.wasPausedBeforeHelp) this.runTracker.recordPauseStart();
    this.isPaused = true;
    this.feedback.onPauseChange(true);
    this.audio.onPauseChange(true);
    this.isHelpOpen = true;
    this.howToPlayOverlay.setVisible(true);
  }

  private closeHelp(): void {
    if (!this.isHelpOpen) return;
    this.isHelpOpen = false;
    this.howToPlayOverlay.setVisible(false);
    this.isPaused = this.wasPausedBeforeHelp;
    this.feedback.onPauseChange(this.isPaused);
    this.audio.onPauseChange(this.isPaused);
  }

  private handleGameOver(reason: "wall" | "obstacle" | "self" | "black_hole"): void {
    this.isGameOver = true;
    this.hideRealTimeEffectVisuals();
    this.feedback.onDeath();
    this.audio.onDeath();
    this.cameras.main.shake(160, 0.006);

    // BLACK HOLE V1 (Saturn World) is a presentation-only distinction -
    // the on-screen reason text and the consumption animation
    // (`startBlackHoleConsumption()`) are the only things that know about
    // it. Every downstream consumer of a death reason (Daily/Endless
    // submission, the leaderboard RPC's own `death_reason` check
    // constraint, `AnalyticsDeathReason`) only ever recognizes
    // "wall"/"obstacle"/"self" - mapping to "obstacle" here (the closest
    // existing category: a fixed lethal tile, not a self-collision or a
    // wall) means zero backend/schema/analytics-type change is needed,
    // and "leaderboard ranking/scoring rules" stay completely untouched.
    const submissionReason = reason === "black_hole" ? "obstacle" : reason;

    if (this.dailyContext) {
      // Death is the expected, valid, ranked end of a Daily attempt - not
      // a "failure"/DNF. This applies uniformly whether the run was V2
      // survival (death is the ONLY way an attempt ends) or an
      // already-stored V1 date (which still has a gate/target length -
      // dying before reaching it is still a real result of a real,
      // fully-played attempt, exactly as ranked as reaching the gate
      // would have been). See `DailyAttemptOutcome.status`'s doc. Daily
      // never authors `blackHoles` on any of its levels, so `reason` can
      // never actually be `"black_hole"` here - `submissionReason` is
      // used anyway, purely so this call site's type never depends on
      // that being true.
      this.finishDailyAttempt(submissionReason);
      return;
    }

    if (this.endlessContext) {
      // Death is one of Endless's three record-eligible endings - never
      // routed through Campaign's ProgressStorage/ScoreV2Storage/
      // leaderboard, exactly like Daily above. Same "never actually
      // reachable, kept type-safe anyway" note as above.
      this.finishEndlessRun("death", submissionReason);
      return;
    }

    this.populateGameOverOverlay(reason);
    this.gameOverOverlay.setVisible(true);
    this.gameOverOverlay.setAlpha(0);
    this.tweens.add({ targets: this.gameOverOverlay, alpha: 1, duration: 200 });

    const snapshot = this.levelGame.getSnapshot();
    // A QA-mode run must never reach the leaderboard/queue - see the
    // `qaMode` field doc and `handleLevelComplete()` below for the same rule.
    if (shouldPersistRun(this.qaMode)) {
      const submission = this.runTracker.buildSubmission({
        status: "failed",
        // A failed run never earns Pace Bonus - Final Score here is just
        // Bite Score + whatever Skill Score was earned before dying.
        score: snapshot.score + snapshot.skillScore,
        biteScore: snapshot.score,
        skillScore: snapshot.skillScore,
        paceBonus: 0,
        stars: 0,
        finalLength: snapshot.length,
        moveCount: snapshot.moveCount,
        maneuverCounts: snapshot.maneuverCounts,
        doubleDangerCount: snapshot.doubleDangerCount,
        bestFlowStreak: snapshot.bestFlowStreak,
        trickBiteCount: snapshot.trickBiteCount,
        clutchBiteCount: snapshot.clutchBiteCount,
        deathReason: submissionReason,
      });
      void getLeaderboardService().submitRun(submission);

      const bites = this.runTracker.getBiteCounts();
      this.runFinishedGuard.emitOnce(() =>
        trackRunFinished({
          runId: this.runTracker.runId,
          worldId: this.level.worldId,
          levelId: this.level.id,
          gameMode: this.gameMode,
          platform: this.platform,
          daily: this.dailyAnalyticsContext(),
          runSource: this.analyticsRunSource(this.runSourceValue),
          outcome: "death",
          deathReason: submissionReason,
          score: submission.score,
          finalLength: submission.finalLength,
          elapsedMs: this.runTracker.getElapsedMs(),
          activePlayMs: this.runTracker.getActiveDurationMs(),
          steps: submission.moveCount,
          newBestScore: this.previousLevelStats.highScore,
          newBestStars: this.previousLevelStats.stars,
          bestScoreImproved: false,
          bestStarsImproved: false,
          safeBites: bites.safe,
          rushBites: bites.rush,
          chaosBites: bites.chaos,
          maxRushMultiplier: bites.maxRushStreak,
        }),
      );
    }
  }

  private onExitingStep(): void {
    if (!this.isExiting) {
      this.isExiting = true;
      this.inputQueue.clear();
      this.pauseButton.setVisible(false).disableInteractive();
      this.helpButton.setVisible(false).disableInteractive();
      this.hideRealTimeEffectVisuals();
      this.feedback.onGateEntry();
      this.audio.onGateEntry();
    }
    this.feedback.playGateEntryFlash();
  }

  private handleLevelComplete(): void {
    // Guards against handleLevelComplete() ever running its body twice
    // (e.g. a stray re-entrant call) - without this, a second call would
    // re-run ProgressStorage/overlay/submission work a second time. The
    // actual server submission is separately made safe against duplicates
    // by RunTracker.buildSubmission()'s own idempotent run_id caching, but
    // there's no reason to let local state (overlay, progress) double-run
    // either.
    if (this.isLevelComplete) return;
    this.isLevelComplete = true;
    this.hideRealTimeEffectVisuals();
    this.feedback.onLevelComplete();
    const snapshot = this.levelGame.getSnapshot();

    // A STARFRUIT gate completion never earns Pace Bonus, and never implies
    // the real target length was reached - see the spec's UI section.
    // STARFRUIT is disabled outright in Daily V1 (see `convert.ts`), so
    // `isStarfruitGate` is always false there - kept as the same
    // `completionReason` check either way, never a mode-specific branch.
    const isStarfruitGate = snapshot.completionReason === "starfruit_gate";
    const activeDurationMs = this.runTracker.getActiveDurationMs();
    const paceBonus = isStarfruitGate ? 0 : computePaceBonus(paceConfigForLevel(this.level), activeDurationMs);
    const biteScore = snapshot.score;
    const skillScore = snapshot.skillScore;

    if (this.dailyContext) {
      // Daily runs never touch ProgressStorage/ScoreV2Storage (campaign
      // stars/best scores/unlocks) or the campaign overlay/leaderboard -
      // see the PR description's "Campaign and Daily state must remain
      // completely separate" rule. Daily has no star/best-score concept of
      // its own, so its completion plays the L3 core alone.
      this.audio.onLevelCompleteCore();
      this.finishDailyAttempt(null);
      return;
    }

    // Score & Stars V2: the single authoritative computation of this run's
    // Final Score and earned stars - both the real and QA-only paths below
    // feed the exact same result to the overlay/persistence/submission, so
    // none of them can ever disagree about what this run scored. See
    // `computeCampaignCompletionResult()`'s own doc.
    const { finalScore, stars: earnedStars } = computeCampaignCompletionResult(this.level, { biteScore, skillScore, paceBonus });

    // A QA-mode run must never write progress (best score/stars/
    // completion/unlocks) and must never be submitted anywhere - see the
    // `qaMode` field doc. `computeQaOnlyOverlayOutcome()` still reads
    // existing saved progress (`this.previousLevelStats`, captured at
    // level start) but never writes.
    const outcome = shouldPersistRun(this.qaMode)
      ? ProgressStorage.recordLevelResult(this.level, { biteScore, stars: earnedStars })
      : computeQaOnlyOverlayOutcome({ biteScore, stars: earnedStars }, this.previousLevelStats);

    // Scoring V2's own, entirely separate best-score record - "NEW HIGH
    // SCORE" here only ever compares Final Score V2 against a previous
    // Final Score V2, never against ProgressStorage's legacy V1 number.
    const v2Outcome = shouldPersistRun(this.qaMode)
      ? ScoreV2Storage.recordFinalScore(this.level, finalScore)
      : { isNewHighScore: finalScore > ScoreV2Storage.getBestFinalScore(this.level) };
    const bestFinalScore = ScoreV2Storage.hasBestFinalScore(this.level) ? ScoreV2Storage.getBestFinalScore(this.level) : null;

    this.audio.onLevelComplete(outcome.isNewStarRecord, v2Outcome.isNewHighScore);

    this.populateLevelCompleteOverlay({
      biteScore,
      skillScore,
      paceBonus,
      finalScore,
      activeDurationMs,
      moveCount: snapshot.moveCount,
      maneuverCounts: snapshot.maneuverCounts,
      doubleDangerCount: snapshot.doubleDangerCount,
      bestFlowStreak: snapshot.bestFlowStreak,
      trickBiteCount: snapshot.trickBiteCount,
      clutchBiteCount: snapshot.clutchBiteCount,
      stars: outcome.stars,
      isNewHighScore: v2Outcome.isNewHighScore,
      isNewStarRecord: outcome.isNewStarRecord,
      bestFinalScore,
      isStarfruitGate,
      newlyUnlockedWorldName: outcome.newlyUnlockedWorld?.name,
      // See `LevelCompleteOverlayResult.isFirstEverCampaignCompletion`'s
      // doc - reuses `newlyCompletedWorld`, already computed by
      // `ProgressStorage.recordLevelResult()` for every world's own
      // unlock-banner logic; `WorldRegistry.nextOf(...)` undefined means
      // the just-completed world has no successor, i.e. this really was
      // the whole Campaign, not just one world.
      isFirstEverCampaignCompletion: outcome.newlyCompletedWorld !== undefined && !WorldRegistry.nextOf(outcome.newlyCompletedWorld.id),
    });
    this.levelCompleteOverlay.setVisible(true);
    this.levelCompleteOverlay.setAlpha(0);
    this.tweens.add({ targets: this.levelCompleteOverlay, alpha: 1, duration: 200 });

    if (!shouldPersistRun(this.qaMode)) return; // no submission, no name prompt - see above.

    // `finalLength` (never `length`) - `length` has already dropped to 0
    // by this point, since the automatic exit walk has just finished
    // shifting every segment off the board. `finalLength` is the length
    // that actually reached the gate, captured once by the engine itself
    // (see LevelGame.checkGateOpen()) and never touched by the exit walk -
    // it's always >= this.effectiveTargetLength (PR34: the platform's
    // effective gate target), which is what the server requires a completed
    // run's final length to satisfy.
    const submission = this.runTracker.buildSubmission({
      status: "completed",
      score: finalScore,
      biteScore,
      skillScore,
      paceBonus,
      stars: outcome.stars,
      finalLength: snapshot.finalLength ?? this.effectiveTargetLength,
      moveCount: snapshot.moveCount,
      maneuverCounts: snapshot.maneuverCounts,
      doubleDangerCount: snapshot.doubleDangerCount,
      bestFlowStreak: snapshot.bestFlowStreak,
      trickBiteCount: snapshot.trickBiteCount,
      clutchBiteCount: snapshot.clutchBiteCount,
      deathReason: null,
      // Straight from the engine's own snapshot - which of the two gate
      // paths (real target length vs. STARFRUIT's temporary gate)
      // actually earned this completion. Falls back to length_target only
      // in the defensive case where the engine somehow never captured it.
      completionReason: snapshot.completionReason ?? "length_target",
    });

    const bites = this.runTracker.getBiteCounts();
    this.runFinishedGuard.emitOnce(() =>
      trackRunFinished({
        runId: this.runTracker.runId,
        worldId: this.level.worldId,
        levelId: this.level.id,
        gameMode: this.gameMode,
        platform: this.platform,
        daily: this.dailyAnalyticsContext(),
        runSource: this.analyticsRunSource(this.runSourceValue),
        outcome: "completed",
        score: submission.score,
        finalLength: submission.finalLength,
        elapsedMs: this.runTracker.getElapsedMs(),
        activePlayMs: this.runTracker.getActiveDurationMs(),
        steps: submission.moveCount,
        starsEarned: outcome.stars,
        newBestScore: Math.max(this.previousLevelStats.highScore, snapshot.score),
        newBestStars: outcome.stars,
        bestScoreImproved: outcome.isNewLegacyHighScore,
        bestStarsImproved: outcome.isNewStarRecord,
        safeBites: bites.safe,
        rushBites: bites.rush,
        chaosBites: bites.chaos,
        maxRushMultiplier: bites.maxRushStreak,
      }),
    );

    void this.submitRunAndMaybePromptName(submission);
  }

  /**
   * The single Daily Challenge completion path - called from both
   * `handleGameOver()` (death - `deathReason` is one of
   * "wall"/"obstacle"/"self") and `handleLevelComplete()` (a V1 date's
   * gate reached - `deathReason: null`; unreachable for a V2 survival
   * run, which has no gate at all - see `LevelGame`'s `objective` guard).
   * Both report `status: "completed"` on the wire - death is the
   * expected, valid, ranked end of a survival attempt, never a
   * "failure"/DNF (see `DailyAttemptOutcome.status`'s doc); only an
   * abandoned run (restart/menu exit, never reaching either of these two
   * paths at all) reports `"incomplete"` - see
   * `submitDailyDnfOnTeardownIfNeeded()`. Computes the same BITE/SKILL/
   * PACE breakdown a campaign run does (Scoring V2's formula is shared -
   * see `handleLevelComplete()` above; Pace Bonus/`finalLength` still
   * only apply to an actual gate-completion, never to death, exactly as
   * before this status rename), tracks `run_finished` with
   * `game_mode: "daily"`, and hands off to `DailyResultScene`, which owns
   * the actual network submission (`DailyChallengeClient.submitResult()`) -
   * this scene's job ends the moment the attempt is over. Never writes
   * `ProgressStorage`/`ScoreV2Storage` and never calls the campaign
   * `LeaderboardService` - see the "Campaign and Daily state must remain
   * completely separate" rule.
   */
  private finishDailyAttempt(deathReason: "wall" | "obstacle" | "self" | null): void {
    if (this.dailyAttemptFinished || !this.dailyContext) return;
    this.dailyAttemptFinished = true;
    const isDeath = deathReason !== null;

    const snapshot = this.levelGame.getSnapshot();
    const activeDurationMs = this.runTracker.getActiveDurationMs();
    // Death never earns Pace Bonus - it's only ever awarded for an actual
    // (V1-only) gate completion, the same rule a failed campaign run
    // follows (see `handleGameOver()` above).
    const paceBonus = isDeath ? 0 : computePaceBonus(paceConfigForLevel(this.level), activeDurationMs);
    const biteScore = snapshot.score;
    const skillScore = snapshot.skillScore;
    const finalScore = biteScore + skillScore + paceBonus;
    const finalLength = isDeath ? snapshot.length : (snapshot.finalLength ?? this.effectiveTargetLength);

    const bites = this.runTracker.getBiteCounts();
    this.runFinishedGuard.emitOnce(() =>
      trackRunFinished({
        runId: this.runTracker.runId,
        worldId: this.level.worldId,
        levelId: this.level.id,
        gameMode: this.gameMode,
        platform: this.platform,
        daily: this.dailyAnalyticsContext(),
        runSource: this.analyticsRunSource(this.runSourceValue),
        outcome: isDeath ? "death" : "completed",
        ...(deathReason ? { deathReason } : {}),
        score: finalScore,
        finalLength,
        elapsedMs: this.runTracker.getElapsedMs(),
        activePlayMs: activeDurationMs,
        steps: snapshot.moveCount,
        newBestScore: this.previousLevelStats.highScore,
        newBestStars: this.previousLevelStats.stars,
        bestScoreImproved: false,
        bestStarsImproved: false,
        safeBites: bites.safe,
        rushBites: bites.rush,
        chaosBites: bites.chaos,
        maxRushMultiplier: bites.maxRushStreak,
      }),
    );

    const spec = this.dailyContext.spec;
    const dailyPoints = spec.formatVersion !== 1 ? computeDailyPoints(spec.scoringProfile, { finalLength, activePlayMs: activeDurationMs, score: finalScore }) : null;

    const outcome: DailyAttemptOutcome = {
      status: "completed",
      deathReason,
      score: finalScore,
      biteScore,
      skillScore,
      paceBonus,
      finalLength,
      activeDurationMs,
      steps: snapshot.moveCount,
      dailyPoints,
    };
    const resultData: DailyResultSceneData = {
      spec: this.dailyContext.spec,
      attemptNumber: this.dailyContext.attemptNumber,
      attemptsRemaining: this.dailyContext.attemptsRemaining,
      outcome,
      isQaPreview: this.dailyContext.isQaPreview,
      sourceChallenge: this.dailyContext.sourceChallenge,
      platform: this.platform,
    };
    this.scene.start("DailyResultScene", resultData);
  }

  /**
   * ARENA CLEARED - Endless Growth V1's exceptional completion, driven by
   * `LevelGame`'s own `"arena-cleared"` real-time event
   * (`level.completeWhenNoBitesCanSpawn`), never Campaign's `"level-complete"`
   * path (no gate ever opens under `objective: "survival"`). Reuses the
   * existing completion/award audio+visual vocabulary (`onLevelComplete()`
   * on both `feedback`/`audio`, the same pair Daily's own completion
   * reuses) rather than inventing a new cue - see the PR description's
   * "reuse, never duplicate" rule.
   */
  private handleArenaCleared(): void {
    if (!this.endlessContext) return; // structurally unreachable for Campaign/Daily - see the doc above.
    this.isEndlessRunOver = true;
    this.hideRealTimeEffectVisuals();
    this.feedback.onLevelComplete();
    this.audio.onLevelCompleteCore();
    this.finishEndlessRun("arena_cleared", null);
  }

  /**
   * END RUN (Pause overlay, Endless only) - a voluntary ending, never the
   * death cue. Reaching this already required opening Pause, so no extra
   * confirmation dialog. Uses the button's own normal click sound (see
   * `createButton()`) for the UI transition - never `audio.onDeath()`/
   * `onLevelComplete()`.
   */
  private quitEndlessRun(): void {
    if (!this.endlessContext || this.isEndlessRunOver) return;
    this.isEndlessRunOver = true;
    this.hideRealTimeEffectVisuals();
    this.finishEndlessRun("quit", null);
  }

  /**
   * QUIT (Pause overlay, Daily & Campaign) - PR28. A voluntary mid-run
   * abandonment, never a completed/dead result. Reaching this already
   * required opening Pause, so no extra confirmation dialog (same
   * precedent as `quitEndlessRun()`). Deliberately does no reporting of
   * its own - `scene.start()` below fires Phaser's normal scene-shutdown
   * machinery (`events.once("shutdown", ...)` -> `teardown()` ->
   * `trackAbandonedRunIfNeeded()`), the SAME path a browser-back/tab-close
   * abandonment already goes through, which already:
   *  - for Daily, reports the already-consumed attempt as `"incomplete"`
   *    via `submitDailyDnfOnTeardownIfNeeded()` - never a fake completed
   *    score, and never calls `daily-attempt` (no second reservation is
   *    possible - the attempt was spent at reservation time, before this
   *    run even started);
   *  - for Campaign, never calls `ProgressStorage.recordLevelResult()`/
   *    `handleGameOver()`/`handleLevelComplete()`, so no fake
   *    completion/stars/high-score is ever saved;
   *  - for both, emits the existing `run_finished: "menu_exit"` analytics
   *    event exactly once (via `runFinishedGuard`).
   * Navigates to the same existing surface each mode's own normal
   * "back"/"try again" control already uses - `DailyResultScene.tryAgain()`
   * for Daily, the Game Over overlay's "LEVELS" button for Campaign -
   * never a dead end.
   */
  private quitCurrentRun(): void {
    if (!this.isPaused) return;
    this.isPaused = false;
    this.pauseOverlay.setVisible(false);
    if (this.dailyContext) {
      this.scene.start("DailyChallengeScene");
    } else {
      this.scene.start("LevelSelectScene", { worldId: this.level.worldId });
    }
  }

  /**
   * The single Endless Growth V1 completion path - called from
   * `handleGameOver()` (death), `quitEndlessRun()` (voluntary END RUN),
   * and `handleArenaCleared()` (the exceptional full-arena completion).
   * Records the local personal-best record (`EndlessStorage`, the locked
   * comparator - see that module's own doc), tracks exactly one
   * `run_finished` with `game_mode: "endless"` (via the same
   * `runFinishedGuard` every other mode shares), and hands off to
   * `EndlessResultScene`. Never touches `ProgressStorage`/`ScoreV2Storage`,
   * never calls `computeCampaignCompletionResult()`, and never calls the
   * campaign `LeaderboardService` - see the "Campaign and Endless state
   * must remain completely separate" rule.
   */
  private finishEndlessRun(reason: EndlessEndReason, deathReason: "wall" | "obstacle" | "self" | null): void {
    if (this.endlessRunFinished || !this.endlessContext) return;
    this.endlessRunFinished = true;

    const snapshot = this.levelGame.getSnapshot();
    const survivalTimeMs = this.runTracker.getActiveDurationMs();
    const bites = this.runTracker.getBiteCounts();
    // The exact same "Score" the in-run HUD already displays
    // (finalScoreSoFar = biteScore + skillScore) - never a separate
    // Endless-only scoring formula.
    const candidate: EndlessBestRecord = { length: snapshot.length, score: snapshot.finalScoreSoFar, survivalTimeMs };
    const { isNewBest } = EndlessStorage.recordRun(candidate);
    const fastestStepMs = this.endlessFastestStepMs ?? snapshot.stepMs;

    this.runFinishedGuard.emitOnce(() =>
      trackRunFinished({
        runId: this.runTracker.runId,
        worldId: this.level.worldId,
        levelId: this.level.id,
        gameMode: this.gameMode,
        platform: this.platform,
        runSource: this.analyticsRunSource(this.runSourceValue),
        outcome: reason,
        ...(deathReason ? { deathReason } : {}),
        score: candidate.score,
        finalLength: candidate.length,
        elapsedMs: this.runTracker.getElapsedMs(),
        activePlayMs: survivalTimeMs,
        steps: snapshot.moveCount,
        // Endless has no star/high-score concept of its own - these four
        // fields are Campaign's; its own "was this a new record" signal is
        // the dedicated `endlessNewBest` field below.
        newBestScore: 0,
        newBestStars: 0,
        bestScoreImproved: false,
        bestStarsImproved: false,
        safeBites: bites.safe,
        rushBites: bites.rush,
        chaosBites: bites.chaos,
        maxRushMultiplier: bites.maxRushStreak,
        fastestStepMs,
        endlessNewBest: isNewBest,
      }),
    );

    const outcome: EndlessRunOutcome = {
      reason,
      deathReason,
      length: candidate.length,
      score: candidate.score,
      survivalTimeMs,
      maxRushCombo: bites.maxRushStreak,
      safeCount: bites.safe,
      rushCount: bites.rush,
      chaosCount: bites.chaos,
      fastestStepMs,
      isNewBest,
    };

    // Local-first, always: the local personal best (above) and the result
    // screen data are both already finalized before this point. Building
    // this run's submission metrics here (every valid terminal run, not
    // only a new local best - see `EndlessLeaderboardService`'s own doc)
    // is as far as GameScene goes: actually attempting the submission -
    // including, for a first-time player, waiting on the Online Features
    // prompt and a display name - is `EndlessResultScene`'s job (see
    // `attemptEndlessSubmission()`), because that consent/name setup can
    // only happen AFTER this method has already returned and handed off
    // to that screen. Handing over the metrics themselves (rather than
    // firing the submission here and discarding it when Online Features
    // happen to be off at this exact instant) is what lets a later ENABLE
    // click on that screen still submit the exact run that just ended.
    const metrics = buildEndlessSubmissionMetrics({
      runId: this.runTracker.runId,
      snapshot,
      aggregateCounters: this.runTracker.getEndlessAggregateCounters(),
      safeCount: bites.safe,
      rushCount: bites.rush,
      chaosCount: bites.chaos,
      maxRushCombo: bites.maxRushStreak,
      endReason: reason,
      deathReason,
      survivalTimeMs,
      fastestStepMs,
      platform: this.platform,
    });
    // PR34: kept on this device right now, synchronously, whenever Online
    // Features are off - never sent from here, but no longer lost if the
    // player presses PLAY AGAIN/MENU or refreshes before opting in (see
    // `EndlessLeaderboardService.captureResult()`). A no-op when opted in:
    // `EndlessResultScene` submits it through the normal queue path.
    endlessLeaderboardService.captureResult(metrics);

    const resultData: EndlessResultSceneData = { outcome, metrics, ...(this.entrySource ? { entrySource: this.entrySource } : {}) };
    this.scene.start("EndlessResultScene", resultData);
  }

  /**
   * Submits the completed run, then - only if it's never asked before and
   * this run actually landed in the level's Top 10 - opens the "NEW HIGH
   * SCORE!" name prompt. Entirely best-effort: any offline/error state
   * from the service just skips the prompt silently, never blocks the
   * level-complete screen the player is already looking at.
   *
   * The rank/name-prompt check only runs after `submitRun()` reports
   * `"sent"` - a confirmed, this-call synchronization. `"queued"` means
   * the run is still only local (offline, or a transient failure) - the
   * server's level_scores hasn't actually been updated yet, so checking
   * rank now could only ever see stale (or no) data; asking "did I make
   * Top 10" before the run has even reached the server would be asking
   * the wrong question, never a true one. It stays queued and gets
   * retried later (see RunQueue/flushQueue) - without a prompt this time.
   */
  private async submitRunAndMaybePromptName(submission: ReturnType<RunTracker["buildSubmission"]>): Promise<void> {
    const service = getLeaderboardService();
    const outcome = await service.submitRun(submission);
    // Avoids two wasted network calls when the run didn't actually sync
    // this call - decideNamePrompt() would reject this case too, but
    // there's no reason to fetch rank/name data we already know we won't use.
    if (outcome !== "sent") return;

    // PR34: the rank on THIS run's own platform board - the board it was just submitted to.
    const [rankResult, nameResult] = await Promise.all([service.getMyLevelRank(submission.levelId, this.platform), service.getMyDisplayName()]);
    const decision = decideNamePrompt(outcome, rankResult, nameResult);
    if (!decision.shouldPrompt || decision.rank === null) return;
    if (!this.isLevelComplete) return;

    this.nameModal.open({ headline: "NEW HIGH SCORE!", contextLine: `YOU'RE #${decision.rank} ON ${this.level.name.toUpperCase()}` });
  }

  private async handleSaveName(name: string): Promise<{ ok: boolean; error?: string }> {
    const result = await getLeaderboardService().setDisplayName(name);
    if (result.state !== "ok") {
      return { ok: false, error: result.state === "error" ? result.message : "Could not save name right now." };
    }
    return { ok: true };
  }

  // ---------------------------------------------------------------------
  // Layout
  // ---------------------------------------------------------------------

  private onResize(): void {
    // PR31: re-evaluate the orientation gate on every resize/orientation-
    // change event - `Scale.RESIZE` fires one for an actual device
    // rotation exactly like it does for a plain window resize, so this is
    // the one hook that catches BOTH a live rotation during an active run
    // AND a desktop window merely being resized. `OrientationGate.ts` also
    // wires its own internal `scale.on("resize", ...)` listener, so this
    // call is redundant with that one in practice - kept anyway since it's
    // free (idempotent) and keeps this Scene's own resize handling visibly
    // complete at this call site.
    this.orientationGate.check();
    this.recomputeLayout();
    this.renderBoard();
    this.renderObstacles();
    this.renderPortals();
    this.renderBlackHoles();
    this.renderGate();
    this.renderSnakeBites();
    // PR32: the snake's PIXEL position depends on `this.layout` (just
    // recomputed above) even though its logical grid position hasn't
    // changed - re-render at the current interpolation progress against
    // the new layout, rather than leaving a stale pre-resize position on
    // screen until the next tick/frame.
    this.renderSnakePosition();
    this.layoutHud();
    this.layoutOverlays();
  }

  /** New player goal clarity: Endless reserves extra top margin (see `ENDLESS_OBJECTIVE_TOP_RESERVED_EXTRA`'s own doc) so the board itself starts lower, leaving room for `endlessObjectiveText` above it - `TOP_RESERVED` unchanged for every other mode. */
  private effectiveTopReserved(): number {
    return this.endlessContext ? TOP_RESERVED + ENDLESS_OBJECTIVE_TOP_RESERVED_EXTRA : TOP_RESERVED;
  }

  /**
   * PR31 (corrected: portrait-only mobile gameplay). Every viewport -
   * desktop or phone - uses the exact same top-reserved-HUD-strip board
   * layout (`computeInsetBoardLayout()`, which is `computeBoardLayout()`
   * plus safe-area-inset awareness). `readSafeAreaInsets()` already
   * returns all-zero on desktop (no `env()` obstruction to avoid there),
   * so there is no `isPhoneLikeViewport()` branch here at all - a mobile-
   * specific side-column layout existed in an earlier landscape-only
   * revision of this PR and was reverted along with the orientation
   * direction itself; mobile gameplay is portrait-only now, so it needs
   * no gameplay layout of its own beyond safe-area handling.
   */
  private recomputeLayout(): void {
    const insets = readSafeAreaInsets();
    const topReserved = this.effectiveTopReserved();
    this.layout = computeInsetBoardLayout(
      this.scale.width,
      this.scale.height,
      this.levelGame.gridCols,
      this.levelGame.gridRows,
      insets,
      topReserved,
      BOTTOM_RESERVED,
      SIDE_PADDING,
    );

    this.layoutHud();
    // Centered on the BOARD's own true horizontal center, not the full
    // screen width - with asymmetric safe-area insets (left != right) the
    // board is no longer exactly centered in the raw viewport, so
    // headline/skill/status lanes centered on the raw screen width could
    // read as slightly off-center relative to the board they're
    // commenting on. `VisualFeedbackController.setLayout()`'s own math is
    // `width / 2`, unchanged here - passing `2 * boardCenterX` redirects
    // that existing math to the board's center without needing to touch
    // that class at all. With symmetric/zero insets (every desktop, and
    // most phones) this is arithmetically identical to `this.scale.width`.
    const boardCenterX = this.layout.originX + this.layout.boardWidth / 2;
    this.feedback.setLayout(boardCenterX * 2, topReserved, this.layout);
  }

  /**
   * PR31 (corrected: portrait-only mobile gameplay). The same top-strip
   * HUD positions used before the (reverted) landscape-only side-column
   * HUD existed, now offset by the live safe-area insets - all-zero on
   * desktop, so desktop's own positions are unaffected; on a phone, the
   * top band shifts down/in to clear a notch/Dynamic Island/rounded
   * corner, and the two side-anchored groups (score/sub-line on the
   * left, pause/help on the right) shift in from the left/right insets
   * respectively, without hardcoding any specific device's geometry.
   */
  private layoutHud(): void {
    const insets = readSafeAreaInsets();
    const left = SIDE_PADDING + insets.left;
    const right = this.scale.width - SIDE_PADDING - insets.right;
    const topBandCenterY = insets.top + TOP_RESERVED / 2;

    this.scoreText.setPosition(left, topBandCenterY - 12);
    const subLineY = topBandCenterY + 12;
    this.worldIconGraphics.setPosition(left + 7, subLineY);
    this.subLineText.setPosition(left + 18, subLineY);
    // Centered in the extra strip Endless alone reserves above the board
    // (see effectiveTopReserved()) - never inside the base TOP_RESERVED
    // HUD row every mode shares, so it can never cover the score/sub-line,
    // pause/help buttons, or (on Endless) the TRICK BITE indicator.
    this.endlessObjectiveText.setPosition(this.scale.width / 2, insets.top + TOP_RESERVED + 6);
    this.speedTrialRewardText.setPosition(this.scale.width / 2, insets.top + TOP_RESERVED + 124);
    this.pauseButton.setPosition(right - PAUSE_BUTTON_SIZE / 2, topBandCenterY);
    this.helpButton.setPosition(right - PAUSE_BUTTON_SIZE - HELP_BUTTON_GAP - HELP_BUTTON_SIZE / 2, topBandCenterY);
    this.qaModeText.setPosition(this.scale.width / 2, insets.top + 4);
    // Below the qaModeText indicator, above the board - clear of the
    // feedback controller's own headline/skill lanes and never over the
    // bottom-of-screen mobile controls.
    this.trickBiteWindowText.setPosition(this.scale.width / 2, insets.top + TOP_RESERVED - 18);
    // Top-left, well clear of the bottom-of-screen mobile touch controls.
    this.qaScoringOverlayText?.setPosition(6 + insets.left, insets.top + TOP_RESERVED + 6);
    this.qaSpeedOverlayText?.setPosition(6 + insets.left, insets.top + TOP_RESERVED + 6);
    // Below the QA button grid (buildChaosQaControls()/buildEndlessQaControls()
    // together can span several rows) rather than under it, well clear of the
    // mobile touch controls at the bottom of the screen.
    this.qaEndlessDebugText?.setPosition(6 + insets.left, insets.top + TOP_RESERVED + 240);
  }

  private layoutOverlays(): void {
    this.resizeOverlayBackground(this.pauseOverlay);
    this.resizeOverlayBackground(this.gameOverOverlay);
    this.resizeOverlayBackground(this.levelCompleteOverlay);
    this.resizeOverlayBackground(this.introOverlay);
    this.centerOverlayContent(this.pauseOverlay);
    this.centerOverlayContent(this.gameOverOverlay);
    this.centerOverlayContent(this.levelCompleteOverlay);
    this.centerOverlayContent(this.introOverlay);
    this.howToPlayOverlay.relayout();
    this.positionReadyOverlay();
  }

  private resizeOverlayBackground(overlay: Phaser.GameObjects.Container): void {
    const bg = overlay.getData("bg") as Phaser.GameObjects.Rectangle | undefined;
    bg?.setSize(this.scale.width, this.scale.height);
    bg?.setPosition(this.scale.width / 2, this.scale.height / 2);
  }

  private centerOverlayContent(overlay: Phaser.GameObjects.Container): void {
    const panel = overlay.getData("panel") as Phaser.GameObjects.Container | undefined;
    panel?.setPosition(this.scale.width / 2, this.scale.height / 2);
  }

  /** The static SPEED TRIAL reward line's own visibility - the live countdown/warning/activation/exit treatment itself is `feedback`'s status lane (`VisualFeedbackController.update()`), driven straight from the same authoritative `speedTrialRemainingMs`, so the two can never disagree about whether a trial is active. */
  private updateSpeedTrialCountdown(): void {
    this.speedTrialRewardText.setVisible(this.levelGame.getSnapshot().speedTrialRemainingMs !== null);
  }

  /** Hides every real-time indicator tied to an active effect that must disappear outright (not simply freeze) - EXITING, Game Over, Level Complete. `feedback`'s own status badges/overlays are cleared by its own `onGateEntry()`/`onDeath()`/`onLevelComplete()` calls; this only covers what GameScene still owns directly. */
  private hideRealTimeEffectVisuals(): void {
    this.speedTrialRewardText.setVisible(false);
    this.trickBiteWindowText.setVisible(false);
  }

  private updateHud(): void {
    const snapshot = this.levelGame.getSnapshot();
    // The scoring combo's own uncapped counter (rushComboStreak) and its
    // capped display (rushStack, capped at 5) diverge once the CURRENT
    // combo run passes x5 - "MAX" flags that. `speedBoostPercent` is the
    // TOTAL composed speed boost (growth + RUSH debt + persistent mode +
    // temporary), not just RUSH's own contribution - see PR11 (Speed
    // Engine V2)'s `LevelGameSnapshot` doc.
    const rushBadge =
      snapshot.rushStack > 0
        ? `   RUSH x${snapshot.rushStack}${snapshot.rushComboStreak > snapshot.rushStack ? " MAX" : ""} • SPEED +${snapshot.speedBoostPercent}%`
        : "";
    // Scoring V2: the main display is "Final Score so far" (biteScore +
    // skillScore) - Pace Bonus is only ever added once, at completion.
    this.scoreText.setText(`SCORE ${snapshot.finalScoreSoFar}${rushBadge}`);

    if (this.dailyContext) {
      // Daily has no star/campaign-progression concept - the sub-line
      // instead shows the live attempt/rule context the spec calls for
      // ("During the run show: DAILY, Attempt X/3, the current special
      // rule, such as RUSH ONLY"). A QA preview never has a real attempt
      // number to show - "Attempt X/3" would imply a ranked attempt is
      // being spent, which is exactly what this run is not.
      const ruleLabel = this.dailyContext.spec.biteMode === "standard" ? "" : ` · ${DAILY_BITE_MODE_HUD_LABEL[this.dailyContext.spec.biteMode]}`;
      const attemptLabel = this.dailyContext.isQaPreview ? "QA PREVIEW" : `Attempt ${this.dailyContext.attemptNumber}/3`;
      // Survival (V2) has no target length to show a fraction against - the
      // HUD instead shows the live, pause-aware survival time and current
      // Daily Points (both freely computable client-side from already-
      // tracked values; the server independently recomputes the final
      // dailyPoints at submission, this is purely a live display). A V1
      // (legacy) Daily date keeps the original "Len X/target" HUD, since it
      // still plays under campaign gate/target-length rules.
      // Read-only "TARGET: X" tag for a challenge-sourced attempt (PR10) -
      // never a live/competing scoring model of its own, purely a label
      // echoing the frozen target the friend link carried; the actual
      // win/tie/loss comparison only ever happens post-run, against the
      // server-verified result (`DailyResultScene`).
      const targetLabel = this.dailyContext.sourceChallenge ? ` · TARGET: ${this.dailyContext.sourceChallenge.targetDailyPoints.toLocaleString("en-US")}` : "";

      const spec = this.dailyContext.spec;
      if (spec.formatVersion !== 1) {
        const activeSeconds = Math.floor(this.runTracker.getActiveDurationMs() / 1000);
        const livePoints = computeDailyPoints(spec.scoringProfile, {
          finalLength: snapshot.length,
          activePlayMs: this.runTracker.getActiveDurationMs(),
          score: snapshot.finalScoreSoFar,
        });
        this.subLineText.setText(`DAILY · ${attemptLabel}${ruleLabel} · Len ${snapshot.length} · ${activeSeconds}s · ${livePoints} pts${targetLabel}`);
      } else {
        this.subLineText.setText(`DAILY · ${attemptLabel}${ruleLabel} · Len ${snapshot.length}/${snapshot.targetLength}${targetLabel}`);
      }
      return;
    }

    if (this.endlessContext) {
      // Endless HUD: LENGTH, BEST, and TIME - never target length, gate
      // state, stars, next-star threshold, or anything Daily/Campaign-only
      // (see the PR description's Endless HUD section). BEST shows the
      // stored record (read once at run start, never re-read mid-run)
      // until the current run's Length actually exceeds it, at which
      // point the live current Length IS the (not-yet-persisted) best -
      // persistence itself only happens once the run reaches a real
      // terminal result, in `finishEndlessRun()`.
      const displayedBest = computeDisplayedBestLength(this.endlessStoredBestLength, snapshot.length);
      const timeLabel = formatSurvivalTime(this.runTracker.getActiveDurationMs());
      this.subLineText.setText(`LENGTH ${snapshot.length} · BEST ${displayedBest} · TIME ${timeLabel}`);
      return;
    }

    // Score & Stars V2: "next star" compares the same live displayed score
    // (finalScoreSoFar = biteScore + skillScore) the big SCORE readout
    // above already shows - never Bite Score alone. Completion-only Pace
    // Bonus isn't reflected here (it doesn't exist until the run actually
    // ends), so an in-progress run's true final tier can still land one
    // tier higher than this live readout suggests - that's intended: Pace
    // Bonus is allowed to push a player across a threshold only at
    // completion.
    const thresholds = getStarThresholds(this.level);
    let nextStar = `Next ★: ${thresholds.star2}`;
    if (snapshot.finalScoreSoFar >= thresholds.star3) nextStar = "★★★ MAX";
    else if (snapshot.finalScoreSoFar >= thresholds.star2) nextStar = `Next ★: ${thresholds.star3}`;

    this.subLineText.setText(`${this.level.name} · Len ${snapshot.length}/${snapshot.targetLength} · ${nextStar}`);
  }

  // ---------------------------------------------------------------------
  // Rendering: board / obstacles / gate / snake / bites
  // ---------------------------------------------------------------------

  private cellToPixel(cell: Point): { x: number; y: number } {
    return {
      x: this.layout.originX + cell.x * this.layout.cellSize,
      y: this.layout.originY + cell.y * this.layout.cellSize,
    };
  }

  private renderBoard(): void {
    const g = this.boardGraphics;
    g.clear();

    g.fillStyle(GameConfig.colors.boardBackground, 1);
    g.fillRoundedRect(this.layout.originX, this.layout.originY, this.layout.boardWidth, this.layout.boardHeight, 12);

    // PR31 (visual polish - world identity inside gameplay): the same
    // restrained backdrop decoration (a faint star/arc scatter, per-world
    // - see `worldGraphics.ts`'s own doc) World Select already draws
    // behind its cards, reused here confined to the board's own bounds so
    // the five worlds read as subtly different while actually playing,
    // not only on the menu screens. Drawn BEFORE the grid lines below (on
    // the same persistent Graphics layer) so the grid stays crisply on
    // top - the decoration never competes with it, and its own alpha
    // values (already tuned low for a menu background) read as even more
    // restrained against gameplay's saturated snake/fruit/obstacle
    // colors. Endless has no Campaign world (see the frame-color comment
    // below) - skipped entirely there rather than substituting a
    // generic backdrop, since Endless's identity is deliberately "no
    // world", not a stand-in one.
    if (!this.endlessContext) {
      drawWorldBackdrop(
        g,
        this.world().theme,
        { x: this.layout.originX, y: this.layout.originY, width: this.layout.boardWidth, height: this.layout.boardHeight },
        worldSeed(this.world().id),
      );
    }

    // PR31 (visual polish - board/grid contrast): raised from 0.6 - the
    // same production "hard to read the arena in bright daylight" finding
    // the accent frame below was already added for. A restrained bump
    // (never full-opacity, never a dominant grid), not a new color.
    g.lineStyle(1, GameConfig.colors.gridLine, 0.75);
    for (let c = 1; c < this.levelGame.gridCols; c++) {
      const x = this.layout.originX + c * this.layout.cellSize;
      g.lineBetween(x, this.layout.originY, x, this.layout.originY + this.layout.boardHeight);
    }
    for (let r = 1; r < this.levelGame.gridRows; r++) {
      const y = this.layout.originY + r * this.layout.cellSize;
      g.lineBetween(this.layout.originX, y, this.layout.originX + this.layout.boardWidth, y);
    }

    // A thin accent frame just outside the board - identifies the world at
    // a glance without touching any cell, the gate, the snake, or an
    // obstacle's own color/contrast. Endless has no Campaign world (never
    // registered in LevelRegistry/WorldRegistry - see
    // buildEndlessLevelDefinition()'s doc) so there's no `theme.accent` to
    // draw from, but production mobile testing found the board otherwise
    // blends into the surrounding black in bright daylight - the same
    // `GameConfig.colors.accent` already used for Endless's own UI
    // elsewhere (e.g. the "YOU" row highlight in `EndlessLeaderboardScene`)
    // stands in for a theme here, drawn with the exact same geometry/weight
    // as Campaign's frame so it reads as the same visual language, not a
    // new one.
    const frameColor = this.endlessContext ? GameConfig.colors.accent : this.world().theme.accent;
    g.lineStyle(2, frameColor, 0.55);
    g.strokeRoundedRect(this.layout.originX - 3, this.layout.originY - 3, this.layout.boardWidth + 6, this.layout.boardHeight + 6, 14);
  }

  private renderObstacles(): void {
    const g = this.obstacleGraphics;
    g.clear();
    const cell = this.layout.cellSize;
    const inset = Math.max(1, cell * 0.05);
    // Visual-language pass (world-specific obstacle identity): fill/border
    // vary per world via the centralized mapping - shape/geometry below is
    // completely unchanged, so obstacles always stay the same recognizable
    // family, just tinted to the current world. Endless has no Campaign
    // world (never registered in LevelRegistry/WorldRegistry - see
    // buildEndlessLevelDefinition()'s doc, and renderBoard()'s own
    // `frameColor` above for the same precedent) so there's no
    // `theme` to draw from there - falls back to the original flat
    // obstacle colors, same "no world identity" choice renderBoard()
    // already makes for Endless's own accent frame. Endless levels never
    // actually have obstacles (`obstacles: []`), but this is still
    // computed unconditionally below (before the loop), so it must never
    // crash calling `this.world()` regardless of whether the loop body
    // would end up running.
    const style = this.endlessContext ? { fill: GameConfig.colors.obstacle, border: GameConfig.colors.obstacleBorder } : obstacleStyleForWorld(this.world().theme);

    for (const obstacle of this.levelGame.obstacles) {
      const pixel = this.cellToPixel(obstacle);
      const size = cell - inset * 2;
      const radius = cell * 0.15;
      // PR31 follow-up fix (POLISH 2, world-identity polish, not a
      // mechanic): SUN's restrained "hot/burning" identity - a soft,
      // low-alpha ember halo drawn just OUTSIDE the obstacle's own crisp
      // border, before the fill/border/bevel below (so it sits fully
      // behind them, never obscuring the shape it surrounds). `undefined`
      // for every other world, so this is a complete no-op there - see
      // `ObstacleWorldStyle.glow`'s own doc. Static (never tweened/
      // animated) - no accumulating tweens, no per-frame cost, exactly
      // like every other obstacle stroke here, all drawn once per
      // `renderObstacles()` call (board load/resize), never per frame.
      if (style.glow !== undefined) {
        const glowInset = inset - cell * 0.05;
        g.lineStyle(cell * 0.12, style.glow, 0.22);
        g.strokeRoundedRect(pixel.x + glowInset, pixel.y + glowInset, size + (inset - glowInset) * 2, size + (inset - glowInset) * 2, radius + (inset - glowInset));
      }
      g.fillStyle(style.fill, 1);
      g.fillRoundedRect(pixel.x + inset, pixel.y + inset, size, size, radius);
      g.lineStyle(1.5, style.border, 0.9);
      g.strokeRoundedRect(pixel.x + inset, pixel.y + inset, size, size, radius);
      // PR31 (visual polish - obstacle contour/depth): a second, fainter
      // inset stroke - reads as a subtle bevel/depth cue on an otherwise
      // completely flat rectangle, without any lighting/shading system.
      // POLISH 2: SUN uses `hotContour` (a brighter, hotter tone) here
      // instead of the same faint `border` tone every other world keeps -
      // reads as a glowing inner edge rather than a plain depth cue.
      const innerInset = Math.max(1, cell * 0.08);
      const bevelColor = style.hotContour ?? style.border;
      g.lineStyle(1, bevelColor, style.hotContour !== undefined ? 0.55 : 0.35);
      g.strokeRoundedRect(
        pixel.x + inset + innerInset,
        pixel.y + inset + innerInset,
        Math.max(1, size - innerInset * 2),
        Math.max(1, size - innerInset * 2),
        Math.max(1, radius - innerInset * 0.5),
      );
    }
  }

  /**
   * Portals V1 (Saturn World): persistent markers for every authored
   * portal definition (a fixed `PortalPair` or, since the JUNCTION
   * gameplay-QA pass, a random `PortalNetwork`) - drawn once per level
   * load/resize, same "draw once and leave it" pattern as
   * `renderObstacles()`. Every endpoint of one definition shares a color
   * (cycling through `GameConfig.colors.portalPairs` by definition index)
   * so its identity reads as one group at a glance, and is never the same
   * palette as WORMHOLE's own cyan/violet - the two systems must never
   * look alike. A no-op (nothing drawn) for every level without `portals`
   * - Moon/Earth/Mars/Daily/Endless are unaffected.
   */
  private renderPortals(): void {
    const g = this.portalGraphics;
    g.clear();
    const cell = this.layout.cellSize;
    const palette = GameConfig.colors.portalPairs;

    this.levelGame.portals.forEach((def, index) => {
      const color = palette[index % palette.length];
      for (const endpoint of portalDefinitionEndpoints(def)) {
        const pixel = this.cellToPixel(endpoint);
        const cx = pixel.x + cell / 2;
        const cy = pixel.y + cell / 2;
        g.lineStyle(Math.max(1.5, cell * 0.07), color, 0.9);
        g.strokeCircle(cx, cy, cell * 0.36);
        g.fillStyle(color, 0.22);
        g.fillCircle(cx, cy, cell * 0.36);
        g.lineStyle(Math.max(1, cell * 0.04), color, 0.6);
        g.strokeCircle(cx, cy, cell * 0.22);
      }
    });
  }

  /**
   * BLACK HOLE V1 (Saturn World): a near-black core with a hot-amber
   * accretion-ring glow - drawn once per level load/resize, same
   * "draw once and leave it" pattern as `renderObstacles()`/`renderPortals()`.
   * A no-op (nothing drawn) for every level without `blackHoles`.
   *
   * PR27 (visual clarity only - collision/mechanics untouched): two
   * changes from the original V1 rendering. First, the old inner ring
   * (a second, fainter circle at `cell*0.3`) is replaced by a square
   * frame matched to the ACTUAL lethal cell bounds - collision is a whole
   * grid cell (see `LevelGame`'s `isBlackHole` check), not just the area
   * inside a circle, so the old two-concentric-circles glyph left the
   * cell's own corners with no visual marking at all even though they
   * were just as lethal. A square frame closes that gap AND gives the
   * hazard a silhouette that's clearly not PORTAL's own smooth
   * concentric-circles marker (`renderPortals()`, unchanged) at a glance.
   * Second, that frame pulses gently - a static frame whose ALPHA fades
   * in and out, never moving/shrinking inward, so it never implies a
   * gravity/pull effect the mechanic doesn't have (see
   * `LevelDefinition.blackHoles`'s own doc: "never pulls the snake from
   * nearby cells"). The core + outer ring stay fully opaque and static,
   * so the hazard never reads as fully "off" even at the pulse's dimmest.
   * Third (human QA follow-up), a small swirl motif spins continuously
   * inside the core itself (`createBlackHoleSwirl()`) - contained well
   * within the core's own radius, so it reads as "something is happening
   * inside the event horizon" without touching the cell's edges or
   * implying anything is being pulled toward it.
   */
  private renderBlackHoles(): void {
    const g = this.blackHoleGraphics;
    const pulse = this.blackHolePulseGraphics;
    g.clear();
    pulse.clear();

    // Every swirl is a small per-hole Container (rotation needs its own
    // pivot per hole - unlike the shared layers above, one Graphics
    // object can't spin two different holes' motifs around two different
    // centers). Torn down and rebuilt from scratch on every call so a
    // level load/resize can never leave a stray swirl (or its tween)
    // behind - see also `teardown()`'s own explicit cleanup for shutdown.
    this.tweens.killTweensOf(this.blackHoleSwirls);
    this.blackHoleSwirls.forEach((swirl) => swirl.destroy());
    this.blackHoleSwirls = [];

    const cell = this.layout.cellSize;
    const frameInset = Math.max(1, cell * 0.04);

    for (const hole of this.levelGame.blackHoles) {
      const pixel = this.cellToPixel(hole);
      const cx = pixel.x + cell / 2;
      const cy = pixel.y + cell / 2;

      pulse.lineStyle(Math.max(1.5, cell * 0.05), GameConfig.colors.blackHoleRing, 1);
      pulse.strokeRoundedRect(pixel.x + frameInset, pixel.y + frameInset, cell - frameInset * 2, cell - frameInset * 2, cell * 0.18);

      g.lineStyle(Math.max(1.5, cell * 0.06), GameConfig.colors.blackHoleRing, 0.85);
      g.strokeCircle(cx, cy, cell * 0.42);
      g.fillStyle(GameConfig.colors.blackHoleCore, 1);
      g.fillCircle(cx, cy, cell * 0.26);

      this.blackHoleSwirls.push(this.createBlackHoleSwirl(cx, cy, cell));
    }

    // Guards against stacking a second pulse tween on the same persistent
    // `pulse` object if `renderBlackHoles()` runs again this scene
    // instance (level load, then a resize) - never accumulates.
    this.tweens.killTweensOf(pulse);
    if (this.levelGame.blackHoles.length > 0) {
      pulse.setAlpha(0.85);
      this.tweens.add({
        targets: pulse,
        alpha: 0.35,
        duration: 900,
        yoyo: true,
        repeat: -1,
        ease: "Sine.easeInOut",
      });
    }
  }

  /**
   * A single small swirl - two short arcs, radially opposite each other
   * (a minimal pinwheel), drawn ONCE into a Graphics child and rotated by
   * tweening the parent Container's `rotation` (0 -> 2*PI, repeating) -
   * never a per-frame redraw. 2*PI is visually identical to 0, so each
   * repeat's reset-to-start is seamless, not a visible snap. Kept well
   * inside the core's own radius (`cell*0.26`) so it never reaches the
   * cell edge, never competes with the outer ring/frame, and can never
   * read as reaching toward - let alone affecting - a neighboring cell.
   * Uses the hazard's own amber ring color (never PORTAL's teal/gold or
   * WORMHOLE's cyan/violet), at low alpha, so it reads as energy inside
   * the existing hazard identity rather than a new visual language.
   */
  private createBlackHoleSwirl(cx: number, cy: number, cell: number): Phaser.GameObjects.Container {
    const container = this.add.container(cx, cy);
    const swirl = this.add.graphics();
    const radius = cell * 0.16;
    const arcSpan = Phaser.Math.DegToRad(110);
    swirl.lineStyle(Math.max(1, cell * 0.035), GameConfig.colors.blackHoleRing, 0.5);
    for (const startDeg of [0, 180]) {
      const start = Phaser.Math.DegToRad(startDeg);
      swirl.beginPath();
      swirl.arc(0, 0, radius, start, start + arcSpan, false);
      swirl.strokePath();
    }
    container.add(swirl);

    this.tweens.add({
      targets: container,
      rotation: Math.PI * 2,
      duration: 4200,
      repeat: -1,
      ease: "Linear",
    });

    return container;
  }

  /** Locked and open gates are drawn very differently on purpose - the
   * player must be able to tell at a glance whether the exit can be used. */
  private renderGate(): void {
    const g = this.gateGraphics;
    g.clear();
    // PR31 follow-up fix (round 2): tear down any previous open-gate swirl
    // BEFORE deciding whether to draw a new one below - unconditional,
    // exactly like renderBlackHoles()'s own kill-and-recreate for
    // `blackHoleSwirls` - so a locked<->open transition (or this level
    // having no gate at all, e.g. Daily survival, via the early return
    // right below) can never leave a stray swirl/tween behind.
    if (this.gatePortalSwirl) this.tweens.killTweensOf(this.gatePortalSwirl);
    this.gatePortalSwirl?.destroy();
    this.gatePortalSwirl = null;
    // Survival mode has no gate at all - not locked, not open, not
    // reachable (see LevelGame.checkGateOpen()'s objective === "survival"
    // short-circuit). Drawing nothing here (not even the "locked" look) is
    // what "no gate animation or gate HUD should appear" for Daily
    // survival actually means - a locked-gate sprite sitting on the board
    // for the entire run would itself be a gate indicator.
    if (this.level.objective === "survival") return;
    const cell = this.layout.cellSize;
    const inset = Math.max(1, cell * 0.05);
    const pixel = this.cellToPixel(this.levelGame.exitPosition);
    const snapshot = this.levelGame.getSnapshot();
    const isOpen = snapshot.isGateOpen;
    // A stronger, wider glow while the temporary STARFRUIT gate is the
    // reason it's open - the permanent gate keeps its normal look.
    const isStarfruitGate = snapshot.starfruitGateRemainingMs !== null;
    // Visual-language pass (world-specific gate treatment): this world's
    // own accent color, reused as a faint extra outer ring - restrained on
    // purpose, never replacing the locked/open silhouette's own fixed
    // violet/green meaning (that "locked vs. go" read must stay identical
    // in every world). Skipped while STARFRUIT's own gold/cyan glow is
    // active - that temporary gate already has its own established
    // identity, exactly like WORMHOLE keeps its own cyan regardless of
    // world, and layering a second accent on top of it would just be
    // clutter.
    // Endless never actually reaches this line (its objective is always
    // "survival", caught by the early return above), but guarded the same
    // way as renderObstacles()'s/renderBoard()'s own theme reads anyway -
    // defense in depth, and consistent with the rest of this file, rather
    // than relying on a different method's early return to keep this safe.
    const worldAccent = this.endlessContext ? GameConfig.colors.accent : this.world().theme.accent;

    if (isOpen) {
      const cx = pixel.x + cell / 2;
      const cy = pixel.y + cell / 2;
      const accentColor = isStarfruitGate ? GameConfig.colors.starfruitSparkle : GameConfig.colors.gateOpenGlow;

      // PR31 follow-up fix (round 2: real-device feedback - "still looks
      // like a green tile with a play icon", full redesign). The open
      // gate no longer fills its cell as a solid tile at all - it's now a
      // circular PORTAL glyph (concentric rings + a bright core) floating
      // over the board's own dark background, reinterpreting this file's
      // own black-hole/wormhole "energy glyph" language (`renderBlackHoles()`/
      // `renderPortals()`) as bright and inviting - white + green -
      // instead of dark/strange, and deliberately never WORMHOLE's own
      // cyan/violet, so the two identities never look alike. A thin
      // square OUTLINE (never a fill) at the tile's true bounds - the
      // exact same "circular glyph inside a square hazard cell" problem
      // `renderBlackHoles()`'s own frame already solves the same way (see
      // that method's own doc) - keeps the exact entry/collision cell
      // obvious even though the portal artwork itself reads as circular.
      // Everything below is static per-call math on the SAME already-
      // reused `gateGraphics` object `renderGate()` always redraws on the
      // relevant tick/resize (no new Graphics object, no per-frame
      // allocation), and the existing alpha "breathing" tween
      // (`buildGateGlowConfig()`) already targets this whole Graphics
      // object, so the entire portal pulses together for free.
      g.lineStyle(Math.max(1, cell * 0.035), GameConfig.colors.gateOpen, 0.45);
      g.strokeRoundedRect(pixel.x + inset, pixel.y + inset, cell - inset * 2, cell - inset * 2, cell * 0.18);

      // Soft outer halo - green, low alpha, wider than the rim ring below -
      // "light spilling outward", never a particle system or bloom pass.
      g.fillStyle(GameConfig.colors.gateOpenGlow, 0.12);
      g.fillCircle(cx, cy, cell * 0.46);

      // The portal's own rim.
      g.lineStyle(Math.max(1.5, cell * 0.06), GameConfig.colors.gateOpen, 0.95);
      g.strokeCircle(cx, cy, cell * 0.37);

      // A second, brighter ring just inside - the "layered concentric
      // rings" look - in the world-agnostic open accent, or STARFRUIT's
      // own established gold/cyan while that temporary gate is the reason
      // it's open (never a separate visual language from the permanent
      // gate's own).
      g.lineStyle(Math.max(1, cell * 0.045), accentColor, 0.9);
      g.strokeCircle(cx, cy, cell * 0.27);

      // A bright white-green luminous core - "bright center/opening."
      g.fillStyle(mixColors(GameConfig.colors.gateOpen, 0xffffff, 0.55), 0.95);
      g.fillCircle(cx, cy, cell * 0.18);

      // An innermost hot-white point - "brighter bloom at the center."
      g.fillStyle(0xffffff, 0.9);
      g.fillCircle(cx, cy, cell * 0.08);

      g.lineStyle(1.5, isStarfruitGate ? GameConfig.colors.starfruitGlow : worldAccent, isStarfruitGate ? 0.85 : 0.35);
      g.strokeCircle(cx, cy, cell * 0.43);

      // A single small rotating swirl motif for subtle orbital energy -
      // reuses the EXACT cheap draw-once-then-only-tween-rotation pattern
      // `createBlackHoleSwirl()` already established in this file (see
      // that method's own doc) - never a per-frame redraw, and never
      // accumulates: torn down unconditionally at the top of this method
      // on every call, before this new one is ever created.
      this.gatePortalSwirl = this.createGatePortalSwirl(cx, cy, cell, accentColor);
    } else {
      g.fillStyle(GameConfig.colors.gateLocked, 1);
      g.fillRoundedRect(pixel.x + inset, pixel.y + inset, cell - inset * 2, cell - inset * 2, cell * 0.15);
      g.lineStyle(2, GameConfig.colors.gateLockedBorder, 0.9);
      g.strokeRoundedRect(pixel.x + inset, pixel.y + inset, cell - inset * 2, cell - inset * 2, cell * 0.15);

      // Bars, to read as a locked gate rather than a plain wall block.
      const barCount = 3;
      g.lineStyle(Math.max(1, cell * 0.05), GameConfig.colors.gateLockedBorder, 0.9);
      for (let i = 1; i <= barCount; i++) {
        const bx = pixel.x + (cell * i) / (barCount + 1);
        g.lineBetween(bx, pixel.y + inset * 2, bx, pixel.y + cell - inset * 2);
      }

      g.lineStyle(1.5, worldAccent, 0.35);
      g.strokeRoundedRect(pixel.x + inset * 0.4, pixel.y + inset * 0.4, cell - inset * 0.8, cell - inset * 0.8, cell * 0.17);
    }

    // PR13-C: exactly one gate-glow tween per gate state - kills and
    // releases any previous one before creating a replacement, fixing the
    // known latent stacking bug (a temporary STARFRUIT gate closing and a
    // later permanent gate opening used to each start their own
    // `!this.gateWasOpen`-guarded tween, with neither ever explicitly
    // killed). See `VisualFeedbackController.syncGateGlow()`/`GateGlowTweenManager`.
    this.feedback.syncGateGlow(snapshot);
  }

  /**
   * PR31 follow-up fix (round 2): a single small counter-rotating swirl
   * motif at the open portal's own center - EXACT same cheap pattern as
   * `createBlackHoleSwirl()` (draw two short arcs ONCE into a Graphics
   * child, then only ever tween the parent Container's own `rotation` -
   * never a per-frame redraw of the arcs themselves). Reused specifically
   * because the real-device feedback asked for "subtle rotational/orbital
   * energy if already cheap to reuse", and this file already has exactly
   * that pattern proven safe. Kept well inside the portal's own rim
   * (`cell*0.37`, see `renderGate()`) so it never reaches the tile edge or
   * competes with the rings/core around it. Uses the portal's OWN accent
   * color (`color`, the same one its inner ring/STARFRUIT-vs-permanent
   * accent already uses) - never WORMHOLE's cyan/violet or BLACK HOLE's
   * amber - so it reads as energy belonging to this identity specifically.
   */
  private createGatePortalSwirl(cx: number, cy: number, cell: number, color: number): Phaser.GameObjects.Container {
    const container = this.add.container(cx, cy);
    const swirl = this.add.graphics();
    const radius = cell * 0.22;
    const arcSpan = Phaser.Math.DegToRad(100);
    swirl.lineStyle(Math.max(1, cell * 0.03), color, 0.55);
    for (const startDeg of [0, 180]) {
      const start = Phaser.Math.DegToRad(startDeg);
      swirl.beginPath();
      swirl.arc(0, 0, radius, start, start + arcSpan, false);
      swirl.strokePath();
    }
    container.add(swirl);

    this.tweens.add({
      targets: container,
      rotation: Math.PI * 2,
      duration: 3200,
      repeat: -1,
      ease: "Linear",
    });

    return container;
  }

  /**
   * PR32: bites only ever change on a tick (eaten/respawned) - split out
   * of the old, combined `renderSnakeAndBites()` so this stays tick-driven
   * exactly as before; only the snake's own POSITION render moved to a
   * per-frame cadence (`renderSnakePosition()`, called from `update()`).
   */
  private renderSnakeBites(): void {
    const snapshot = this.levelGame.getSnapshot();
    this.renderBites(snapshot.bites, snapshot.trickBiteWindowRemainingMoves > 0 && !snapshot.isGateOpen);
  }

  /**
   * PR32 (Snake Movement Smoothing) route/trail replacement: draws the
   * snake at its current render-only sampled position along `snakeRoute`
   * (see `computeCurrentRenderSnake()`) - called once per render frame
   * (`update()`, and once from `create()` for the very first frame before
   * any tick has run), never from `tick()` itself, so a multi-tick
   * catch-up burst still only redraws `snakeGraphics` once for that
   * frame. Skipped entirely while `blackHoleConsuming` - that animation's
   * own `tweens.addCounter` owns `snakeGraphics` exclusively from the
   * instant it starts (see `startBlackHoleConsumption()`'s own doc), and
   * a second writer here would fight it for the same Graphics object.
   */
  private renderSnakePosition(): void {
    if (this.blackHoleConsuming) return;
    const snapshot = this.levelGame.getSnapshot();
    const renderSnake = this.computeCurrentRenderSnake(snapshot.snake);
    this.drawSnakeSegments(renderSnake, this.rushGlowIntensity());

    if (renderSnake.length > 0 && snapshot.phase !== "exiting") {
      this.drawSnakeEyes(renderSnake[0], snapshot.direction);
    }
  }

  /**
   * PR32 follow-up (continuous-body art pass). Draws `snake` (head-first,
   * same shape as `LevelGameSnapshot.snake`, though `renderSnakePosition()`
   * passes fractional render-only coordinates rather than raw grid
   * integers) into `snakeGraphics` as ONE continuous body instead of a
   * row of separately shaped cells - shared by `renderSnakePosition()`'s
   * per-frame interpolated draw and `startBlackHoleConsumption()`'s own
   * animation (which redraws a shrinking slice of the frozen death-moment
   * body) so neither duplicates this logic. No eyes, no bites - callers
   * that want those draw them separately.
   *
   * Approach: `computeSnakeVisualRuns()` (pure, in
   * `snakeRenderInterpolation.ts`) splits `snake` into maximal runs of
   * segments that are still geometrically continuous - every ordinary
   * snake is exactly one run; a portal/WORMHOLE teleport (which relocates
   * only the head, by more than one cell) always splits into two, so the
   * body is NEVER drawn as a stroke across the board (see that function's
   * own doc). Each run is rendered as a filled "capsule chain" -
   * `drawSnakeBodyLayer()` fills a circle at every point in the run plus
   * a straight quad (`computeBodyEdgeQuad()`) between every consecutive
   * pair - which gives clean, automatically rounded joins at every turn
   * (including 90-degree corners) for free, without needing Phaser's own
   * line-join support (this Graphics version has no reliable round-join
   * primitive for multi-segment strokes) and without a GameObject or
   * tween per segment. Three layered passes over the SAME run geometry
   * give the body its depth - see each pass's own comment below - then
   * the head is drawn separately, last, as its own slightly larger circle
   * on top of the body's own end-cap at that exact point, so the two
   * connect with zero visible seam while the head still reads as its own
   * shape. A run's LAST point (the tail, for the run containing it)
   * simply gets the same end-cap circle as every other point - a clean
   * rounded ending with no extra tapering logic.
   *
   * `rushIntensity` (`0..1`, from `rushGlowIntensity()` - unchanged
   * mechanic/timing) blends `bodyFill`/`headFill` toward
   * `GameConfig.colors.biteRush` exactly as before (`RUSH_FILL_MIX`,
   * modest, so the snake's own green identity never fully disappears),
   * and separately blends the outline pass toward a much more saturated
   * gold (see `BODY_SHADOW_COLOR`/`HEAD_SHADOW_COLOR`'s own doc) - ONE
   * continuous outline pass, so there is no possible per-segment color
   * seam. The highlight pass is derived from the LIVE (already
   * RUSH-blended) fill colors, so it automatically warms up too, with no
   * separate branch. `startBlackHoleConsumption()`'s call site always
   * omits `rushIntensity` (defaults to `0`) - a RUSH identity has no
   * business reappearing on an already-frozen, already-dying body.
   */
  private drawSnakeSegments(snake: readonly Point[], rushIntensity = 0): void {
    const sg = this.snakeGraphics;
    sg.clear();
    if (snake.length === 0) return;

    const cell = this.layout.cellSize;
    const inset = Math.max(1, cell * 0.08);
    // The TOTAL visual footprint (outline included) is deliberately kept
    // at exactly `cell - inset * 2` - the same, already-tuned body
    // footprint PR31/PR32 established - so the negative space between two
    // non-consecutive parallel body sections in neighboring cells (and
    // self-collision/narrow-passage readability) is completely unchanged
    // by this art pass. The outline "eats into" that footprint from the
    // outside in, rather than adding beyond it.
    const outlineRadius = (cell - inset * 2) / 2;
    const outlineWidth = Math.max(1, cell * OUTLINE_WIDTH_SCALE);
    const bodyRadius = Math.max(1, outlineRadius - outlineWidth);
    // The head's OUTER radius (including its own outline) is clamped to
    // `cell * 0.48` - comfortably inside the cell's own half-width
    // (`0.5`) - so a "slightly larger" head can never visually imply a
    // collision footprint outside the single cell it actually occupies.
    const headOuterRadius = Math.min(outlineRadius * HEAD_RADIUS_SCALE, cell * 0.48);
    const headRadius = Math.max(1, headOuterRadius - outlineWidth);
    const highlightOffset = cell * HIGHLIGHT_OFFSET_SCALE;

    const bodyFill = rushIntensity > 0 ? mixColors(GameConfig.colors.snakeBody, GameConfig.colors.biteRush, RUSH_FILL_MIX * rushIntensity) : GameConfig.colors.snakeBody;
    const headFill = rushIntensity > 0 ? mixColors(GameConfig.colors.snakeHead, GameConfig.colors.biteRush, RUSH_FILL_MIX * rushIntensity) : GameConfig.colors.snakeHead;
    // A single continuous outline pass, blended toward RUSH's own gold as
    // intensity rises (mixColors(..., 0) is exactly the plain shadow tone,
    // so this needs no separate branch for the "RUSH off" case).
    const bodyOutline = mixColors(BODY_SHADOW_COLOR, GameConfig.colors.biteRush, rushIntensity);
    const headOutline = mixColors(HEAD_SHADOW_COLOR, GameConfig.colors.biteRush, rushIntensity);
    const bodyHighlight = mixColors(bodyFill, 0xffffff, HIGHLIGHT_MIX);
    const headHighlight = mixColors(headFill, 0xffffff, HIGHLIGHT_MIX);

    // PR32 final polish (turn refinement + tail): `runs` is softened
    // per-corner ONCE (see `softenRunCorners()`'s own doc) and reused
    // across all 3 layers below, so the outline/fill/highlight passes
    // stay perfectly aligned with zero seam - only the geometry each
    // layer draws over changed, never a separate computation per layer.
    // The taper (`computeTailTaperScales()`) is likewise computed ONCE
    // per run and reused across all 3 layers: only the LAST run ever
    // contains the true tail (`computeSnakeVisualRuns()`'s own doc), so
    // every other run gets a uniform scale of `1` - a genuine portal/
    // WORMHOLE-split run boundary is never mistaken for the tail.
    const runs = computeSnakeVisualRuns(snake).map((run) => softenRunCorners(run));
    const radiusScales = runs.map((run, i) => (i === runs.length - 1 ? computeTailTaperScales(run) : new Array<number>(run.length).fill(1)));

    // 1. Outline/shadow pass - a uniformly darker (or, during RUSH, gold)
    //    silhouette slightly larger than the main body, giving the whole
    //    continuous shape one clean, consistent rim with zero per-segment
    //    seams.
    this.drawSnakeBodyLayer(runs, radiusScales, outlineRadius, bodyOutline, 1, 0, 0);
    // 2. Main body fill - the actual continuous body color.
    this.drawSnakeBodyLayer(runs, radiusScales, bodyRadius, bodyFill, 1, 0, 0);
    // 3. Highlight pass - a soft, restrained inner sheen offset toward the
    //    upper-left, suggesting one consistent light source without
    //    turning the body into a hard shape of its own.
    this.drawSnakeBodyLayer(runs, radiusScales, bodyRadius * HIGHLIGHT_RADIUS_SCALE, bodyHighlight, HIGHLIGHT_ALPHA, -highlightOffset, -highlightOffset);

    // Head - drawn last, on top of the body's own end-cap at the same
    // point, so the two connect with zero visible seam while the head
    // still reads as its own, slightly larger, slightly distinct shape.
    const headPixel = this.cellToPixel(snake[0]);
    const headCenter = { x: headPixel.x + cell / 2, y: headPixel.y + cell / 2 };
    sg.fillStyle(headOutline, 1);
    sg.fillCircle(headCenter.x, headCenter.y, headOuterRadius);
    sg.fillStyle(headFill, 1);
    sg.fillCircle(headCenter.x, headCenter.y, headRadius);
    sg.fillStyle(headHighlight, HIGHLIGHT_ALPHA);
    sg.fillCircle(headCenter.x - highlightOffset, headCenter.y - highlightOffset, headRadius * HIGHLIGHT_RADIUS_SCALE);
  }

  /**
   * PR32 follow-up (continuous-body art pass) + PR32 final polish (tail
   * taper): fills one layer (an outline, main-fill, or highlight pass -
   * same shared routine for all three, only `baseRadius`/`color`/`alpha`/
   * offset differ per call) across EVERY run - a circle at each point
   * plus a straight quad (`computeBodyEdgeQuad()`) between every
   * consecutive pair within a run, in the SAME `fillStyle`, so the whole
   * pass is exactly two kinds of primitive (`fillCircle`/`fillPoints`),
   * no GameObject and no tween anywhere. `offsetX`/`offsetY` shift every
   * point in this layer only (used by the highlight pass) without
   * touching the snake's own committed/interpolated positions. A run
   * boundary (a genuine portal/WORMHOLE discontinuity) never gets an
   * edge quad between runs - only within one - so this can never streak
   * a layer across the board.
   *
   * `radiusScales` is `runs`' own parallel per-point multiplier array
   * (`1` = full `baseRadius`, see `computeTailTaperScales()`'s own doc
   * for how the tail's own run tapers) - each point's circle uses its
   * own scaled radius, and the quad bridging two consecutive points uses
   * their SUM (matching the untapered `radius + radius === radius * 2`
   * exactly whenever neither end is tapered), giving a smoothly
   * narrowing bridge rather than a sudden step between two differently
   * sized circles.
   */
  private drawSnakeBodyLayer(runs: readonly Point[][], radiusScales: readonly number[][], baseRadius: number, color: number, alpha: number, offsetX: number, offsetY: number): void {
    const cell = this.layout.cellSize;
    const sg = this.snakeGraphics;
    sg.fillStyle(color, alpha);
    for (let r = 0; r < runs.length; r++) {
      const run = runs[r]!;
      const scales = radiusScales[r]!;
      let previousCenter: { x: number; y: number } | null = null;
      let previousRadius = baseRadius;
      for (let i = 0; i < run.length; i++) {
        const point = run[i]!;
        const radius = baseRadius * (scales[i] ?? 1);
        const pixel = this.cellToPixel(point);
        const center = { x: pixel.x + cell / 2 + offsetX, y: pixel.y + cell / 2 + offsetY };
        if (previousCenter) {
          const quad = computeBodyEdgeQuad(previousCenter, center, previousRadius + radius);
          if (quad.length > 0) sg.fillPoints(quad, true);
        }
        sg.fillCircle(center.x, center.y, radius);
        previousCenter = center;
        previousRadius = radius;
      }
    }
  }

  /**
   * BLACK HOLE V1 (Saturn World): the run is already irrecoverably over
   * the instant the engine resolved this tick (`LevelGame`'s phase is
   * already `"game-over"`) - this only delays the NORMAL Game Over
   * presentation (`handleGameOver()`) until a short, deterministic
   * consumption animation finishes. `isGameOver = true` is set
   * immediately (not after the animation) so every input/pause/update
   * gate that already checks it (`handleDirectionInput()`, `update()`'s
   * own top-of-frame gate, `togglePause()`, `openHelp()`) stops
   * instantly - "ordinary player input stops" and "no additional fruit/
   * score action occurs during consumption" both hold by construction:
   * the main per-frame loop (which is what would ever call
   * `processRealTime()`/advance bites/accept input) is gated off exactly
   * like a normal Game Over, while this method's own animation runs
   * purely on Phaser's tween scheduler, independent of that gate. The
   * body is visually consumed head-first, one segment per animation
   * step, from a FROZEN snapshot of the death-moment body - the engine's
   * own `snakeBody` is never touched again (the engine is already
   * terminal), this only changes what `snakeGraphics` draws meanwhile.
   */
  private startBlackHoleConsumption(): void {
    this.isGameOver = true;
    // PR32: claims exclusive ownership of `snakeGraphics` from this exact
    // instant - `renderSnakePosition()` (the normal movement-smoothing
    // draw) checks this flag and no-ops for as long as it's set, so the
    // two can never race to redraw the same Graphics object on the same
    // frame. See `renderSnakePosition()`'s own doc.
    this.blackHoleConsuming = true;
    this.inputQueue.clear();
    const frozenSnake = [...this.levelGame.getSnapshot().snake];
    const blackHoleCell = frozenSnake[0]; // the head is on the black-hole cell the instant it died there
    this.feedback.onBlackHoleConsume(blackHoleCell);
    // No new audio cue for V1 - the existing `onDeath()` sound still
    // plays the instant `handleGameOver()` finally runs below, at the
    // end of the animation. Visual-only otherwise, per the PR description.

    // PR32: draws the full, correct pre-consumption body immediately,
    // synchronously - `renderSnakePosition()` no longer runs inside
    // `tick()` (it's now a per-FRAME call, and this frame's own call is
    // already skipped by `blackHoleConsuming` above), so without this the
    // last thing actually on `snakeGraphics` would be whatever the
    // PREVIOUS frame's mid-transition interpolated position happened to
    // be, and this animation's own tween wouldn't repaint it until its
    // first `onUpdate` fires - a one-frame visible snap/flicker right
    // before consumption starts. This restores the exact same "already
    // showing the true final position" guarantee the old, tick-synchronous
    // `renderSnakeAndBites()` call used to provide for free.
    this.drawSnakeSegments(frozenSnake);

    const total = frozenSnake.length;
    const finalReason: "black_hole" = "black_hole";
    if (total === 0) {
      this.handleGameOver(finalReason);
      return;
    }
    this.tweens.addCounter({
      from: 0,
      to: total,
      // Short and deterministic, independent of body length - exactly
      // "a short deterministic consumption animation", never a real-time
      // simulation of suction/pull.
      duration: 450,
      ease: "Linear",
      onUpdate: (tween) => {
        const consumed = Math.floor(tween.getValue() ?? total);
        this.drawSnakeSegments(frozenSnake.slice(consumed));
      },
      onComplete: () => {
        this.drawSnakeSegments([]);
        this.handleGameOver(finalReason);
      },
    });
  }

  /** Renders the current SAFE/RUSH/CHAOS triplet with a clear +1 / +2 / ?
   * label on each - readable at a glance on a phone screen too. While a
   * TRICK BITE window is active, every live fruit gets a lightweight
   * pulsing glow (drawn behind, never obscuring the fruit's own color or
   * label) inviting the player to cash it in. */
  private renderBites(bites: readonly Bite[], showBonusGlow: boolean): void {
    const cell = this.layout.cellSize;
    const g = this.biteGraphics;
    g.clear();
    const radius = cell * 0.38;
    const fontSize = Math.max(12, Math.round(cell * 0.32));

    for (let i = 0; i < this.biteLabels.length; i++) {
      const bite = bites[i];
      const label = this.biteLabels[i];
      if (!bite) {
        label.setVisible(false);
        continue;
      }
      const pixel = this.cellToPixel(bite.point);
      const cx = pixel.x + cell / 2;
      const cy = pixel.y + cell / 2;

      if (showBonusGlow) this.drawBiteBonusGlow(g, cx, cy, radius);

      if (bite.kind === "safe") {
        // SAFE's idle personality: a very slow, barely-there "breathing"
        // scale - stable/calm, reads as almost static at a glance. Same
        // time-based-only technique as `drawStarfruit()`'s own pulse below
        // (recomputed from `this.time.now` on every call - no tween, so
        // there is nothing to track or clean up across spawns/despawns).
        const safePulse = 1 + 0.02 * Math.sin(this.time.now / 900);
        this.drawApple(g, cx, cy, radius * safePulse);
      } else if (bite.kind === "starfruit") {
        this.drawStarfruit(g, cx, cy, radius);
      } else if (bite.kind === "rush") {
        this.drawRushBite(g, cx, cy, radius);
      } else {
        this.drawChaosBite(g, cx, cy, radius);
      }

      label.setVisible(true);
      label.setPosition(cx, cy);
      label.setFontSize(fontSize);
      label.setColor(bite.kind === "starfruit" ? "#3a1f66" : "#0f1117");
      label.setText(BITE_LABEL[bite.kind]);
    }
  }

  /**
   * TRICK BITE: a lightweight, restrained pulsing ring behind an
   * eligible fruit while the window is active - drawn first (behind the
   * fruit's own shape/label), a soft ring rather than a filled disc so it
   * never obscures the fruit's own color or its label on top. Same
   * time-based pulse technique as `drawStarfruit()`'s idle animation, so
   * no per-fruit tween lifetime needs to be tracked.
   */
  private drawBiteBonusGlow(g: Phaser.GameObjects.Graphics, cx: number, cy: number, radius: number): void {
    const pulse = 1 + 0.12 * Math.sin(this.time.now / 260);
    g.fillStyle(GameConfig.colors.star, 0.16);
    g.fillCircle(cx, cy, radius * 1.45 * pulse);
    g.lineStyle(Math.max(1.5, radius * 0.1), GameConfig.colors.star, 0.55);
    g.strokeCircle(cx, cy, radius * 1.25 * pulse);
  }

  /**
   * RUSH's idle personality: a restrained pulse slightly quicker than
   * SAFE's own - "energy suggesting speed/risk", never distorting the
   * flat circle silhouette RUSH is recognized by. Same time-based-only
   * technique as every other idle pulse in this file (no tween).
   */
  private drawRushBite(g: Phaser.GameObjects.Graphics, cx: number, cy: number, radius: number): void {
    const pulse = 1 + 0.06 * Math.sin(this.time.now / 380);
    g.fillStyle(BITE_COLOR.rush, 1);
    g.fillCircle(cx, cy, radius * pulse);
    g.lineStyle(2, 0x0f1117, 0.55);
    g.strokeCircle(cx, cy, radius * pulse);
  }

  /**
   * CHAOS's idle personality: the core circle stays completely static -
   * never resized/distorted, so CHAOS is still recognized at a glance -
   * while one small fleck orbits it slowly and fades in and out,
   * suggesting something unstable/strange without touching the core
   * shape. Same time-based-only technique as every other idle pulse in
   * this file (no tween, nothing to clean up).
   */
  private drawChaosBite(g: Phaser.GameObjects.Graphics, cx: number, cy: number, radius: number): void {
    g.fillStyle(BITE_COLOR.chaos, 1);
    g.fillCircle(cx, cy, radius);
    g.lineStyle(2, 0x0f1117, 0.55);
    g.strokeCircle(cx, cy, radius);

    const angle = this.time.now / 900;
    const flickerAlpha = 0.25 + 0.35 * (0.5 + 0.5 * Math.sin(this.time.now / 500));
    const fx = cx + Math.cos(angle) * radius * 1.25;
    const fy = cy + Math.sin(angle) * radius * 1.25;
    g.fillStyle(GameConfig.colors.biteChaos, flickerAlpha);
    g.fillCircle(fx, fy, Math.max(1, radius * 0.14));
  }

  /**
   * STARFRUIT: a 5-point star (shared `drawStar` helper, same as the
   * level-complete rating stars) in a white/cyan prismatic body, with a
   * violet edge glow and a small halo - clearly distinct from RUSH's flat
   * gold circle at a glance, on desktop or a phone screen. A restrained
   * idle pulse (radius/alpha modulated by scene time, redrawn every real
   * tick - no per-fruit tween to manage) stands in for a persistent
   * animation without needing to track fruit-instance lifetimes.
   */
  private drawStarfruit(g: Phaser.GameObjects.Graphics, cx: number, cy: number, radius: number): void {
    const pulse = 1 + 0.08 * Math.sin(this.time.now / 240);

    // Outer violet halo.
    g.fillStyle(GameConfig.colors.starfruitGlow, 0.22);
    g.fillCircle(cx, cy, radius * 1.35 * pulse);

    // Prismatic body: a white/cyan star with a violet edge stroke.
    drawStar(g, cx, cy, radius * pulse, GameConfig.colors.starfruitBody);
    g.lineStyle(Math.max(1.5, radius * 0.12), GameConfig.colors.starfruitGlow, 0.9);
    const outerPoints: { x: number; y: number }[] = [];
    for (let i = 0; i < 10; i++) {
      const r = i % 2 === 0 ? radius * pulse : radius * pulse * 0.45;
      const angle = (Math.PI / 5) * i - Math.PI / 2;
      outerPoints.push({ x: cx + Math.cos(angle) * r, y: cy + Math.sin(angle) * r });
    }
    g.strokePoints(outerPoints, true);

    // Small sparkle accents orbiting the star.
    for (let i = 0; i < 3; i++) {
      const angle = this.time.now / 700 + (i * Math.PI * 2) / 3;
      const sx = cx + Math.cos(angle) * radius * 1.1;
      const sy = cy + Math.sin(angle) * radius * 1.1;
      g.fillStyle(GameConfig.colors.starfruitSparkle, 0.85);
      g.fillCircle(sx, sy, Math.max(1, radius * 0.09));
    }
  }

  /**
   * SAFE reads as an actual apple silhouette, not a plain circle (which
   * read as a tomato) - twin rounded shoulders, a notch between them
   * where the stem sits, and a body that tapers to a narrower base with
   * a slight bottom dimple. One closed outline (not unioned primitives),
   * so the fill/stroke never shows a seam. The `+1` label (drawn
   * separately, always white) still carries the number on top.
   */
  private drawApple(g: Phaser.GameObjects.Graphics, cx: number, cy: number, radius: number): void {
    const bodyCy = cy + radius * 0.05;
    // [angle in degrees clockwise from straight up, radius fraction].
    // Two peaks (55/305) either side of a shallow notch (0) give the
    // rounded shoulders; the shape narrows through 130-230 to a shallow
    // dimple at the bottom (180).
    const outline: [number, number][] = [
      [0, 0.68],
      [25, 1.02],
      [55, 1.08],
      [90, 1.03],
      [130, 0.86],
      [160, 0.6],
      [180, 0.42],
      [200, 0.6],
      [230, 0.86],
      [270, 1.03],
      [305, 1.08],
      [335, 1.02],
    ];
    const points = outline.map(([angleDeg, frac]) => {
      const rad = Phaser.Math.DegToRad(angleDeg);
      return { x: cx + Math.sin(rad) * radius * frac, y: bodyCy - Math.cos(rad) * radius * frac };
    });

    g.fillStyle(GameConfig.colors.biteSafe, 1);
    g.fillPoints(points, true);
    g.lineStyle(Math.max(1.5, radius * 0.1), GameConfig.colors.biteSafeContour, 0.85);
    g.strokePoints(points, true);

    // Stem, growing from the top notch.
    const notch = points[0];
    g.lineStyle(Math.max(1.5, radius * 0.15), GameConfig.colors.biteSafeStem, 1);
    g.lineBetween(notch.x, notch.y, notch.x + radius * 0.18, notch.y - radius * 0.36);

    // Leaf, attached partway up the stem.
    g.fillStyle(GameConfig.colors.biteSafeLeaf, 1);
    g.fillEllipse(notch.x + radius * 0.36, notch.y - radius * 0.24, radius * 0.46, radius * 0.26);

    // A soft highlight on the upper-left of the skin.
    g.fillStyle(GameConfig.colors.biteSafeShine, 0.4);
    g.fillEllipse(cx - radius * 0.32, bodyCy - radius * 0.26, radius * 0.3, radius * 0.48);
  }

  /**
   * PR32 follow-up (continuous-body art pass): each eye is now a small
   * white "sclera" circle with a darker pupil on top - reads cleanly at
   * mobile size and stays visible against the head's own new fill/
   * highlight (a flat dark dot alone could get lost against the
   * highlight pass right where the eyes sit) - still fully static, still
   * two circles, not a character-animation feature.
   */
  private drawSnakeEyes(head: Point, direction: Direction): void {
    const cell = this.layout.cellSize;
    const pixel = this.cellToPixel(head);
    const cx = pixel.x + cell / 2;
    const cy = pixel.y + cell / 2;
    const offset = cell * 0.19;
    const scleraRadius = Math.max(1.5, cell * 0.095);
    const pupilRadius = Math.max(1, cell * 0.055);

    let e1 = { x: cx, y: cy };
    let e2 = { x: cx, y: cy };

    if (direction === "up" || direction === "down") {
      const dy = direction === "up" ? -offset : offset;
      e1 = { x: cx - offset, y: cy + dy };
      e2 = { x: cx + offset, y: cy + dy };
    } else {
      const dx = direction === "left" ? -offset : offset;
      e1 = { x: cx + dx, y: cy - offset };
      e2 = { x: cx + dx, y: cy + offset };
    }

    const sg = this.snakeGraphics;
    sg.fillStyle(EYE_SCLERA_COLOR, 0.95);
    sg.fillCircle(e1.x, e1.y, scleraRadius);
    sg.fillCircle(e2.x, e2.y, scleraRadius);
    sg.fillStyle(GameConfig.colors.boardBackground, 1);
    sg.fillCircle(e1.x, e1.y, pupilRadius);
    sg.fillCircle(e2.x, e2.y, pupilRadius);
  }

  // ---------------------------------------------------------------------
  // Scoring V2: QA overlay (?qa=scoring only)
  // ---------------------------------------------------------------------

  /** Live debug readout for `?qa=scoring` - never touches persistence, purely a read of already-computed engine/tracker state. */
  private updateQaScoringOverlay(): void {
    if (!this.qaScoringOverlayText) return;
    const snap = this.levelGame.getSnapshot();
    const moment = this.levelGame.getLastSkillMoment();
    const lastManeuvers = moment ? moment.maneuvers.map((m) => MANEUVER_LABEL[m.type]).join(" + ") : "-";
    const anchor = moment?.maneuvers[0]?.anchor;
    const activeSeconds = (this.runTracker.getActiveDurationMs() / 1000).toFixed(1);
    const pace = paceConfigForLevel(this.level);
    const projectedPaceBonus = computePaceBonus(pace, this.runTracker.getActiveDurationMs());
    const lastBonus = this.levelGame.getLastBiteBonus();
    const lastBonusLabel = lastBonus ? `${lastBonus.kind.toUpperCase()} BITE +${lastBonus.points}` : "-";
    const windowOpenedBy = snap.trickBiteWindowOpenedBy ? snap.trickBiteWindowOpenedBy.map((m) => MANEUVER_LABEL[m]).join(" + ") : "-";

    const lines = [
      "QA MODE: SCORING",
      `dirs: ${this.recentDirections.join(" > ") || "-"}`,
      `move #: ${snap.moveCount}`,
      `active: ${activeSeconds}s`,
      `last maneuver(s): ${lastManeuvers}`,
      `anchor: ${anchor ? `(${anchor.x},${anchor.y})` : "-"}`,
      `awarded: +${moment?.awardedPoints ?? 0}`,
      `skill score: ${snap.skillScore}`,
      `flow: streak ${snap.currentFlowStreak} (x${snap.currentFlowMultiplier.toFixed(2)})`,
      `maneuver speed weight: ${moment ? moment.maneuverSpeedWeight.toFixed(2) : "-"}`,
      `projected pace bonus: +${projectedPaceBonus}`,
      // PR13-B: active window source/remaining, last bonus source - see
      // GameScene's "UI scope for this PR" requirement to expose these on
      // the existing QA scoring overlay, not a new one.
      `trick window: ${snap.trickBiteWindowRemainingMoves > 0 ? `active (${snap.trickBiteWindowRemainingMoves} left)` : "inactive"}`,
      `window source: ${snap.trickBiteWindowSource ?? "-"}`,
      `window opened by: ${windowOpenedBy}`,
      `last trick/clutch: ${lastBonusLabel}`,
      `last bonus source: ${lastBonus?.source ?? "-"}`,
      `trick bites: ${snap.trickBiteCount} · clutch bites: ${snap.clutchBiteCount}`,
    ];

    // Score & Stars V2 fields - Campaign-only (Daily levels have no entry
    // in CAMPAIGN_FINAL_SCORE_THRESHOLDS and never need one - see
    // stars.ts), so this whole block is skipped for a Daily or Endless run
    // rather than risk calling getStarThresholds()/ProgressStorage/
    // ScoreV2Storage for a level id neither was ever meant to cover -
    // Endless never resolves to qaMode "scoring" today (see `create()`),
    // but this guard stays correct defensively either way.
    if (!this.dailyContext && !this.endlessContext) {
      const thresholds = getStarThresholds(this.level);
      const projectedFinalScore = snap.finalScoreSoFar + projectedPaceBonus;
      const projectedStars = projectedFinalScore >= thresholds.star3 ? 3 : projectedFinalScore >= thresholds.star2 ? 2 : 1;
      const bestStars = ProgressStorage.getLevelStats(this.level).stars;
      const bestFinalScore = ScoreV2Storage.hasBestFinalScore(this.level) ? ScoreV2Storage.getBestFinalScore(this.level) : "-";
      lines.push(
        `bite score: ${snap.score}`,
        `live score (bite+skill): ${snap.finalScoreSoFar}`,
        `2-star / 3-star threshold: ${thresholds.star2} / ${thresholds.star3}`,
        `projected final score (if completed now): ${projectedFinalScore}`,
        `projected stars (if completed now): ${projectedStars}${projectedStars > bestStars ? " (new star record)" : ""}`,
        `best stars (stored): ${bestStars}`,
        `best final score (stored): ${bestFinalScore}`,
      );
    }

    this.qaScoringOverlayText.setText(lines.join("\n"));
  }

  // ---------------------------------------------------------------------
  // PR11 (Speed Engine V2): QA overlay (?qa=speed only)
  // ---------------------------------------------------------------------

  /** Live movement-speed debug readout for `?qa=speed` - every field `SpeedDebugSnapshot` exposes, never touching persistence. Never shown in normal gameplay - see `resolveQaMode()`'s strict host allowlist. */
  private updateQaSpeedOverlay(): void {
    if (!this.qaSpeedOverlayText) return;
    const snap = this.levelGame.getSnapshot();
    const d = snap.speedDebug;

    const lines = [
      "QA MODE: SPEED",
      `mode: ${d.mode}`,
      `world/day base: ${d.worldBaseStepMs}ms`,
      `pressure-adjusted base: ${d.pressureAdjustedBaseMs.toFixed(1)}ms`,
      `length: ${d.actualLength} (added ${d.addedLength})`,
      `growth boost: +${d.growthBoostPercent}%`,
      `cumulative yellow: ${d.cumulativeYellowCount}`,
      `RUSH debt: +${d.rushDebtPercent}%`,
      `RUSH combo (score): x${d.rushComboStack}`,
      `persistent mode boost: +${d.persistentModeBoostPercent}%`,
      `temporary % boost (unused by SPEED TRIAL): +${d.temporaryBoostPercent}%`,
      `active cap: ${d.activeCapMs}ms`,
      `uncapped target: ${d.uncappedTargetStepMs.toFixed(1)}ms`,
      `underlying target: ${d.underlyingTargetStepMs.toFixed(1)}ms`,
      `fixed override: ${d.isFixedOverrideActive ? `ACTIVE @ ${d.fixedOverrideStepMs}ms` : `inactive (would be ${d.fixedOverrideStepMs}ms)`}`,
      `effective target: ${d.effectiveTargetStepMs.toFixed(1)}ms`,
      `current (interpolated): ${d.currentStepMs.toFixed(1)}ms`,
      `cells/sec: ${d.currentCellsPerSecond.toFixed(2)}`,
      `transitioning: ${d.isTransitioning ? `yes (${d.transitionRemainingMs.toFixed(0)}ms left)` : "no"}`,
      `SPEED TRIAL: ${d.speedTrialRemainingMs !== null ? `${(d.speedTrialRemainingMs / 1000).toFixed(1)}s left` : "inactive"}`,
    ];
    this.qaSpeedOverlayText.setText(lines.join("\n"));
  }

  /** `?qa=endless` live debug counters - minimal fields needed to verify Endless lifecycle behavior (fruit-cell degradation, ARENA CLEARED, respawn timing) by eye, all already exposed on `getSnapshot()`. */
  private updateEndlessQaDebugCounters(): void {
    if (!this.qaEndlessDebugText) return;
    const snap = this.levelGame.getSnapshot();
    const lines = [
      "QA MODE: ENDLESS",
      `phase: ${snap.phase}`,
      `bites on board: ${snap.bites.length}`,
      `pending respawn: ${snap.pendingRespawn}`,
      `length: ${snap.length}`,
      `stepMs: ${snap.stepMs.toFixed(1)}`,
      `fastest stepMs so far: ${(this.endlessFastestStepMs ?? snap.stepMs).toFixed(1)}`,
      `stored best length: ${this.endlessStoredBestLength ?? "-"}`,
    ];
    this.qaEndlessDebugText.setText(lines.join("\n"));
  }

  // ---------------------------------------------------------------------
  // `?qa=feedback` (PR13-C - Visual Feedback V2): a compact grid of small
  // buttons, one per `QA_GALLERY_TRIGGERS` entry - every click calls
  // straight into `VisualFeedbackController.triggerQa()`, which is
  // visual-only by construction (never touches `LevelGame`, persistence,
  // analytics, or the network - see that method's own doc). Built once in
  // `create()`, destroyed with the rest of the scene on teardown.
  // ---------------------------------------------------------------------

  private buildFeedbackQaGallery(): void {
    // A fixed button width with a column count fitted to the actual
    // viewport - narrow mobile gets fewer columns (wrapping into more
    // rows) instead of a fixed 4 that can clip a label off a narrow
    // screen's right edge.
    const gapX = 4;
    const gapY = 4;
    const btnWidth = 96;
    const btnHeight = 22;
    const available = this.scale.width - SIDE_PADDING * 2;
    const cols = Math.max(1, Math.min(4, Math.floor((available + gapX) / (btnWidth + gapX))));
    const startX = SIDE_PADDING + btnWidth / 2;
    const startY = TOP_RESERVED + 44;

    QA_GALLERY_TRIGGERS.forEach((trigger, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = startX + col * (btnWidth + gapX);
      const y = startY + row * (btnHeight + gapY);
      const button = createButton(this, x, y, trigger.label, () => this.feedback.triggerQa(trigger.id), {
        width: btnWidth,
        height: btnHeight,
        fontSize: "8px",
        cornerRadius: 4,
        fillColor: 0x2a3145,
        hoverColor: 0x3a4160,
        textColor: "#f5f7fa",
        silent: true,
      });
      // QA-follow-up fix (Defect B): this debug button grid must never
      // render above the feedback lanes it exists to demo - on narrow
      // viewports its full-width grid coincides with the lanes' centered
      // x-position, and a debug overlay sitting on top of the very feature
      // under test reads as a text "collision" in manual QA. Kept below
      // every feedback-lane depth (41/42/44 in `VisualFeedbackController`)
      // and every non-QA overlay (pause 50+), so nothing about real
      // gameplay's own depth ordering changes - only this QA-only grid
      // moves.
      button.setDepth(30).setScrollFactor(0);
      this.qaGalleryButtons.push(button);
    });
  }

  // ---------------------------------------------------------------------
  // `?qa=chaos` only (PR13-D audio integration follow-up, section D): a
  // small deterministic-QA button row - FORCE NEXT CHAOS: <outcome> and
  // FORCE NEXT SPAWN: STARFRUIT. Every button calls straight into
  // `LevelGame`'s one-shot QA override setters (a no-op unless this scene
  // was constructed with `qaOverridesEnabled: true`, itself only ever true
  // here because `buildQaLevelGameOptions()` already required
  // `resolveQaMode()` to pass its host-allowlist check) - the very next
  // real CHAOS bite/fruit group then resolves through the exact same
  // `tick()`/`handleTickResult()` path as any other bite, so the real
  // production `AudioManager` (and every other gameplay system) reacts
  // exactly as it would to a genuine roll. Never changes real
  // probabilities/RNG, and each override is one-shot - see
  // `LevelGame.setQaForcedChaosOutcome()`/`setQaForceStarfruitNextSpawn()`.
  // ---------------------------------------------------------------------

  private buildChaosQaControls(): void {
    const gapX = 4;
    const gapY = 4;
    const btnWidth = 132;
    const btnHeight = 24;
    const available = this.scale.width - SIDE_PADDING * 2;
    const cols = Math.max(1, Math.min(3, Math.floor((available + gapX) / (btnWidth + gapX))));
    const startX = SIDE_PADDING + btnWidth / 2;
    const startY = TOP_RESERVED + 44;

    const controls: Array<{ label: string; onClick: () => void }> = [
      { label: "FORCE: JACKPOT", onClick: () => this.applyChaosQaOverride(() => this.levelGame.setQaForcedChaosOutcome("jackpot"), "Next CHAOS -> JACKPOT") },
      { label: "FORCE: WORMHOLE", onClick: () => this.applyChaosQaOverride(() => this.levelGame.setQaForcedChaosOutcome("wormhole"), "Next CHAOS -> WORMHOLE") },
      { label: "FORCE: SHRINK", onClick: () => this.applyChaosQaOverride(() => this.levelGame.setQaForcedChaosOutcome("shrink"), "Next CHAOS -> SHRINK") },
      { label: "FORCE: SPEED TRIAL", onClick: () => this.applyChaosQaOverride(() => this.levelGame.setQaForcedChaosOutcome("speed-trial"), "Next CHAOS -> SPEED TRIAL") },
      // STARFRUIT forcing is deliberately excluded from the Endless QA
      // panel - Endless locks "STARFRUIT never spawns" (see
      // `endlessLevelGameOptions()`), and forcing it here would contradict
      // that even as a QA-only escape hatch.
      ...(this.endlessContext ? [] : [{ label: "FORCE: STARFRUIT", onClick: () => this.applyChaosQaOverride(() => this.levelGame.setQaForceStarfruitNextSpawn(), "Next spawn -> STARFRUIT") }]),
      ...(this.endlessContext ? this.buildEndlessQaControls() : []),
    ];

    controls.forEach((control, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = startX + col * (btnWidth + gapX);
      const y = startY + row * (btnHeight + gapY);
      const button = createButton(this, x, y, control.label, control.onClick, {
        width: btnWidth,
        height: btnHeight,
        fontSize: "10px",
        cornerRadius: 4,
        fillColor: 0x2a3145,
        hoverColor: 0x3a4160,
        textColor: "#f5f7fa",
        silent: true,
      });
      button.setDepth(30).setScrollFactor(0);
      this.qaChaosButtons.push(button);
    });

    const statusY = startY + Math.ceil(controls.length / cols) * (btnHeight + gapY) + 4;
    this.qaChaosStatusText = this.add
      .text(SIDE_PADDING, statusY, "", {
        fontFamily: "Arial, sans-serif",
        fontSize: "11px",
        color: "#7dffb3",
      })
      .setOrigin(0, 0)
      .setDepth(30)
      .setScrollFactor(0);
  }

  private applyChaosQaOverride(apply: () => void, statusText: string): void {
    apply();
    this.qaChaosStatusText?.setText(`QA override armed: ${statusText}`);
  }

  // ---------------------------------------------------------------------
  // `?qa=endless` only - Endless-specific QA controls, appended onto the
  // same button grid `buildChaosQaControls()` already builds (see that
  // method's own STARFRUIT-exclusion comment). Deliberately does NOT add
  // any new production constructor API to `LevelGame` to fabricate a long
  // snake/near-full-board state - near-full-board/full-board behavior is
  // covered by deterministic automated tests instead (see
  // `arenaCapacity.test.ts`). These controls only ever exercise real
  // GameScene code paths that already exist for real gameplay.
  // ---------------------------------------------------------------------

  private buildEndlessQaControls(): Array<{ label: string; onClick: () => void }> {
    return [
      { label: "FORCE: DEATH", onClick: () => this.applyChaosQaOverride(() => this.handleGameOver("wall"), "Forced death (wall)") },
      { label: "PREVIEW: YOU DIED", onClick: () => this.qaPreviewEndlessResult("death") },
      { label: "PREVIEW: RUN ENDED", onClick: () => this.qaPreviewEndlessResult("quit") },
      { label: "PREVIEW: ARENA CLEARED", onClick: () => this.qaPreviewEndlessResult("arena_cleared") },
      { label: "CLEAR BEST", onClick: () => this.qaClearEndlessBest() },
    ];
  }

  /**
   * Jumps straight to `EndlessResultScene` with the CURRENT run's real
   * stats under a chosen `reason`, for visually previewing every result
   * headline/layout without playing an actual run to that ending. Never
   * persists (never calls `EndlessStorage.recordRun()`) and never emits
   * `run_finished` - a preview is not a real run outcome, the same rule
   * Daily's own QA preview follows for its result screen.
   */
  private qaPreviewEndlessResult(reason: EndlessEndReason): void {
    const snapshot = this.levelGame.getSnapshot();
    const bites = this.runTracker.getBiteCounts();
    const outcome: EndlessRunOutcome = {
      reason,
      deathReason: reason === "death" ? "wall" : null,
      length: snapshot.length,
      score: snapshot.finalScoreSoFar,
      survivalTimeMs: this.runTracker.getActiveDurationMs(),
      maxRushCombo: bites.maxRushStreak,
      safeCount: bites.safe,
      rushCount: bites.rush,
      chaosCount: bites.chaos,
      fastestStepMs: this.endlessFastestStepMs ?? snapshot.stepMs,
      isNewBest: false, // a preview never claims a fabricated record improvement.
    };
    const resultData: EndlessResultSceneData = { outcome };
    this.scene.start("EndlessResultScene", resultData);
  }

  /** QA-only: clears the local Endless personal best entirely - never called by real gameplay. */
  private qaClearEndlessBest(): void {
    EndlessStorage.clear();
    this.endlessStoredBestLength = null;
    this.qaChaosStatusText?.setText("QA: Endless best cleared");
  }

  // ---------------------------------------------------------------------
  // HUD: pause / help buttons
  // ---------------------------------------------------------------------

  private createPauseButton(): void {
    const container = this.add.container(0, 0);
    const bg = this.add.circle(0, 0, PAUSE_BUTTON_SIZE / 2, 0x1c2030, 1);
    bg.setStrokeStyle(1.5, GameConfig.colors.gridLine, 1);

    const barWidth = 4;
    const barHeight = 14;
    const gap = 5;
    const leftBar = this.add.rectangle(-gap, 0, barWidth, barHeight, 0xf5f7fa);
    const rightBar = this.add.rectangle(gap, 0, barWidth, barHeight, 0xf5f7fa);

    container.add([bg, leftBar, rightBar]);
    container.setSize(PAUSE_BUTTON_SIZE, PAUSE_BUTTON_SIZE);
    container.setInteractive(
      new Phaser.Geom.Circle(0, 0, PAUSE_BUTTON_SIZE / 2),
      Phaser.Geom.Circle.Contains,
    );
    container.on("pointerdown", () => this.togglePause());

    this.pauseButton = container;
  }

  /** Small, always-reachable "?" button: reopens the CHOOSE YOUR BITE
   * explainer (with the exact CHAOS percentages) at any time. */
  private createHelpButton(): void {
    const container = this.add.container(0, 0);
    const bg = this.add.circle(0, 0, HELP_BUTTON_SIZE / 2, 0x1c2030, 1);
    bg.setStrokeStyle(1.5, GameConfig.colors.gridLine, 1);
    const label = this.add
      .text(0, 0, "?", {
        fontFamily: "Arial, sans-serif",
        fontSize: "20px",
        fontStyle: "bold",
        color: "#f5f7fa",
      })
      .setOrigin(0.5);

    container.add([bg, label]);
    container.setSize(HELP_BUTTON_SIZE, HELP_BUTTON_SIZE);
    container.setInteractive(
      new Phaser.Geom.Circle(0, 0, HELP_BUTTON_SIZE / 2),
      Phaser.Geom.Circle.Contains,
    );
    container.on("pointerdown", () => this.openHelp());

    this.helpButton = container;
  }

  // ---------------------------------------------------------------------
  // Overlays: pause / game over / level complete / help / intro
  // ---------------------------------------------------------------------

  private createOverlayShell(depth: number): Phaser.GameObjects.Container {
    const overlay = this.add.container(0, 0);
    overlay.setDepth(depth);
    overlay.setVisible(false);

    const bg = this.add.rectangle(this.scale.width / 2, this.scale.height / 2, this.scale.width, this.scale.height, 0x0a0c12, 0.82);
    overlay.add(bg);
    overlay.setData("bg", bg);

    const panel = this.add.container(this.scale.width / 2, this.scale.height / 2);
    overlay.add(panel);
    overlay.setData("panel", panel);

    return overlay;
  }

  private createPauseOverlay(): void {
    const overlay = this.createOverlayShell(50);
    const bg = overlay.getData("bg") as Phaser.GameObjects.Rectangle;
    // Fully opaque - Scoring V2's Pace Bonus rewards efficient play, so the
    // board must never be visible (and plannable) while paused. See
    // `RunTracker.addPausedMs()`/`recordPauseStart()`: paused time is
    // tracked separately from `activeDurationMs` and never counts toward it.
    bg.setFillStyle(0x0a0c12, 1);
    const panel = overlay.getData("panel") as Phaser.GameObjects.Container;

    const title = this.add
      .text(0, -20, "PAUSED", {
        fontFamily: "Arial, sans-serif",
        fontSize: "32px",
        fontStyle: "bold",
        color: "#f5f7fa",
      })
      .setOrigin(0.5);
    const hint = this.add
      .text(0, 24, "Tap here or press Space to resume", {
        fontFamily: "Arial, sans-serif",
        fontSize: "14px",
        color: "#8b93a7",
      })
      .setOrigin(0.5);
    panel.add([title, hint]);

    this.reparentButton(panel, 0, 68, "HOW TO PLAY", () => this.openHelp(), {
      width: 200,
      height: 46,
      fillColor: 0x1c2030,
      hoverColor: 0x262c40,
      textColor: "#f5f7fa",
    });

    // PR13-D production integration: the same persistent Sound On/Off
    // control as the Main Menu (spec section 9's "in-game Pause overlay"
    // requirement) - `silent` for the same reason as the Menu one:
    // `AudioManager.setSoundEnabled()` already plays its own confirmation
    // sound only when turning sound ON.
    const soundButton = createButton(this, 0, 0, this.audio.isSoundEnabled() ? "SOUND ON" : "SOUND OFF", () => this.toggleSoundButton(soundButton), {
      width: 200,
      height: 46,
      fillColor: 0x1c2030,
      hoverColor: 0x262c40,
      textColor: "#f5f7fa",
      silent: true,
    });
    soundButton.setPosition(0, 130);
    panel.add(soundButton);

    // END RUN (Endless) / QUIT (Daily & Campaign) - every mode gets exactly
    // one exit affordance from Pause. Reaching this already required
    // opening Pause, so no extra confirmation dialog - same precedent as
    // Endless's own END RUN. Endless's is record-eligible (see
    // `quitEndlessRun()`); Daily/Campaign's `quitCurrentRun()` (PR28) is a
    // plain abandonment - never a ranked/completed result, never a second
    // attempt reservation, never fake progress.
    if (this.gameMode === "endless") {
      this.reparentButton(panel, 0, 192, "END RUN", () => this.quitEndlessRun(), {
        width: 200,
        height: 46,
        fillColor: 0x3a1620,
        hoverColor: 0x4a1c28,
        textColor: "#ff8f94",
      });
    } else {
      this.reparentButton(panel, 0, 192, "QUIT", () => this.quitCurrentRun(), {
        width: 200,
        height: 46,
        fillColor: 0x3a1620,
        hoverColor: 0x4a1c28,
        textColor: "#ff8f94",
      });
    }

    bg.setInteractive({ useHandCursor: true });
    bg.on("pointerdown", () => this.togglePause());

    this.pauseOverlay = overlay;
  }

  private toggleSoundButton(button: Phaser.GameObjects.Container): void {
    const next = !this.audio.isSoundEnabled();
    this.audio.setSoundEnabled(next);
    const labelText = button.list[1] as Phaser.GameObjects.Text;
    labelText.setText(next ? "SOUND ON" : "SOUND OFF");
  }

  private createGameOverOverlay(): void {
    this.gameOverOverlay = this.createOverlayShell(60);
  }

  private createLevelCompleteOverlay(): void {
    this.levelCompleteOverlay = this.createOverlayShell(60);
  }

  /** First-run only: blocks movement until the player presses LET'S GO. */
  private createIntroOverlay(): void {
    const overlay = this.createOverlayShell(90);
    const panel = overlay.getData("panel") as Phaser.GameObjects.Container;

    const title = this.add
      .text(0, -130, "CHOOSE YOUR BITE", {
        fontFamily: "Arial, sans-serif",
        fontSize: "30px",
        fontStyle: "bold",
        color: "#f5f7fa",
      })
      .setOrigin(0.5);

    const lines: { text: string; color: string }[] = [
      { text: "SAFE +1", color: hexToCss(GameConfig.colors.biteSafe) },
      { text: "RUSH +2 + FASTER", color: "#f5c34d" },
      { text: "CHAOS ?", color: "#c9a9ff" },
    ];
    const lineTexts = lines.map((line, i) =>
      this.add
        .text(0, -68 + i * 38, line.text, {
          fontFamily: "Arial, sans-serif",
          fontSize: "21px",
          fontStyle: "bold",
          color: line.color,
        })
        .setOrigin(0.5),
    );

    const detailsHint = this.add
      .text(0, 46, "Open HOW TO PLAY for full details.", {
        fontFamily: "Arial, sans-serif",
        fontSize: "13px",
        color: "#8b93a7",
      })
      .setOrigin(0.5);

    // Deliberately just one subtle line - the first-time tutorial stays
    // focused on the three regular choices, not a preview of the rare
    // fourth one.
    const legendaryHint = this.add
      .text(0, 70, "A legendary fourth bite may rarely appear…", {
        fontFamily: "Arial, sans-serif",
        fontSize: "12px",
        fontStyle: "italic",
        color: hexToCss(GameConfig.colors.starfruitGlow),
      })
      .setOrigin(0.5);

    panel.add([title, ...lineTexts, detailsHint, legendaryHint]);
    this.reparentButton(panel, 0, 118, "LET'S GO", () => this.dismissIntro(), { width: 240, height: 58 });

    this.introOverlay = overlay;
  }

  private showIntroOverlay(): void {
    this.introOverlay.setVisible(true);
    this.introOverlay.setAlpha(0);
    this.tweens.add({ targets: this.introOverlay, alpha: 1, duration: 200 });
  }

  private dismissIntro(): void {
    // Only the very first time this player ever dismisses the intro - a
    // meaningful state transition, not a re-open of HOW TO PLAY (which
    // never touches TutorialStorage/isIntroActive at all).
    const isFirstCompletion = !TutorialStorage.hasSeenIntro();
    TutorialStorage.markIntroSeen();
    this.isIntroActive = false;
    this.introOverlay.setVisible(false);
    // No reliable input-mode signal exists here - see the PR description:
    // keyboard and touch listeners are both always active regardless of
    // device, and no move is possible before the intro is dismissed.
    if (isFirstCompletion) trackTutorialCompleted({ tutorialVersion: TUTORIAL_VERSION });
    // Instant Play & Onboarding V1: CHOOSE YOUR BITE always hands off to
    // the ready gate next - never straight into ticking - so the two
    // "waiting" overlays can never appear simultaneously (see the class
    // doc on `isReadyGateActive`).
    this.beginReadyGate();
  }

  // ---------------------------------------------------------------------
  // PR31 (corrected; follow-up fix): portrait-only mobile gameplay - the
  // ROTATE YOUR DEVICE / TAP TO CONTINUE gate itself now lives in the
  // shared `OrientationGate.ts` (attached in `create()`, `requiresExplicitResume:
  // () => true` unconditionally, exactly reproducing this Scene's
  // original behavior) - only the gameplay-specific freeze/resume side
  // effects stay here.
  // ---------------------------------------------------------------------

  /**
   * Freezes gameplay exactly like Pause does (same underlying mechanisms:
   * `feedback.onPauseChange()`/`audio.onPauseChange()`, plus
   * `orientationGate.blocked` joining `update()`'s own top-of-frame
   * early-return gate - see that check's own call site for why this is
   * deliberately NOT `isPaused` itself). No death, no attempt consumption,
   * no scoring change, no timer advancement is possible from here: every
   * one of those requires either an executed movement tick or
   * `advanceRealTime()`, and both are driven exclusively from `update()`,
   * which this makes an immediate no-op for as long as the gate is blocked.
   */
  private onOrientationBlockStart(): void {
    // Belt-and-suspenders (the real defense is `handleDirectionInput()`'s
    // own `orientationGate.blocked` guard, checked first, ahead of every
    // other branch): a turn queued the instant before rotation started
    // must never sit around and fire on whatever the FIRST post-resume
    // tick turns out to be - starting that tick fresh, from the engine's
    // own current direction, is the only predictable behavior here.
    this.inputQueue.clear();
    this.feedback.onPauseChange(true);
    this.audio.onPauseChange(true);
  }

  /**
   * Restores gameplay from exactly the state it was frozen in - no
   * restart, no attempt loss, no timer jump: every piece of state this
   * touches (`levelGame`, `runTracker`, real-time countdowns) was simply
   * never advanced while blocked, so there is nothing to resynchronize.
   */
  private onOrientationBlockEnd(): void {
    this.feedback.onPauseChange(false);
    this.audio.onPauseChange(false);
  }

  // ---------------------------------------------------------------------
  // Instant Play & Onboarding V1: pre-run ready gate
  // ---------------------------------------------------------------------

  /** Phaser's own device detection (no new dependency) - the best available signal for which copy to show BEFORE any real input has arrived. `?qaInputMode=desktop|mobile` (host-gated) overrides it for QA. */
  private detectInputMode(): InputMode {
    const override = resolveQaInputModeOverride(window.location.hostname, window.location.search);
    if (override) return override === "mobile" ? "touch" : "keyboard";
    return this.sys.game.device.input.touch ? "touch" : "keyboard";
  }

  /**
   * Engages the compact "PRESS A DIRECTION TO START"/"SWIPE TO START"
   * ready gate for this run - never a tutorial, never persisted, never
   * its own completion event (see `completeReadyGate()`). Called from
   * exactly two places: `dismissIntro()` (a brand-new player, right after
   * CHOOSE YOUR BITE, in EVERY mode) and `create()`'s `else if
   * (this.endlessContext)` branch (a returning player, but only for
   * Endless - covering Instant Play, Main Menu ENDLESS, every PLAY AGAIN,
   * and a returning player's Endless run alike). A returning player in
   * Campaign/Daily/Beat My Score never reaches this method at all -
   * ticking begins immediately, unchanged from before this feature.
   */
  private beginReadyGate(): void {
    this.isReadyGateActive = true;
    this.showReadyOverlay();
  }

  /** Non-full-screen: a small backed text block over the board, never a dark scrim - the board/HUD stay visible while gameplay is frozen. Built once in `create()`, repositioned on resize by `layoutOverlays()`/`onResize()`. */
  private createReadyIndicator(): void {
    this.readyPromptText = this.add
      .text(0, 0, "", {
        fontFamily: "Arial, sans-serif",
        fontSize: "20px",
        fontStyle: "bold",
        color: "#f5f7fa",
        align: "center",
        backgroundColor: "#0f1117cc",
        padding: { x: 16, y: 12 },
      })
      .setOrigin(0.5)
      .setDepth(45)
      .setVisible(false);
  }

  private showReadyOverlay(): void {
    const isTouch = this.detectInputMode() === "touch";
    this.readyPromptText.setText(isTouch ? "SWIPE TO START" : "PRESS A DIRECTION TO START");
    this.positionReadyOverlay();
    this.readyPromptText.setVisible(true);
    this.readyPromptText.setAlpha(0);
    this.tweens.add({ targets: this.readyPromptText, alpha: 1, duration: 200 });
  }

  private hideReadyOverlay(): void {
    this.readyPromptText.setVisible(false);
  }

  private positionReadyOverlay(): void {
    if (!this.layout) return;
    this.readyPromptText.setPosition(this.layout.originX + this.layout.boardWidth / 2, this.layout.originY + this.layout.boardHeight / 2);
  }

  /**
   * The ready-gate's own direction handler - intercepted in
   * `handleDirectionInput()` before any other guard, since it must run
   * precisely while every other gameplay guard would otherwise block it.
   * A reverse of the snake's CURRENT direction is invalid (gate stays);
   * anything else - including pressing the current direction itself,
   * which the normal `InputQueue.push()` would silently no-op - counts as
   * valid start intent and both dismisses the gate and becomes the
   * captured first-move direction (queued normally for a perpendicular
   * turn; naturally the engine's own fallback for same-direction, since
   * `tick()`'s `inputQueue.consume()` already falls back to the current
   * direction when nothing is queued - see `InputQueue`'s own doc).
   */
  private handleReadyGateInput(direction: Direction, inputMode: InputMode): DirectionHandlerResult {
    if (!this.isReadyGateActive) return "ignored";
    const current = this.levelGame.getSnapshot().direction;
    if (isOppositeDirection(direction, current)) return "illegal-reverse";
    const result = this.inputQueue.push(direction, current);
    this.completeReadyGate(inputMode);
    return result;
  }

  private completeReadyGate(inputMode: InputMode): void {
    this.isReadyGateActive = false;
    this.hideReadyOverlay();
    trackFirstInputReceived({ gameMode: this.gameMode, inputMode });
    // New player goal clarity: shown exactly here, not decided earlier in
    // create() - see shouldShowEndlessGoalHint's own doc for why the
    // OnboardingStorage write is deferred to this actual display moment.
    if (this.shouldShowEndlessGoalHint) {
      this.shouldShowEndlessGoalHint = false;
      OnboardingStorage.setEndlessGoalHintShown();
      // The emphasis settles into the persistent objective label once it
      // finishes - endlessObjectiveText starts hidden for exactly this
      // case (see its own doc), so the two are never visible together.
      this.feedback.showEndlessGoalHint(() => this.endlessObjectiveText.setVisible(true));
    }
  }

  private populateGameOverOverlay(reason: "wall" | "obstacle" | "self" | "black_hole"): void {
    const panel = this.gameOverOverlay.getData("panel") as Phaser.GameObjects.Container;
    panel.removeAll(true);

    const snapshot = this.levelGame.getSnapshot();

    const title = this.add
      .text(0, -120, "GAME OVER", {
        fontFamily: "Arial, sans-serif",
        fontSize: "34px",
        fontStyle: "bold",
        color: "#ff5f6d",
      })
      .setOrigin(0.5);

    const reasonLine = this.add
      .text(0, -76, GAME_OVER_REASON_TEXT[reason], {
        fontFamily: "Arial, sans-serif",
        fontSize: "16px",
        color: "#8b93a7",
      })
      .setOrigin(0.5);

    const lengthLine = this.add
      .text(0, -48, `Length reached: ${snapshot.length} / ${this.effectiveTargetLength}`, {
        fontFamily: "Arial, sans-serif",
        fontSize: "16px",
        color: "#f5f7fa",
      })
      .setOrigin(0.5);

    const scoreLine = this.add
      .text(0, -22, `Score: ${snapshot.score} (not saved)`, {
        fontFamily: "Arial, sans-serif",
        fontSize: "14px",
        color: "#8b93a7",
      })
      .setOrigin(0.5);

    panel.add([title, reasonLine, lengthLine, scoreLine]);

    // The two available actions side by side whenever there's room - one
    // stacked column only on a genuinely narrow viewport. Started from
    // `scoreLine`'s own measured bottom edge (the last content line)
    // plus a responsive gap, never a fixed Y guessed to clear whatever
    // text happens to be above it today.
    const contentBottom = scoreLine.y + scoreLine.height / 2;
    const { placements } = layoutButtonRows(
      [[{}, {}]],
      this.panelButtonRowsConfig(contentFlowStartY(contentBottom, this.scale.height, { floor: 30, cap: 48, factor: 0.045 })),
    );
    const [retryPlacement, levelSelectPlacement] = placements[0];

    this.reparentButton(
      panel,
      retryPlacement!.x,
      retryPlacement!.y,
      "RETRY",
      () => {
        trackPlayAgainClicked({ gameMode: this.gameMode });
        this.scene.restart({ levelId: this.level.id, runSource: "replay" });
      },
      {
        width: retryPlacement!.width,
        fontSize: retryPlacement!.isTwoColumn ? "16px" : "20px",
      },
    );
    this.reparentButton(
      panel,
      levelSelectPlacement!.x,
      levelSelectPlacement!.y,
      "LEVELS",
      () => this.scene.start("LevelSelectScene", { worldId: this.level.worldId }),
      {
        width: levelSelectPlacement!.width,
        fontSize: levelSelectPlacement!.isTwoColumn ? "15px" : "20px",
        fillColor: 0x1c2030,
        hoverColor: 0x262c40,
        textColor: "#f5f7fa",
      },
    );
  }

  private populateLevelCompleteOverlay(result: LevelCompleteOverlayResult): void {
    const {
      biteScore,
      skillScore,
      paceBonus,
      finalScore,
      activeDurationMs,
      moveCount,
      maneuverCounts,
      doubleDangerCount,
      bestFlowStreak,
      trickBiteCount,
      clutchBiteCount,
      stars,
      isNewHighScore,
      isNewStarRecord,
      bestFinalScore,
      isStarfruitGate,
      newlyUnlockedWorldName,
      isFirstEverCampaignCompletion,
    } = result;

    const panel = this.levelCompleteOverlay.getData("panel") as Phaser.GameObjects.Container;
    panel.removeAll(true);

    const isLastInWorld = LevelRegistry.isLastLevelInWorld(this.level);
    const nextLevel = isLastInWorld ? undefined : LevelRegistry.nextOf(this.level.id);
    const nextWorld = isLastInWorld ? WorldRegistry.nextOf(this.level.worldId) : undefined;
    // A STARFRUIT gate escape is never claimed as the campaign's proper
    // ending, whatever level it happens on - it didn't reach the real
    // target length.
    const isCampaignComplete = !isStarfruitGate && isLastInWorld && !nextWorld;

    let y = -200;
    if (newlyUnlockedWorldName) {
      const unlockBanner = this.add
        .text(0, y, `${newlyUnlockedWorldName} UNLOCKED`, {
          fontFamily: "Arial, sans-serif",
          fontSize: "17px",
          fontStyle: "bold",
          color: hexToCss(GameConfig.colors.star),
        })
        .setOrigin(0.5);
      panel.add(unlockBanner);
      y += 30;
    }

    // Campaign Complete Option 2: permanent title/subtitle for EVERY
    // completion of the last level of the last world (first-ever or any
    // later replay) - "CAMPAIGN COMPLETE" is always true once it's true,
    // never "for now". Only the one-time emphasis below (gated by
    // `isFirstEverCampaignCompletion`) differs between the first run and
    // a replay.
    const titleText = isStarfruitGate ? "LEGENDARY ESCAPE" : isCampaignComplete ? "CAMPAIGN COMPLETE" : "LEVEL COMPLETE";
    const title = this.add
      .text(0, y, titleText, {
        fontFamily: "Arial, sans-serif",
        fontSize: isCampaignComplete ? "26px" : "30px",
        fontStyle: "bold",
        color: isStarfruitGate ? hexToCss(GameConfig.colors.starfruitGlow) : "#7dffb3",
      })
      .setOrigin(0.5);
    y += 38;
    // One-time celebratory emphasis (the same scale-pop already used for
    // NEW STAR RECORD/NEW HIGH SCORE below - no new animation primitive)
    // only on the very first-ever Campaign completion, never on a replay.
    if (isCampaignComplete && isFirstEverCampaignCompletion) this.feedback.emphasize(title);

    const subtitleText = isStarfruitGate
      ? "Escaped through STARFRUIT's gate - not the real target length"
      : isCampaignComplete
        ? "You've cleared all 25 levels."
        : this.level.name;
    const subtitle = this.add
      .text(0, y, subtitleText, {
        fontFamily: "Arial, sans-serif",
        fontSize: "13px",
        color: "#8b93a7",
        align: "center",
        // PR27: was a fixed 320px, which could overflow past the edge on
        // a narrow viewport even though the button row right below it
        // already caps itself responsively (see `panelButtonRowsConfig()`)
        // - matches that exact same cap, so text and buttons share one
        // consistent max width instead of two independent ones.
        wordWrap: { width: Math.min(380, this.scale.width - 40) },
      })
      .setOrigin(0.5);
    y += subtitle.height + 14;

    const starsGraphics = this.add.graphics();
    for (let i = 0; i < 3; i++) {
      const starX = (i - 1) * 42;
      drawStar(starsGraphics, starX, y, 15, i < stars ? GameConfig.colors.star : GameConfig.colors.starEmpty);
    }
    y += 40;

    // NEW STAR RECORD and NEW HIGH SCORE are independent achievements (a
    // star record can improve without a new Final Score record, and vice
    // versa - see ProgressStorage.recordLevelResult()/ScoreV2Storage's own
    // docs) - shown as two separate lines so neither ever suppresses or
    // overlaps the other. This line only exists (and only takes up space)
    // when it actually fires.
    let starRecordLine: Phaser.GameObjects.Text | undefined;
    if (isNewStarRecord) {
      starRecordLine = this.add
        .text(0, y, "NEW STAR RECORD!", {
          fontFamily: "Arial, sans-serif",
          fontSize: "14px",
          fontStyle: "bold",
          color: hexToCss(GameConfig.colors.star),
        })
        .setOrigin(0.5);
      y += 22;
      // PR13-C: a short scale-pop emphasis so a new star record reads as
      // a real achievement, independent of (and never overlapping) NEW
      // HIGH SCORE's own emphasis below - text/value unchanged.
      this.feedback.emphasize(starRecordLine);
    }

    const finalScoreLine = this.add
      .text(0, y, `FINAL SCORE: ${finalScore}${isNewHighScore ? "  – NEW HIGH SCORE!" : ""}`, {
        fontFamily: "Arial, sans-serif",
        fontSize: "19px",
        fontStyle: "bold",
        color: "#f5f7fa",
      })
      .setOrigin(0.5);
    y += 26;
    if (isNewHighScore) this.feedback.emphasize(finalScoreLine);

    const breakdownLine = this.add
      .text(0, y, `BITE ${biteScore} · SKILL ${skillScore} · PACE ${paceBonus}`, {
        fontFamily: "Arial, sans-serif",
        fontSize: "14px",
        color: "#d7dbe6",
      })
      .setOrigin(0.5);
    y += 22;

    const timeSeconds = Math.floor(activeDurationMs / 1000);
    const timeLabel = `${Math.floor(timeSeconds / 60)}:${String(timeSeconds % 60).padStart(2, "0")}`;
    const timeMovesLine = this.add
      .text(0, y, `TIME ${timeLabel} · MOVES ${moveCount}`, {
        fontFamily: "Arial, sans-serif",
        fontSize: "13px",
        color: "#8b93a7",
      })
      .setOrigin(0.5);
    y += 20;

    // Per-maneuver counts, DOUBLE DANGER, best FLOW streak - one compact
    // line, always shown (even at 0) so the breakdown is always complete.
    const maneuverLine = this.add
      .text(
        0,
        y,
        `FOLD ${maneuverCounts.perfect_fold} · TURN ${maneuverCounts.last_second_turn} · GRAZE ${maneuverCounts.tail_graze} · THREAD ${maneuverCounts.thread_the_needle} · DOUBLE DANGER ${doubleDangerCount} · BEST FLOW ${bestFlowStreak}`,
        {
          fontFamily: "Arial, sans-serif",
          fontSize: "11px",
          color: "#8b93a7",
          align: "center",
          // PR27: same fix/reasoning as `subtitle`'s own wordWrap above -
          // was a fixed 320px, now matches the button row's own
          // responsive cap (`panelButtonRowsConfig()`).
          wordWrap: { width: Math.min(380, this.scale.width - 40) },
        },
      )
      .setOrigin(0.5);
    y += maneuverLine.height + 8;

    // TRICK BITE / CLUTCH BITE - the two mutually-exclusive payoff-bonus
    // counters, always shown (even at 0), same convention as the maneuver
    // line above.
    const biteBonusLine = this.add
      .text(0, y, `TRICK BITES ${trickBiteCount} · CLUTCH BITES ${clutchBiteCount}`, {
        fontFamily: "Arial, sans-serif",
        fontSize: "11px",
        color: "#8b93a7",
        align: "center",
      })
      .setOrigin(0.5);
    y += biteBonusLine.height + 10;

    // The player-facing "Best" is always the Final Score V2 record now -
    // the legacy Bite-Score-only number (still preserved internally, see
    // `LevelStats.highScore`'s doc) is never shown here. `bestFinalScore`
    // is `null` only when no run (this one included, in QA mode) has ever
    // recorded a Score V2 Final Score for this level - shown honestly as
    // "—", never mislabeling the unrelated legacy number as a Final Score.
    const bestFinalScoreLine = this.add
      .text(0, y, `BEST FINAL SCORE: ${bestFinalScore ?? "—"}`, {
        fontFamily: "Arial, sans-serif",
        fontSize: "11px",
        color: "#6b7280",
      })
      .setOrigin(0.5);
    // The last content line's own measured bottom edge, not a fixed
    // guess - `bestFinalScoreLine` is always exactly one line, but this
    // keeps the "measure it, don't assume it" rule uniform with every
    // other panel overlay's button-row gap.
    y = contentFlowStartY(bestFinalScoreLine.y + bestFinalScoreLine.height / 2, this.scale.height, { floor: 30, cap: 48, factor: 0.045 });

    panel.add(
      [title, subtitle, starsGraphics, starRecordLine, finalScoreLine, breakdownLine, timeMovesLine, maneuverLine, biteBonusLine, bestFinalScoreLine].filter(
        (child): child is Phaser.GameObjects.Graphics | Phaser.GameObjects.Text => child !== undefined,
      ),
    );

    // Most important actions first, side by side whenever there's room:
    // NEXT LEVEL/EXPLORE <world> (whichever applies - hidden, and the row
    // reflows to just REPLAY centered, when campaign is fully complete)
    // paired with REPLAY, then the secondary LEADERBOARD/LEVELS
    // pair, then WORLDS centered in its own final row.
    const config = this.panelButtonRowsConfig(y);
    const { placements } = layoutButtonRows(
      [
        [nextLevel || nextWorld ? {} : null, {}],
        [{}, {}],
        [{}],
      ],
      config,
    );

    const [primaryPlacement, replayPlacement] = placements[0];
    const [leaderboardPlacement, levelSelectPlacement] = placements[1];
    const [worldSelectPlacement] = placements[2];

    if (nextLevel && primaryPlacement) {
      this.reparentButton(panel, primaryPlacement.x, primaryPlacement.y, "NEXT LEVEL", () => this.scene.start("GameScene", { levelId: nextLevel.id, runSource: "next_level" }), {
        width: primaryPlacement.width,
        fontSize: primaryPlacement.isTwoColumn ? "15px" : "20px",
      });
    } else if (nextWorld && primaryPlacement) {
      this.reparentButton(panel, primaryPlacement.x, primaryPlacement.y, `EXPLORE ${nextWorld.name}`, () => this.scene.start("LevelSelectScene", { worldId: nextWorld.id }), {
        width: primaryPlacement.width,
        fontSize: primaryPlacement.isTwoColumn ? "15px" : "20px",
      });
    }

    this.reparentButton(
      panel,
      replayPlacement!.x,
      replayPlacement!.y,
      "REPLAY",
      () => {
        trackPlayAgainClicked({ gameMode: this.gameMode });
        this.scene.restart({ levelId: this.level.id, runSource: "replay" });
      },
      {
        width: replayPlacement!.width,
        fontSize: replayPlacement!.isTwoColumn ? "15px" : "20px",
        fillColor: 0x1c2030,
        hoverColor: 0x262c40,
        textColor: "#f5f7fa",
      },
    );

    this.reparentButton(
      panel,
      leaderboardPlacement!.x,
      leaderboardPlacement!.y,
      "LEADERBOARD",
      () => this.scene.start("LeaderboardScene", { levelId: this.level.id, returnScene: "LevelSelectScene", returnData: { worldId: this.level.worldId } }),
      {
        width: leaderboardPlacement!.width,
        fontSize: leaderboardPlacement!.isTwoColumn ? "15px" : "20px",
        fillColor: 0x1c2030,
        hoverColor: 0x262c40,
        textColor: "#f5f7fa",
      },
    );
    this.reparentButton(panel, levelSelectPlacement!.x, levelSelectPlacement!.y, "LEVELS", () => this.scene.start("LevelSelectScene", { worldId: this.level.worldId }), {
      width: levelSelectPlacement!.width,
      fontSize: levelSelectPlacement!.isTwoColumn ? "15px" : "20px",
      fillColor: 0x1c2030,
      hoverColor: 0x262c40,
      textColor: "#f5f7fa",
    });

    this.reparentButton(panel, worldSelectPlacement!.x, worldSelectPlacement!.y, "WORLDS", () => this.scene.start("WorldSelectScene"), {
      width: worldSelectPlacement!.width,
      fillColor: 0x1c2030,
      hoverColor: 0x262c40,
      textColor: "#f5f7fa",
    });
  }

  private world() {
    return LevelRegistry.groupOf(this.level)!.world;
  }

  /** Shared `layoutButtonRows` config for both overlay panels (game-over/level-complete) - the panel is a centered dialog, not edge-to-edge, so its available width is capped well below the full viewport width (matching the panel's own text content, which already wraps at 320px). `rowHeight` scales down slightly on a short viewport, floored comfortably above `reparentButton`'s default 54px button height so stacked (1-column) rows never touch. */
  private panelButtonRowsConfig(startY: number): Parameters<typeof layoutButtonRows>[1] {
    return {
      availableWidth: Math.min(380, this.scale.width - 40),
      startY,
      rowHeight: Math.max(60, Math.min(68, this.scale.height * 0.078)),
    };
  }

  /** Creates a button via the shared factory and moves it into `panel`
   * (buttons are created at scene-root depth, then re-parented so their
   * coordinates become relative to the panel they belong to). */
  private reparentButton(
    panel: Phaser.GameObjects.Container,
    x: number,
    y: number,
    text: string,
    onClick: () => void,
    options?: Parameters<typeof createButton>[5],
  ): void {
    const button = createButton(this, 0, 0, text, onClick, { width: 200, height: 54, ...options });
    button.setPosition(x, y);
    panel.add(button);
  }
}
